import { useEffect, useState, createContext, useContext, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useParams, useNavigate, Navigate, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ArrowUpRight, ExternalLink, ChevronRight, ChevronLeft, ChevronDown, ArrowLeft, LogIn, LogOut, User, Settings, BarChart3, Tag, Clock, Users, Plus, Pencil, Trash2, Play, ToggleLeft, ToggleRight, X as XIcon, Heart, Bookmark, SlidersHorizontal, Sparkles, Compass, Mail, Send, Download, Eye, LayoutGrid, RefreshCw, Archive, Newspaper, Search as SearchIcon } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { Toaster, toast } from "sonner";
import CollectionBuilder from "./components/CollectionBuilder";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

// ==================== AFFILIATE / UTM TRACKING ====================
// First-touch acquisition source, captured once per visitor and persisted.
const detectSource = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const utmSource = params.get("utm_source");
    if (utmSource) return utmSource.toLowerCase();
    const ref = (document.referrer || "").toLowerCase();
    if (!ref) return "direct";
    if (/instagram\.com/.test(ref)) return "instagram";
    if (/google\./.test(ref)) return "google";
    if (/facebook\.com|fb\.com|fb\.me/.test(ref)) return "facebook";
    if (/pinterest\./.test(ref)) return "pinterest";
    if (/t\.co|twitter\.com|x\.com/.test(ref)) return "twitter";
    if (/youtube\.|youtu\.be/.test(ref)) return "youtube";
    if (/linkedin\./.test(ref)) return "linkedin";
    if (/whatsapp|wa\.me/.test(ref)) return "whatsapp";
    if (ref.includes(window.location.hostname)) return "direct";
    return "referral";
  } catch { return "direct"; }
};

const getSource = () => {
  try {
    let s = localStorage.getItem("edyte_source");
    if (!s) { s = detectSource(); localStorage.setItem("edyte_source", s); }
    return s;
  } catch { return "direct"; }
};

const slugify = (s) => (s || "").toString().toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

// Requests a right-sized image from Shopify's CDN (instead of the full-res original) so
// mobile swipe cards load fast instead of feeling "stuck" while huge photos download.
const shopifyImg = (url, width) => {
  if (!url || !url.includes("cdn.shopify.com")) return url;
  return url.includes("?") ? `${url}&width=${width}` : `${url}?width=${width}`;
};

// Append UTM params to an outbound product link so brands + Edyte can attribute the click.
const buildAffiliateUrl = (product, themeContext) => {
  const base = product?.product_link;
  if (!base) return base;
  try {
    const url = new URL(base);
    url.searchParams.set("utm_source", getSource());
    url.searchParams.set("utm_medium", "edyte");
    url.searchParams.set("utm_campaign", slugify(themeContext || product?.assigned_theme || "general"));
    url.searchParams.set("utm_content", slugify(product?.brand_name || "label"));
    return url.toString();
  } catch { return base; }
};

// Record an outbound product click for Edyte's own affiliate analytics.
const trackProductClick = (product, themeContext) => {
  try {
    api.post("/analytics/view", {
      event_type: "product_click",
      target_id: product?.id,
      target_name: product?.title,
      source: getSource(),
      theme_name: themeContext || product?.assigned_theme || null,
      brand_name: product?.brand_name || null,
    }).catch(() => {});
  } catch {}
  // Meta Pixel: custom event for outbound clicks leaving Edyte for a label's external site.
  try {
    if (typeof window.fbq === "function") {
      window.fbq("trackCustom", "ClickOutToLabel", {
        label_name: product?.brand_name || undefined,
        product_name: product?.title || undefined,
      });
    }
  } catch {}
};

// ==================== UTILITY HOOKS ====================
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.matchMedia("(max-width: 640px)").matches : false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const handler = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);
  return isMobile;
};

// SEO helper — sets <title> + meta description + canonical for the current route.
// Runs as a side-effect on mount and whenever the title/description changes.
// Lightweight (no react-helmet dependency); Google indexes client-side-rendered titles.
const useSEO = ({ title, description, canonical }) => {
  useEffect(() => {
    if (title) document.title = title;
    const setMeta = (selector, attr, value) => {
      if (value == null) return;
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement(selector.startsWith('link') ? 'link' : 'meta');
        if (selector.startsWith('meta[name=')) el.setAttribute('name', selector.split('"')[1]);
        else if (selector.startsWith('meta[property=')) el.setAttribute('property', selector.split('"')[1]);
        else if (selector.startsWith('link[rel=')) el.setAttribute('rel', selector.split('"')[1]);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };
    if (description) {
      setMeta('meta[name="description"]', 'content', description);
      setMeta('meta[property="og:description"]', 'content', description);
      setMeta('meta[name="twitter:description"]', 'content', description);
    }
    if (title) {
      setMeta('meta[property="og:title"]', 'content', title);
      setMeta('meta[name="twitter:title"]', 'content', title);
    }
    const canonicalUrl = canonical || (typeof window !== 'undefined' ? window.location.href : null);
    if (canonicalUrl) {
      setMeta('link[rel="canonical"]', 'href', canonicalUrl);
      setMeta('meta[property="og:url"]', 'content', canonicalUrl);
    }
  }, [title, description, canonical]);
};

// Guarantees a meta description lands in the ~120-158 char sweet spot (Ahrefs/Google
// flag shorter as "too short"). Appends a suffix if base is short; trims if too long.
const ensureSeoDescription = (base, suffix) => {
  if (!base) return suffix;
  const trimmed = base.length > 158 ? base.slice(0, 155).trim() + "…" : base;
  if (base.length >= 120) return trimmed;
  const combined = `${base} ${suffix}`;
  return combined.length > 158 ? combined.slice(0, 155).trim() + "…" : combined;
};

// ==================== AUTH CONTEXT ====================
const AuthContext = createContext(null);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [savedIds, setSavedIds] = useState(new Set());
  // Ref mirror of savedIds — always holds the latest value, immune to React
  // batching. Used by toggleSave so rapid taps see the freshest state.
  const savedIdsRef = useRef(new Set());
  useEffect(() => { savedIdsRef.current = savedIds; }, [savedIds]);

  const fetchSavedIds = useCallback(async () => {
    try {
      const { data } = await api.get("/user/saved-ids");
      setSavedIds(new Set(data.ids));
    } catch {
      setSavedIds(new Set());
    }
  }, []);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setChecking(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  useEffect(() => {
    if (user && user.id) fetchSavedIds();
    else setSavedIds(new Set());
  }, [user, fetchSavedIds]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    setUser(data);
    return data;
  };

  const loginWithGoogle = async (sessionId) => {
    const { data } = await api.post("/auth/google-session", { session_id: sessionId });
    setUser(data);
    return data;
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(false);
    setSavedIds(new Set());
  };

  const toggleSave = async (productId) => {
    if (!user) return;
    // Read CURRENT state from ref (not from closure-captured savedIds which
    // may be stale during rapid consecutive taps). Determine action upfront,
    // synchronously — no dependency on React's batched setState callback timing.
    const current = savedIdsRef.current;
    const isSaved = current.has(productId);
    const next = new Set(current);
    if (isSaved) next.delete(productId); else next.add(productId);
    savedIdsRef.current = next; // update ref immediately so next concurrent tap sees it
    setSavedIds(next);
    // Immediate toast confirmation — gives the user confident feedback even
    // before the network roundtrip resolves. Optimistic; rolled back on error.
    try {
      if (isSaved) toast("Removed from saved", { duration: 1500 });
      else toast.success("Saved", { duration: 1500 });
    } catch { /* sonner not mounted yet — ignore */ }
    try {
      if (isSaved) {
        await api.delete(`/user/save/${productId}`);
      } else {
        await api.post("/user/save", { product_id: productId });
      }
      // After mutation, re-sync from server so UI never drifts from DB truth.
      // Lightweight call — returns only IDs. Catches any race / lost write.
      try {
        const { data } = await api.get("/user/saved-ids");
        const fresh = new Set(data.ids);
        savedIdsRef.current = fresh;
        setSavedIds(fresh);
      } catch { /* ignore — keep optimistic state */ }
    } catch {
      // Rollback both ref and state on failure
      const revert = new Set(savedIdsRef.current);
      if (isSaved) revert.add(productId); else revert.delete(productId);
      savedIdsRef.current = revert;
      setSavedIds(revert);
      try { toast.error("Couldn't save — please try again"); } catch {}
    }
  };

  return (
    <AuthContext.Provider value={{ user, checking, login, register, loginWithGoogle, logout, savedIds, toggleSave }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

function formatError(detail) {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

// ==================== NAVBAR ====================
const Navbar = () => {
  const { user, logout, savedIds } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showMenu, setShowMenu] = useState(false);

  const focusHomeSearch = () => {
    const el = document.querySelector('[data-testid="ai-search-input"]');
    if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.focus(); }
  };

  const handleSearchIconClick = () => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(focusHomeSearch, 400);
    } else {
      focusHomeSearch();
    }
  };

  const isHome = location.pathname === "/";

  return (
    <nav className={isHome ? "fixed top-0 left-0 right-0 z-50" : "glass-nav fixed top-0 left-0 right-0 z-50"} style={isHome ? { backgroundColor: "#F6C7B8" } : undefined} data-testid="navbar">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10">
        <div className="flex items-center justify-between h-16 sm:h-20 md:h-24">
          <Link to="/" className="flex items-center gap-1.5 sm:gap-2" data-testid="logo-link">
            <img src="/edyte-logo.png" alt="" className="h-[1.05rem] sm:h-[1.3rem] md:h-[1.5rem] w-auto" aria-hidden="true" />
            <span className="font-bodoni text-[1.15rem] sm:text-[1.4rem] md:text-[1.6rem] tracking-[0.05em] text-[#0A0A0A] leading-none" style={{ fontWeight: 600 }}>EDYTE</span>
          </Link>
          <div className="flex items-center gap-2.5 sm:gap-3.5">
            <button onClick={handleSearchIconClick} className="text-[#0A0A0A] hover:text-[#B5416B] transition-colors p-1 -m-1" data-testid="nav-search-btn" aria-label="Search">
              <SearchIcon className="w-[17px] h-[17px] sm:w-[19px] sm:h-[19px]" strokeWidth={1.75} />
            </button>
            {user && user.role === "admin" && (
              <Link to="/admin" className="text-[10px] sm:text-xs tracking-[0.15em] uppercase text-[#71717A] hover:text-[#0A0A0A] transition-colors" data-testid="nav-admin">
                Admin
              </Link>
            )}
            {user ? (
              <div className="flex items-center gap-4 sm:gap-4 md:gap-5">
                <Link to="/saved" className="relative flex items-center justify-center w-8 h-8 sm:w-auto sm:h-auto text-[10px] sm:text-xs tracking-[0.15em] uppercase text-[#71717A] hover:text-[#0A0A0A] transition-colors" data-testid="nav-saved">
                  <Heart className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                  {savedIds.size > 0 && <span className="absolute -top-0.5 -right-0.5 sm:-top-1.5 sm:-right-2.5 bg-[#0A0A0A] text-white text-[8px] w-4 h-4 flex items-center justify-center rounded-full">{savedIds.size}</span>}
                </Link>
                <div className="relative">
                  <button onClick={() => setShowMenu(!showMenu)} className="flex items-center gap-1.5 text-xs text-[#71717A] hover:text-[#0A0A0A] transition-colors p-1 -m-1" data-testid="user-menu-btn">
                    <User className="w-4 h-4" />
                    <span className="hidden sm:inline text-[10px] sm:text-xs">{user.name || user.email.split("@")[0]}</span>
                  </button>
                  {showMenu && (
                    <div className="absolute right-0 top-8 bg-white border border-[#E5E5E5] shadow-lg py-2 min-w-[150px] z-50">
                      <Link to="/saved" onClick={() => setShowMenu(false)} className="w-full text-left px-4 py-2 text-sm text-[#71717A] hover:text-[#0A0A0A] hover:bg-[#FAFAFA] flex items-center gap-2" data-testid="menu-saved-btn">
                        <Bookmark className="w-3.5 h-3.5" /> Saved Items
                      </Link>
                      <button onClick={() => { logout(); setShowMenu(false); }} className="w-full text-left px-4 py-2 text-sm text-[#71717A] hover:text-[#0A0A0A] hover:bg-[#FAFAFA] flex items-center gap-2" data-testid="logout-btn">
                        <LogOut className="w-3.5 h-3.5" /> Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2.5 sm:gap-3.5">
                <span className="hidden sm:inline-block w-px h-3.5 bg-[#D4D4D8]" aria-hidden="true" />
                <button onClick={() => navigate("/register")} className="font-bodoni text-[11px] sm:text-xs tracking-[0.08em] uppercase text-[#0A0A0A] hover:text-[#B5416B] transition-colors" data-testid="signup-nav-btn">
                  Sign Up
                </button>
                <button onClick={() => navigate("/login")} className="font-bodoni bg-[#0A0A0A] text-white px-4 sm:px-6 py-2 sm:py-2.5 text-[11px] sm:text-xs tracking-[0.08em] uppercase hover:bg-[#B5416B] transition-colors" data-testid="login-nav-btn">
                  Log In
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

// ==================== GOOGLE SIGN IN BUTTON ====================
const GoogleSignInButton = ({ label = "Continue with Google" }) => {
  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <button onClick={handleGoogle} className="w-full flex items-center justify-center gap-2 border border-[#E5E5E5] py-3 text-sm hover:bg-[#FAFAFA] transition-colors" data-testid="google-signin-btn">
      <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
      {label}
    </button>
  );
};

// ==================== AUTH CALLBACK ====================
const AuthCallback = () => {
  const { loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);
    // Always scrub the session_id from the current history entry FIRST so the
    // user can never get back to it via the browser back button (which would
    // try to re-consume an expired session and falsely log them out).
    try {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    } catch { /* ignore — non-critical */ }
    if (sessionIdMatch) {
      const sessionId = sessionIdMatch[1];
      loginWithGoogle(sessionId)
        .then((data) => {
          navigate(data.role === 'admin' ? '/admin' : '/', { replace: true });
        })
        .catch(() => {
          navigate('/login', { replace: true });
        });
    } else {
      navigate('/', { replace: true });
    }
  }, [loginWithGoogle, navigate]);

  return <div className="min-h-screen flex items-center justify-center bg-white"><p className="text-[#71717A] text-sm">Signing in...</p></div>;
};

// ==================== AUTH PAGES ====================
const LoginPage = () => {
  const { login, user, checking } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // If user is already logged in (e.g., they pressed back to /login after auth),
  // bounce them home rather than show a stale login form which feels like a logout.
  useEffect(() => {
    if (!checking && user && user.id) {
      navigate(user.role === "admin" ? "/admin" : "/", { replace: true });
    }
  }, [user, checking, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(email, password);
      navigate(data.role === "admin" ? "/admin" : "/", { replace: true });
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20 flex items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-sm">
        <h1 className="font-heading text-2xl tracking-tight text-[#0A0A0A] mb-6 text-center">Sign In</h1>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-3 mb-4" data-testid="login-error">{error}</div>}
        <GoogleSignInButton />
        <div className="flex items-center gap-3 my-4">
          <div className="h-px flex-1 bg-[#E5E5E5]"></div>
          <span className="text-xs text-[#71717A]">or</span>
          <div className="h-px flex-1 bg-[#E5E5E5]"></div>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} className="w-full border border-[#E5E5E5] px-4 py-3 text-sm focus:outline-none focus:border-[#0A0A0A]" data-testid="login-email" required />
          <input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} className="w-full border border-[#E5E5E5] px-4 py-3 text-sm focus:outline-none focus:border-[#0A0A0A]" data-testid="login-password" required />
          <button type="submit" disabled={loading} className="w-full bg-[#0A0A0A] text-white py-3 text-sm tracking-wider uppercase hover:bg-[#FF4FB4] transition-colors disabled:opacity-50" data-testid="login-submit">{loading ? "Signing in..." : "Sign In"}</button>
        </form>
        <p className="text-center text-xs text-[#71717A] mt-6">Don't have an account? <Link to="/register" className="text-[#0A0A0A] underline" data-testid="register-link">Sign Up</Link></p>
      </div>
    </div>
  );
};

const RegisterPage = () => {
  const { register: doRegister, user, checking } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!checking && user && user.id) {
      navigate(user.role === "admin" ? "/admin" : "/", { replace: true });
    }
  }, [user, checking, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await doRegister(email, password, name);
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20 flex items-center justify-center px-4" data-testid="register-page">
      <div className="w-full max-w-sm">
        <h1 className="font-heading text-2xl tracking-tight text-[#0A0A0A] mb-6 text-center">Create Account</h1>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-3 mb-4" data-testid="register-error">{error}</div>}
        <GoogleSignInButton label="Sign up with Google" />
        <div className="flex items-center gap-3 my-4">
          <div className="h-px flex-1 bg-[#E5E5E5]"></div>
          <span className="text-xs text-[#71717A]">or</span>
          <div className="h-px flex-1 bg-[#E5E5E5]"></div>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input type="text" placeholder="Name" value={name} onChange={e => setName(e.target.value)} className="w-full border border-[#E5E5E5] px-4 py-3 text-sm focus:outline-none focus:border-[#0A0A0A]" data-testid="register-name" />
          <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} className="w-full border border-[#E5E5E5] px-4 py-3 text-sm focus:outline-none focus:border-[#0A0A0A]" data-testid="register-email" required />
          <input type="password" placeholder="Password (min 6 chars)" value={password} onChange={e => setPassword(e.target.value)} className="w-full border border-[#E5E5E5] px-4 py-3 text-sm focus:outline-none focus:border-[#0A0A0A]" data-testid="register-password" required />
          <button type="submit" disabled={loading} className="w-full bg-[#0A0A0A] text-white py-3 text-sm tracking-wider uppercase hover:bg-[#FF4FB4] transition-colors disabled:opacity-50" data-testid="register-submit">{loading ? "Creating..." : "Sign Up"}</button>
        </form>
        <p className="text-center text-xs text-[#71717A] mt-6">Already have an account? <Link to="/login" className="text-[#0A0A0A] underline" data-testid="login-link">Sign In</Link></p>
      </div>
    </div>
  );
};

// ==================== HERO SECTION ====================
// Fluid, single responsive layout (no breakpoint-swapped variants): every size below uses
// clamp(min, viewport-scaled, max) so the banner scales continuously from mobile to desktop
// like one real design, never leaving dead space and never cropping the models.
const HeroSection = () => {
  return (
    <section className="relative overflow-hidden pt-16 sm:pt-20 md:pt-24" style={{ backgroundColor: "#F6C7B8" }} data-testid="hero-section">
      {/* Single crawlable H1 + supporting SEO copy — kept once (sr-only). Do not change this H1 text. */}
      <h1 className="sr-only" data-testid="hero-headline">Fashion Curated. Unfiltered.</h1>
      <p className="sr-only" data-testid="hero-subtext">Edyte is a curated fashion discovery platform for independent Indian fashion labels, bringing together distinctive women's clothing, designers and styles beyond the obvious.</p>

      {/* decorative dotted circles — minor accents from the reference */}
      <span className="absolute pointer-events-none rounded-full bg-white/70" style={{ top: "4%", left: "48%", width: "6px", height: "6px" }} aria-hidden="true" />
      <span className="absolute pointer-events-none rounded-full bg-white/55" style={{ top: "28%", left: "7%", width: "6px", height: "6px" }} aria-hidden="true" />
      <span className="absolute pointer-events-none rounded-full bg-white/55" style={{ bottom: "20%", right: "7%", width: "6px", height: "6px" }} aria-hidden="true" />
      <span className="absolute pointer-events-none rounded-full border border-white/35" style={{ top: "14%", right: "24%", width: "clamp(40px,6vw,80px)", height: "clamp(40px,6vw,80px)" }} aria-hidden="true" />

      <div className="relative flex items-end justify-center gap-[2vw]" data-testid="hero-web">
        <img
          src="/hero-model-left.png"
          alt="Edyte model wearing an emerald floral gown"
          className="flex-shrink-0 object-contain object-bottom"
          style={{ height: "clamp(150px, 38vw, 620px)", width: "auto" }}
          fetchPriority="high"
          decoding="async"
        />

        <div className="relative z-10 flex-shrink-0 text-center" style={{ paddingBottom: "clamp(10px, 2vw, 56px)", width: "clamp(150px, 26vw, 460px)" }}>
          <img src="/edyte-wordmark.png" alt="EDYTE" className="mx-auto" style={{ height: "clamp(3.4rem, 13.7vw, 10.5rem)", width: "auto", marginBottom: "clamp(2px,0.6vw,8px)" }} />
          <p className="font-bodoni text-[#0A0A0A] uppercase leading-snug" style={{ fontWeight: 600, fontSize: "clamp(0.85rem, 2.6vw, 1.75rem)", letterSpacing: "0.04em", marginTop: "clamp(2px,0.8vw,10px)" }}>Fashion Curated.<br />Unfiltered.</p>
          <span className="block bg-[#0A0A0A]/50 mx-auto" style={{ width: "clamp(24px,3vw,40px)", height: "1px", margin: "clamp(6px,1.4vw,16px) auto" }} aria-hidden="true" />
          <p className="font-bodoni text-[#3F3426] uppercase leading-relaxed" style={{ fontSize: "clamp(7px,1.1vw,14px)", letterSpacing: "0.15em" }}>Discover Independent<br />Indian Fashion Labels.</p>
          <Link to="/labels" className="font-bodoni inline-flex items-center gap-1.5 uppercase text-[#0A0A0A] border-b border-[#0A0A0A] hover:text-[#B5416B] hover:border-[#B5416B] transition-colors" style={{ fontSize: "clamp(7px,1.1vw,13px)", letterSpacing: "0.12em", marginTop: "clamp(6px,1.6vw,20px)", paddingBottom: "3px" }} data-testid="hero-explore-btn">
            Explore Labels <ArrowRight style={{ width: "clamp(9px,1.3vw,15px)", height: "clamp(9px,1.3vw,15px)" }} />
          </Link>
        </div>

        <img
          src="/hero-model-right.png"
          alt="Edyte model wearing a navy floral gown"
          className="flex-shrink-0 object-contain object-bottom"
          style={{ height: "clamp(150px, 38vw, 620px)", width: "auto" }}
          decoding="async"
        />
      </div>
    </section>
  );
};


// ==================== AI FASHION DISCOVERY SEARCH ====================
const SEARCH_SUGGESTIONS = ["Resort wear under 8k"];

const AiSearchSection = () => {
  const [enabled, setEnabled] = useState(false);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => { api.get("/search/settings").then(r => setEnabled(!!r.data?.enabled)).catch(() => {}); }, []);

  const runSearch = (text) => {
    const q = (text ?? query).trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  if (!enabled) return null;

  return (
    <section className="relative overflow-hidden bg-white pt-10 sm:pt-14 md:pt-16 pb-4 sm:pb-6 md:pb-8 px-4 sm:px-8" data-testid="ai-search-section">
      <div className="relative max-w-[640px] mx-auto text-center">
        <img src="/edyte-logo.png" alt="" className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-4 sm:mb-5 opacity-80" aria-hidden="true" />
        <h2 className="font-bodoni text-2xl sm:text-3xl md:text-[2.1rem] text-[#0A0A0A] leading-tight" style={{ fontWeight: 600 }}>More than a marketplace.</h2>
        <p className="font-bodoni italic text-lg sm:text-xl md:text-[1.6rem] text-[#0A0A0A] mt-1 mb-4 sm:mb-5">A new way to discover Indian fashion.</p>
        <p className="font-bodoni text-xs sm:text-sm text-[#5B4A44] leading-relaxed max-w-[480px] mx-auto">EDYTE is a curated discovery platform for independent Indian fashion labels. We bring together unique designers, thoughtful collections and untold stories — all in one place, so you can explore, be inspired and shop directly from the labels.</p>

        <span className="block w-10 h-px bg-[#0A0A0A]/40 mx-auto my-4 sm:my-5" aria-hidden="true" />

        <p className="font-bodoni italic text-xs sm:text-sm text-[#5B4A44] mb-4 sm:mb-5">Tell us what you have in mind. We'll find the best from independent labels.</p>

        <form onSubmit={(e) => { e.preventDefault(); runSearch(); }} className="relative flex items-center w-full max-w-[460px] mx-auto bg-white border-[1.5px] border-[#D9A066] rounded-full px-4 sm:px-5 py-2 sm:py-2.5 shadow-sm focus-within:border-[#0A0A0A] transition-colors">
          <SearchIcon className="w-4 h-4 text-[#0A0A0A] flex-shrink-0" />
          <span className="w-px h-5 bg-[#E5D3C8] mx-2.5 flex-shrink-0" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Dress for a Goa vacation…"
            className="font-bodoni flex-1 min-w-0 bg-transparent outline-none text-xs sm:text-sm text-[#0A0A0A] placeholder:text-[#A1A1AA] placeholder:italic"
            data-testid="ai-search-input"
          />
          <button type="submit" className="ml-2 w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center hover:bg-[#B5416B] transition-colors flex-shrink-0" data-testid="ai-search-submit-btn" aria-label="Search">
            <ArrowRight className="w-4 h-4" />
          </button>
        </form>

        <div className="flex items-center justify-center gap-2 sm:gap-3 mt-5 sm:mt-6 flex-wrap">
          <span className="font-bodoni text-[10px] tracking-[0.2em] uppercase text-[#8A6F64] flex-shrink-0">Try this</span>
          {SEARCH_SUGGESTIONS.map((s, i) => (
            <button key={s} onClick={() => runSearch(s)} className="font-bodoni px-3.5 py-1.5 rounded-full border border-[#D9BEB0] text-xs sm:text-sm text-[#0A0A0A] hover:border-[#0A0A0A] hover:bg-white transition-colors" data-testid={`ai-search-chip-${i}`}>
              {s}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

const SearchResultsPage = () => {
  const [searchParams] = useSearchParams();
  const q = searchParams.get("q") || "";
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState(null);
  const [maxResults, setMaxResults] = useState(50);
  const [swipeDone, setSwipeDone] = useState(false);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  useSEO({ title: q ? `"${q}" — Search | Edyte` : "Search | Edyte", description: `Search results for "${q}" on Edyte — independent Indian fashion labels.` });

  useEffect(() => {
    if (!q) { navigate("/"); return; }
    setLoading(true);
    setSwipeDone(false);
    api.get("/search/settings").then(r => setMaxResults(r.data?.view_all_count || 50)).catch(() => {});
    api.post("/search", { query: q }).then(r => {
      setResults(r.data);
      api.post("/analytics/view", { event_type: "ai_search", target_name: q }).catch(() => {});
    }).catch(() => setResults({ products: [], count: 0 })).finally(() => setLoading(false));
  }, [q]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-white" data-testid="search-page-loading"><p className="text-sm text-[#71717A]">Searching…</p></div>;
  }

  const products = (results?.products || []).slice(0, maxResults);

  if (isMobile && !swipeDone && products.length > 0) {
    return <SwipeStack kind="search" title={q} subtitle="AI Fashion Discovery results" coverImage={products[0]?.image_url} products={products} onExit={() => setSwipeDone(true)} themeContext="AI Search" />;
  }

  return (
    <div className="min-h-screen bg-white pt-24 sm:pt-28 pb-16 px-4 sm:px-8 md:px-16" data-testid="search-results-page">
      <button onClick={() => navigate("/")} className="flex items-center gap-1.5 text-sm text-[#71717A] hover:text-[#0A0A0A] mb-6" data-testid="search-back-btn">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <h1 className="font-heading text-2xl sm:text-3xl tracking-tight text-[#0A0A0A] mb-1">"{q}"</h1>
      <p className="text-sm text-[#71717A] mb-8" data-testid="search-results-count">{products.length} piece{products.length === 1 ? "" : "s"} found</p>
      {products.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-[#D4D4D8]" data-testid="search-empty-state">
          <p className="text-sm text-[#71717A]">No matches found. Try rephrasing your search.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 sm:gap-6" data-testid="search-results-grid">
          {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} themeContext="AI Search" />)}
        </div>
      )}
    </div>
  );
};

// ==================== SMALL PRODUCT CARD ====================
const SmallProductCard = ({ product, index }) => {
  const { user, savedIds, toggleSave } = useAuth();
  const isMobile = useIsMobile();
  const isSaved = savedIds.has(product.id);

  const handleSave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return;
    toggleSave(product.id);
  };

  return (
    <div className="relative flex-shrink-0 w-[220px] sm:w-[270px] md:w-[320px] fade-in group" style={{ animationDelay: `${Math.min(index * 0.04, 0.4)}s`, opacity: 0 }} data-testid={`top-pick-card-${product.id}`}>
      <a href={buildAffiliateUrl(product)} onClick={() => trackProductClick(product)} target={isMobile ? "_self" : "_blank"} rel={isMobile ? undefined : "noopener noreferrer"} className="product-card block">
        <div className="aspect-[2/3] overflow-hidden relative bg-[#F5F5F5]">
          <img src={product.image_url} alt={product.title} className="w-full h-full object-cover" loading="lazy" />
        </div>
        <div className="p-2 sm:p-3">
          <span className="text-[10px] sm:text-xs tracking-[0.12em] uppercase text-[#71717A] block mb-1">{product.brand_name}</span>
          <h3 className="font-medium text-xs sm:text-sm leading-tight line-clamp-2 text-[#0A0A0A] mb-1">{product.title}</h3>
          <span className="text-xs sm:text-sm text-[#0A0A0A] font-semibold">{'\u20B9'}{product.price?.toLocaleString('en-IN')}</span>
        </div>
      </a>
      {user && (
        <button type="button" onClick={handleSave} className="absolute top-2 right-2 w-9 h-9 flex items-center justify-center bg-white/95 hover:bg-white shadow-sm rounded-full transition-colors z-10" aria-label={isSaved ? "Unsave" : "Save"} data-testid={`save-btn-${product.id}`}>
          <Heart className={`w-4 h-4 transition-colors ${isSaved ? 'fill-[#FF4FB4] text-[#FF4FB4]' : 'text-[#0A0A0A]'}`} />
        </button>
      )}
    </div>
  );
};

// ==================== PRODUCT CARD ====================
const ProductCard = ({ product, index, showCaption = true, themeContext }) => {
  const { user, savedIds, toggleSave } = useAuth();
  const isMobile = useIsMobile();
  const isSaved = savedIds.has(product.id);

  const handleSave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return;
    toggleSave(product.id);
  };

  return (
    <div className="relative fade-in group" style={{ animationDelay: `${Math.min(index * 0.04, 0.4)}s`, opacity: 0 }} data-testid={`product-card-${product.id}`}>
      <a href={buildAffiliateUrl(product, themeContext)} onClick={() => trackProductClick(product, themeContext)} target={isMobile ? "_self" : "_blank"} rel={isMobile ? undefined : "noopener noreferrer"} className="product-card block">
        <div className="aspect-[2/3] overflow-hidden relative bg-[#F5F5F5]">
          <img src={product.image_url} alt={product.title} className="w-full h-full object-cover" loading="lazy" />
        </div>
        <div className="p-2 sm:p-3 md:p-4">
          <div className="flex items-start justify-between gap-1 sm:gap-2 mb-1 sm:mb-2">
            <span className="text-[9px] sm:text-[10px] md:text-xs tracking-[0.12em] uppercase text-[#71717A]">{product.brand_name}</span>
            <span className="text-[11px] sm:text-xs md:text-sm text-[#0A0A0A] font-semibold whitespace-nowrap">{'\u20B9'}{product.price?.toLocaleString('en-IN')}</span>
          </div>
          <h3 className="font-medium text-xs sm:text-sm mb-1 sm:mb-2 line-clamp-2 text-[#0A0A0A]">{product.title}</h3>
          {product.craft ? (
            <span className="inline-block text-[8px] sm:text-[9px] md:text-[10px] tracking-[0.12em] uppercase text-[#71717A] border border-[#E5E5E5] px-1.5 py-0.5 capitalize" data-testid={`product-craft-${product.id}`}>{product.craft}</span>
          ) : null}
        </div>
      </a>
      {user && (
        <button type="button" onClick={handleSave} className="absolute top-2 sm:top-3 right-2 sm:right-3 w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-white/95 hover:bg-white shadow-sm rounded-full transition-colors z-10" aria-label={isSaved ? "Unsave" : "Save"} data-testid={`save-btn-${product.id}`}>
          <Heart className={`w-4 h-4 sm:w-[18px] sm:h-[18px] transition-colors ${isSaved ? 'fill-[#FF4FB4] text-[#FF4FB4]' : 'text-[#0A0A0A]'}`} />
        </button>
      )}
    </div>
  );
};

// ==================== THE SELECTION ====================
const EdytePickCard = ({ product, index, num, widthClass = "w-[210px] sm:w-[230px] md:w-[250px]" }) => {
  const { user, savedIds, toggleSave } = useAuth();
  const isMobile = useIsMobile();
  const isSaved = savedIds.has(product.id);
  const handleSave = (e) => { e.preventDefault(); e.stopPropagation(); if (!user) return; toggleSave(product.id); };
  return (
    <div className={`relative snap-start flex-shrink-0 ${widthClass} group`} data-testid={`edyte-pick-card-${product.id}`}>
      <a href={buildAffiliateUrl(product)} onClick={() => trackProductClick(product)} target={isMobile ? "_self" : "_blank"} rel={isMobile ? undefined : "noopener noreferrer"} className="block">
        <div className="aspect-[3/4] overflow-hidden relative bg-[#F5F5F5] rounded-2xl">
          <img src={product.image_url} alt={product.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" loading="lazy" />
        </div>
        <div className="pt-3">
          <h3 className="font-medium text-sm leading-tight text-[#0A0A0A] line-clamp-2">{product.title}</h3>
          <p className="text-[10px] tracking-[0.18em] uppercase text-[#71717A] mt-1.5">{product.brand_name}</p>
          <p className="text-sm text-[#0A0A0A] font-semibold mt-1">{'\u20B9'}{product.price?.toLocaleString('en-IN')}</p>
        </div>
      </a>
      {user && (
        <button type="button" onClick={handleSave} className="absolute top-2 right-2 w-9 h-9 flex items-center justify-center bg-white/95 hover:bg-white shadow-sm rounded-full transition-colors z-10" aria-label={isSaved ? "Unsave" : "Save"} data-testid={`save-btn-${product.id}`}>
          <Heart className={`w-4 h-4 transition-colors ${isSaved ? 'fill-[#FF4FB4] text-[#FF4FB4]' : 'text-[#0A0A0A]'}`} />
        </button>
      )}
    </div>
  );
};

const EdytePickMobileCarousel = ({ products, loading }) => {
  const ref = useRef(null);
  const prog = useRef(false);
  const userTs = useRef(0);
  const [active, setActive] = useState(0);
  const goTo = (i) => {
    const el = ref.current; if (!el) return;
    const child = el.children[i]; if (!child) return;
    prog.current = true;
    el.scrollTo({ left: child.offsetLeft - (el.clientWidth - child.clientWidth) / 2, behavior: 'smooth' });
    setTimeout(() => { prog.current = false; }, 700);
    setActive(i);
  };
  useEffect(() => {
    if (!products || products.length <= 1) return;
    const t = setInterval(() => {
      if (Date.now() - userTs.current < 7000) return; // pause auto after manual scroll
      setActive(a => { const n = (a + 1) % products.length; goTo(n); return n; });
    }, 5000);
    return () => clearInterval(t);
  }, [products]);
  const onScroll = () => {
    const el = ref.current; if (!el) return;
    if (!prog.current) userTs.current = Date.now();
    const center = el.scrollLeft + el.clientWidth / 2;
    let best = 0, bd = Infinity;
    Array.from(el.children).forEach((c, i) => { const cc = c.offsetLeft + c.clientWidth / 2; const d = Math.abs(cc - center); if (d < bd) { bd = d; best = i; } });
    setActive(best);
  };
  if (loading) return <div className="px-[8%]"><div className="skeleton aspect-[2/3] rounded-2xl"></div></div>;
  if (!products?.length) return null;
  return (
    <div className="relative" data-testid="edyte-pick-mobile-carousel">
      <div ref={ref} onScroll={onScroll} className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide -mx-4 px-[8%] py-1">
        {products.map((p, i) => (
          <div key={p.id} className="snap-center shrink-0 basis-[84%] px-1">
            <div className={`transition-all duration-500 origin-center ${i === active ? 'scale-100 opacity-100' : 'scale-[0.82] opacity-45'}`}>
              <EdytePickCard product={p} index={i} num={String(i + 1).padStart(2, '0')} widthClass="w-full" />
            </div>
          </div>
        ))}
      </div>
      <button onClick={() => goTo(Math.max(0, active - 1))} className="absolute left-1 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 shadow flex items-center justify-center hover:bg-white" data-testid="carousel-prev" aria-label="Previous"><ChevronLeft className="w-4 h-4" /></button>
      <button onClick={() => goTo(Math.min(products.length - 1, active + 1))} className="absolute right-1 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 shadow flex items-center justify-center hover:bg-white" data-testid="carousel-next" aria-label="Next"><ChevronRight className="w-4 h-4" /></button>
      <div className="flex justify-center gap-1.5 mt-4" data-testid="carousel-dots">
        {products.map((_, i) => <span key={i} onClick={() => goTo(i)} className={`h-1.5 rounded-full transition-all cursor-pointer ${i === active ? 'w-5 bg-[#0A0A0A]' : 'w-1.5 bg-[#D4D4D8]'}`} />)}
      </div>
    </div>
  );
};

const EdytePickSection = () => {
  const [displayed, setDisplayed] = useState([]); // 6 products — stable for 30 min
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const fetchPool = async () => {
    try {
      const r = await api.get("/products/featured?limit=30");
      const fetched = r.data || [];
      if (fetched.length === 0) return;
      // Server returns the same order for the entire 30-min bucket.
      setDisplayed(fetched.slice(0, 6));
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchPool();
    // Schedule a refresh exactly when the next 30-min bucket begins, then every 30 min after.
    const ms = 30 * 60 * 1000;
    const now = Date.now();
    const msUntilNextBucket = ms - (now % ms) + 500; // small buffer
    let interval;
    const timeout = setTimeout(() => {
      fetchPool();
      interval = setInterval(fetchPool, ms);
    }, msUntilNextBucket);
    return () => { clearTimeout(timeout); if (interval) clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="pt-8 sm:pt-10 md:pt-12 pb-8 md:pb-12 bg-white" data-testid="featured-section">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-5 md:gap-8">
          {/* LEFT TITLE PANEL */}
          <div className="md:sticky md:top-28 md:self-start" data-testid="edyte-pick-heading">
            <h2 className="text-2xl sm:text-3xl md:text-4xl text-[#0A0A0A] leading-[1] tracking-tight" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}>Edyte Top Pick<span className="text-[#A1A1AA] ml-2 align-top text-sm md:text-base">✦</span></h2>
            <p className="text-sm sm:text-base md:text-lg text-[#3F3F46] leading-snug mt-3 sm:mt-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Our curation.<br/>Handpicked for you.</p>
            <button onClick={() => navigate('/products')} className="inline-flex items-center gap-2 mt-5 sm:mt-6 text-[10px] sm:text-xs tracking-[0.22em] uppercase text-[#0A0A0A] border-b border-[#0A0A0A] pb-1.5 hover:gap-3 transition-all" data-testid="view-all-picks-btn">View All Picks <ArrowRight className="w-3.5 h-3.5" /></button>
          </div>
          {/* RIGHT — mobile auto-carousel / desktop horizontal scroll */}
          {isMobile ? (
            <EdytePickMobileCarousel products={displayed.slice(0, 6)} loading={loading} />
          ) : (
            <div className="overflow-x-auto scrollbar-hide -mr-4 sm:-mr-6 md:-mr-10 pr-4 sm:pr-6 md:pr-10">
              <div className="flex gap-4 sm:gap-5 snap-x snap-mandatory">
                {loading
                  ? [...Array(4)].map((_, i) => <div key={i} className="flex-shrink-0 w-[300px] md:w-[330px]"><div className="skeleton aspect-[2/3]"></div></div>)
                  : displayed.slice(0, 6).map((p, i) => (
                      <EdytePickCard key={p.id} product={p} index={i} num={String(i + 1).padStart(2, '0')} />
                    ))
                }
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// ==================== THIS WEEK'S PERSPECTIVES (Themes) ====================
const PERSPECTIVE_TONES = ["#D8E0D7", "#EDE5D6"];
const PerspectiveCard = ({ p, index }) => {
  const navigate = useNavigate();
  const tone = PERSPECTIVE_TONES[index % PERSPECTIVE_TONES.length];
  const img = p.banner_image || p.cover_image;
  return (
    <button onClick={() => navigate(`/theme/${p.theme_slug || p.theme_id}?p=${p.id}`)} className="group relative block w-full text-left overflow-hidden rounded-2xl sm:rounded-3xl shadow-sm hover:shadow-2xl transition-shadow duration-500" style={{ backgroundColor: tone }} data-testid={`perspective-card-${p.theme_id}`}>
      <div className="relative h-[200px] sm:h-[300px] md:h-[340px]">
        {img && <img src={img} alt={p.display_title} className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-[1200ms] group-hover:scale-105" loading="lazy" />}
        {/* Speckled dissolve: colour panel dissolves into the photo */}
        <div className="persp-solid" style={{ "--tone": tone }} />
        <div className="persp-speckle" style={{ "--tone": tone }} />
        {/* Text */}
        <div className="relative z-10 h-full flex flex-col justify-center px-5 sm:px-10 md:px-14 py-5 text-[#1F1B16] max-w-[62%] sm:max-w-[52%]">
          <h3 className="text-xl sm:text-4xl md:text-5xl tracking-tight uppercase leading-[0.95]" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }} data-testid={`perspective-title-${p.theme_id}`}>{p.display_title}</h3>
          <div className="w-10 h-px bg-[#1F1B16]/30 my-2.5 sm:my-4" />
          {p.description && <p className="text-[11px] sm:text-sm text-[#4A4238] leading-snug max-w-[320px]" style={{ fontFamily: "'Outfit', sans-serif" }}>{p.description}</p>}
          <span className="inline-flex items-center gap-2 mt-3 sm:mt-5 text-[9px] sm:text-xs tracking-[0.2em] uppercase border-b border-[#1F1B16]/50 pb-1 w-fit group-hover:gap-3 transition-all">Explore This Perspective <ArrowRight className="w-3.5 h-3.5" /></span>
        </div>
      </div>
    </button>
  );
};

const ThisWeeksPerspectivesSection = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/perspectives").then(r => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false)); }, []);
  if (!loading && items.length === 0) return null;
  return (
    <section className="pt-2 pb-10 sm:py-14 md:py-16 bg-white" data-testid="perspectives-section">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10">
        <div className="mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-[#0A0A0A] leading-[1] tracking-tight" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }} data-testid="perspectives-heading">This Week's Perspectives<span className="text-[#A1A1AA] ml-2 align-top text-sm md:text-base">✦</span></h2>
          <p className="text-sm sm:text-base text-[#52525B] mt-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Two fresh perspectives. Same exceptional labels.</p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:gap-5">
          {loading
            ? [...Array(2)].map((_, i) => <div key={i} className="skeleton h-[220px] sm:h-[300px] md:h-[340px] rounded-2xl sm:rounded-3xl"></div>)
            : items.map((p, i) => <PerspectiveCard key={p.id} p={p} index={i} />)
          }
        </div>
      </div>
    </section>
  );
};

// ==================== DISCOVER LABELS ====================
const DiscoverLabelCard = ({ label }) => {
  const { user, savedIds } = useAuth();
  return (
    <Link
      to={`/brand/${label.slug || label.id}`}
      className="group relative overflow-hidden text-left w-full block"
      data-testid={`discover-label-card-${label.id}`}
    >
      <div className="aspect-[10/17] sm:aspect-[7/10] overflow-hidden relative" style={{ borderTopLeftRadius: '50% 24%', borderTopRightRadius: '50% 24%' }}>
        {label.hero_image
          ? <img src={label.hero_image} alt={label.brand_name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
          : <div className="w-full h-full bg-[#1A1A1A]" />
        }
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/85 via-[#0A0A0A]/15 to-transparent" />
        {user && (
          <span className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center text-white">
            <Bookmark className={`w-4 h-4 ${savedIds.has(label.id) ? 'fill-white' : ''}`} />
          </span>
        )}
        <div className="absolute inset-0 p-4 sm:p-6 md:p-7 flex flex-col justify-end text-white">
          <h3 className="text-base sm:text-2xl md:text-3xl tracking-tight uppercase leading-tight" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }} data-testid={`label-name-${label.id}`}>{label.brand_name}</h3>
          <p className="text-[11px] sm:text-sm text-white/90 mt-1.5 max-w-[280px] leading-snug hidden sm:block" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid={`label-tagline-${label.id}`}>{label.tagline}</p>
          {label.top_themes && label.top_themes.length > 0 && (
            <div className="mt-2 sm:mt-3">
              <p className="text-[9px] sm:text-[11px] text-white/80 mb-1 sm:mb-1.5 tracking-wide uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>Best known for</p>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {label.top_themes.slice(0, 2).map((t, i) => (
                  <span key={t} className="text-[8px] sm:text-[10px] tracking-[0.12em] sm:tracking-[0.18em] uppercase text-white/95 border border-white/35 px-1.5 sm:px-2.5 py-0.5 sm:py-1">
                    {t}{i < Math.min(label.top_themes.length, 2) - 1 && ''}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

const DiscoverLabelsSection = () => {
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/labels/discover?limit=6").then(r => setLabels(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <section className="pt-6 sm:pt-8 md:pt-10 pb-12 sm:pb-14 md:pb-16 bg-white" data-testid="discover-labels-section">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-6 md:gap-10">
          {/* LEFT TITLE PANEL */}
          <div className="md:sticky md:top-28 md:self-start" data-testid="discover-labels-heading">
            <h2 className="text-2xl sm:text-3xl md:text-4xl text-[#0A0A0A] leading-[1] tracking-tight" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}>Discover Labels<span className="text-[#A1A1AA] ml-2 align-top text-sm md:text-base">✦</span></h2>
            <p className="text-base sm:text-lg md:text-xl text-[#3F3F46] leading-snug mt-4 sm:mt-5" style={{ fontFamily: "'Outfit', sans-serif" }}>Independent labels.<br/>Distinct stories.<br/>Timeless style.</p>
            <Link to="/labels" className="inline-flex items-center gap-2 mt-6 sm:mt-7 px-5 py-2.5 border border-[#0A0A0A] text-[10px] sm:text-xs tracking-[0.22em] uppercase text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors" data-testid="view-all-labels-btn">View All Labels <ArrowRight className="w-3.5 h-3.5" /></Link>
          </div>
          {/* RIGHT - vertical grid (mobile 2-col top-to-down, desktop 3-col) */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5">
            {loading
              ? [...Array(6)].map((_, i) => <div key={i}><div className="skeleton aspect-[10/17] sm:aspect-[7/10]" style={{ borderTopLeftRadius: '50% 24%', borderTopRightRadius: '50% 24%' }}></div></div>)
              : labels.map(l => <DiscoverLabelCard key={l.id} label={l} />)
            }
          </div>
        </div>
      </div>
    </section>
  );
};

// ==================== HOME FEATURE ROW (4-col benefits) ====================
const HomeFeatureRow = () => (
  <section className="py-12 sm:py-16 border-t border-[#E5E5E5] bg-white" data-testid="home-feature-row">
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-8 sm:gap-y-10 gap-x-6 lg:gap-x-10 sm:divide-x sm:divide-[#E5E5E5]">
      {[
        { Icon: Sparkles, title: 'Curated Discovery', body: "Handpicked labels you won't find everywhere." },
        { Icon: Compass, title: 'Intelligent Matching', body: 'Discover labels that match your style and taste.' },
        { Icon: Bookmark, title: 'Save & Revisit', body: 'Bookmark your favourite labels to explore later.' },
        { Icon: ArrowUpRight, title: 'Shop Directly', body: 'Seamlessly explore and shop on brand websites.' },
      ].map((f, i) => (
        <div key={i} className="flex items-start gap-4 sm:px-6 lg:px-4 first:pl-0" data-testid={`home-feature-${i}`}>
          <f.Icon className="w-5 h-5 mt-0.5 text-[#0A0A0A] shrink-0" strokeWidth={1.5} />
          <div>
            <h4 className="text-[11px] sm:text-xs tracking-[0.28em] uppercase text-[#0A0A0A] mb-2 font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>{f.title}</h4>
            <p className="text-sm text-[#52525B] leading-relaxed max-w-[260px]" style={{ fontFamily: "'Outfit', sans-serif" }}>{f.body}</p>
          </div>
        </div>
      ))}
    </div>
  </section>
);

// ==================== ABOUT + FAQ SECTION ====================
const FAQS = [
  { q: 'How is Edyte different from marketplaces?', a: 'Instead of showing endless product listings, Edyte focuses on curated fashion edits and emerging labels.' },
  { q: 'Does Edyte sell products directly?', a: "No. Purchases happen on the original brand's website." },
  { q: 'How does Edyte curate products?', a: 'Products are intelligently tagged and grouped into distinct fashion edits based on style and visual identity.' },
  { q: 'Why focus on independent Indian labels?', a: "India's emerging labels are creating globally relevant fashion, but many remain difficult to discover." },
  { q: 'Is Edyte personalized?', a: 'Yes. Discovery gradually improves based on user preferences and interactions.' },
];

const FAQItem = ({ item, idx }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#E5E5E5] py-5" data-testid={`faq-item-${idx}`}>
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-start justify-between gap-4 text-left" data-testid={`faq-toggle-${idx}`}>
        <span className="text-base sm:text-lg text-[#0A0A0A] font-medium leading-snug" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.q}</span>
        <span className={`shrink-0 mt-1 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}>
          <ChevronDown className="w-5 h-5 text-[#0A0A0A]" />
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? 'max-h-60 mt-3' : 'max-h-0'}`}>
        <p className="text-sm sm:text-base text-[#52525B] leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid={`faq-answer-${idx}`}>{item.a}</p>
      </div>
    </div>
  );
};

const AboutFAQSection = () => (
  <section className="py-12 sm:py-16 md:py-20 bg-white border-t border-[#E5E5E5]" data-testid="about-faq-section">
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
      {/* WHAT IS EDYTE */}
      <div data-testid="what-is-edyte">
        <h2 className="text-2xl sm:text-3xl md:text-4xl text-[#0A0A0A] leading-[1] tracking-tight" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}>What is Edyte?<span className="text-[#A1A1AA] ml-2 align-top text-sm md:text-base">✦</span></h2>
        <div className="mt-6 sm:mt-8 space-y-5 text-base sm:text-lg text-[#3F3F46] leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }}>
          <p className="font-medium text-[#0A0A0A]">Edyte is an intelligent fashion discovery platform focused on independent Indian labels.</p>
          <p>We bring together curated collections from emerging brands and organize them into distinct edits such as <span className="text-[#0A0A0A] font-medium">Beach Club</span>, <span className="text-[#0A0A0A] font-medium">Print Story</span>, <span className="text-[#0A0A0A] font-medium">Desi by Design</span>, and <span className="text-[#0A0A0A] font-medium">Social Edit</span> — making fashion discovery more refined, visual, and effortless.</p>
          <p>Built for modern consumers who want to discover beyond mass marketplaces, Edyte combines thoughtful curation with intelligent tagging to surface labels, styles, and pieces that feel distinctive and relevant.</p>
          <p>Unlike traditional marketplaces built around endless inventory, Edyte is designed around discovery, aesthetics, and emerging fashion culture.</p>
        </div>
      </div>
      {/* FAQ */}
      <div data-testid="faq-list">
        <h2 className="text-2xl sm:text-3xl md:text-4xl text-[#0A0A0A] leading-[1] tracking-tight" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}>FAQs<span className="text-[#A1A1AA] ml-2 align-top text-sm md:text-base">✦</span></h2>
        <div className="mt-4 sm:mt-6">
          {FAQS.map((f, i) => <FAQItem key={i} item={f} idx={i} />)}
        </div>
      </div>
    </div>
  </section>
);

// ==================== FOOTER ====================
const Footer = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null); // 'loading' | 'success' | 'error'
  const [msg, setMsg] = useState("");
  const [exploreThemes, setExploreThemes] = useState([]);
  const [exploreEditorials, setExploreEditorials] = useState([]);

  // Sitewide "Explore" links — ensures every theme/editorial page (even ones
  // not surfaced on the home page tiles) has a crawlable incoming internal link.
  useEffect(() => {
    api.get("/themes").then(r => setExploreThemes(r.data || [])).catch(() => {});
    api.get("/editorials").then(r => setExploreEditorials(r.data || [])).catch(() => {});
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    try {
      const r = await api.post("/subscribe", { email: email.trim(), source: "footer" });
      setStatus("success");
      setMsg(r.data?.already_subscribed ? "You're already on the list." : "You're in. Look out for The Edyte Weekly.");
      setEmail("");
    } catch (err) {
      setStatus("error");
      setMsg(formatError(err?.response?.data?.detail) || "Please enter a valid email.");
    }
  };

  return (
    <footer className="border-t border-[#E5E5E5] py-10 sm:py-14 px-4 sm:px-6 md:px-10 bg-white" data-testid="footer">
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10 items-start">
        {/* Newsletter signup */}
        <div className="lg:col-span-1" data-testid="footer-newsletter">
          <p className="text-[10px] sm:text-xs tracking-[0.28em] uppercase text-[#71717A] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>The Edyte Weekly <span className="text-[#A1A1AA] ml-1">✦</span></p>
          <h3 className="text-xl sm:text-2xl md:text-3xl text-[#0A0A0A] tracking-tight leading-tight mb-3" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}>
            Edits, labels & finds — every Sunday.
          </h3>
          <p className="text-sm text-[#52525B] mb-4 max-w-[440px] leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }}>
            A short, curated dispatch from the Edyte editors. No spam, no sales gimmicks.
          </p>
          <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-2 max-w-[440px]" data-testid="footer-subscribe-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yourname.com"
              className="flex-1 px-4 py-3 text-sm bg-white border border-[#E5E5E5] focus:outline-none focus:border-[#0A0A0A] transition-colors"
              data-testid="footer-subscribe-input"
            />
            <button
              type="submit"
              disabled={status === "loading"}
              className="px-5 py-3 text-[10px] sm:text-xs tracking-[0.18em] uppercase bg-[#0A0A0A] text-white hover:bg-[#FF4FB4] transition-colors disabled:opacity-50"
              data-testid="footer-subscribe-btn"
            >
              {status === "loading" ? "Joining..." : "Subscribe"}
            </button>
          </form>
          {status === "success" && <p className="text-xs text-[#15803D] mt-2.5" data-testid="footer-subscribe-success">{msg}</p>}
          {status === "error" && <p className="text-xs text-[#DC2626] mt-2.5" data-testid="footer-subscribe-error">{msg}</p>}
        </div>
        {/* Explore — sitewide links to every theme + editorial */}
        <div data-testid="footer-explore">
          <p className="text-[10px] sm:text-xs tracking-[0.28em] uppercase text-[#71717A] mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Explore</p>
          <ul className="space-y-2">
            <li><Link to="/themes" className="text-sm text-[#3F3F46] hover:text-[#FF4FB4] transition-colors" data-testid="footer-link-themes">All Themes</Link></li>
            <li><Link to="/labels" className="text-sm text-[#3F3F46] hover:text-[#FF4FB4] transition-colors" data-testid="footer-link-labels">All Labels</Link></li>
            {exploreThemes.map(t => (
              <li key={t.id}><Link to={`/theme/${t.slug || t.id}`} className="text-sm text-[#3F3F46] hover:text-[#FF4FB4] transition-colors" data-testid={`footer-link-theme-${t.id}`}>{t.name}</Link></li>
            ))}
          </ul>
        </div>
        {/* Editorials */}
        {exploreEditorials.length > 0 && (
          <div data-testid="footer-editorials">
            <p className="text-[10px] sm:text-xs tracking-[0.28em] uppercase text-[#71717A] mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Editorials</p>
            <ul className="space-y-2">
              {exploreEditorials.map(ed => (
                <li key={ed.id}><Link to={`/editorial/${ed.slug}`} className="text-sm text-[#3F3F46] hover:text-[#FF4FB4] transition-colors" data-testid={`footer-link-editorial-${ed.id}`}>{ed.name}</Link></li>
              ))}
            </ul>
          </div>
        )}
        {/* Brand + contact + copyright */}
        <div className="lg:text-right lg:self-end" data-testid="footer-contact">
          <Link to="/" className="inline-block font-heading text-xl sm:text-2xl text-[#0A0A0A] mb-3">EDYTE</Link>
          <p className="text-[10px] sm:text-xs tracking-[0.28em] uppercase text-[#71717A] mb-1.5" style={{ fontFamily: "'Outfit', sans-serif" }}>Get in touch</p>
          <a href="mailto:hello@theedyte.com" className="inline-flex items-center gap-2 text-sm sm:text-base text-[#0A0A0A] hover:text-[#FF4FB4] transition-colors mb-3 lg:justify-end" data-testid="footer-contact-email">
            <Mail className="w-4 h-4" /> hello@theedyte.com
          </a>
          <p className="text-[10px] sm:text-xs text-[#71717A] tracking-wider">&copy; 2026 EDYTE &mdash; INDIE FASHION CURATOR</p>
        </div>
      </div>
    </footer>
  );
};

// ==================== SIGNUP POPUP (one-time on first visit) ====================
const SIGNUP_POPUP_KEY = "edyte_signup_popup_v1";
const SignupPopup = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (user) { setOpen(false); return; } // never show to logged-in users
    // On mobile, /theme/:id and /brand/:id render the full-screen swipe stack — never overlay the popup there (it would block the swipe controls).
    const onSwipeRoute = isMobile && (location.pathname.startsWith("/theme/") || location.pathname.startsWith("/brand/") || location.pathname.startsWith("/editorial/") || location.pathname.startsWith("/search"));
    if (location.pathname.startsWith("/login") || location.pathname.startsWith("/register") || location.pathname.startsWith("/admin") || onSwipeRoute) { setOpen(false); return; }
    try {
      if (localStorage.getItem(SIGNUP_POPUP_KEY)) return;
    } catch (_) { return; }
    const t = setTimeout(() => setOpen(true), 8000);
    return () => clearTimeout(t);
  }, [user, location.pathname, isMobile]);

  const dismiss = () => {
    try { localStorage.setItem(SIGNUP_POPUP_KEY, "1"); } catch (_) {}
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4" data-testid="signup-popup">
      <div className="absolute inset-0 bg-[#0A0A0A]/60 backdrop-blur-sm" onClick={dismiss} />
      <div className="relative bg-white border border-[#E5E5E5] shadow-2xl max-w-md w-full p-7 sm:p-9" style={{ animation: "fadeIn 0.4s ease" }}>
        <button onClick={dismiss} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-[#71717A] hover:text-[#0A0A0A] transition-colors" data-testid="signup-popup-close">
          <XIcon className="w-4 h-4" />
        </button>
        <p className="text-[10px] tracking-[0.28em] uppercase text-[#71717A] mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Join Edyte <span className="text-[#A1A1AA] ml-1">✦</span></p>
        <h3 className="text-2xl sm:text-3xl text-[#0A0A0A] tracking-tight leading-[1.1] mb-3" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}>
          Discovery, made personal.
        </h3>
        <p className="text-sm text-[#52525B] leading-relaxed mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Create your free account to save pieces you love, follow emerging labels, and get curated edits tuned to your taste.
        </p>
        <div className="flex flex-col gap-2.5">
          <button
            onClick={() => { dismiss(); navigate("/register"); }}
            className="w-full px-5 py-3 text-xs tracking-[0.18em] uppercase bg-[#0A0A0A] text-white hover:bg-[#FF4FB4] transition-colors"
            data-testid="signup-popup-create-btn"
          >
            Create your Edyte account
          </button>
          <button
            onClick={dismiss}
            className="w-full px-5 py-2.5 text-[11px] tracking-[0.18em] uppercase text-[#71717A] hover:text-[#0A0A0A] transition-colors"
            data-testid="signup-popup-dismiss-btn"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
};


// ==================== FEATURED EDITORIAL ====================
const FeaturedEditorialCard = ({ ed }) => {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(`/editorial/${ed.slug}`)}
      className="group relative overflow-hidden text-left w-full rounded-2xl sm:rounded-3xl shadow-sm hover:shadow-2xl transition-shadow duration-500"
      data-testid={`featured-editorial-card-${ed.slug}`}
    >
      <div className="aspect-[3/4] md:aspect-[4/5] overflow-hidden relative rounded-2xl sm:rounded-3xl bg-[#1A1A1A]">
        {ed.cover_image && <img src={ed.cover_image} alt={ed.name} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105" loading="lazy" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/80 via-[#0A0A0A]/25 to-transparent" />
        <div className="absolute inset-0 p-5 sm:p-8 md:p-10 flex flex-col justify-end text-white">
          <h3 className="text-2xl sm:text-3xl md:text-4xl tracking-tight leading-tight" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}>{ed.name}</h3>
          {ed.subtitle && <p className="text-xs sm:text-sm text-white/85 mt-2 max-w-[380px] leading-snug" style={{ fontFamily: "'Outfit', sans-serif" }}>{ed.subtitle}</p>}
          <span className="mt-3 sm:mt-4 inline-flex items-center gap-2 text-[11px] sm:text-xs tracking-[0.15em] uppercase">
            Explore <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </span>
        </div>
      </div>
    </button>
  );
};

const FeaturedEditorialSection = () => {
  const [eds, setEds] = useState([]);
  useEffect(() => { api.get("/editorials").then(r => setEds(r.data || [])).catch(() => {}); }, []);
  if (!eds.length) return null;
  return (
    <section className="pt-6 pb-2 sm:pt-10 sm:pb-4 bg-white" data-testid="featured-editorial-section">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10">
        <div className="flex items-baseline justify-between mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-[#0A0A0A] leading-[1] tracking-tight" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }} data-testid="featured-edit-heading">Featured Edit<span className="text-[#A1A1AA] ml-2 align-top text-sm md:text-base">✦</span></h2>
        </div>
        <div className={`grid gap-4 sm:gap-5 ${eds.length > 1 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"}`}>
          {eds.map(ed => <FeaturedEditorialCard key={ed.id} ed={ed} />)}
        </div>
      </div>
    </section>
  );
};

// ==================== FEATURED STORY (mini-editorial, product row) ====================
const StoryProductCard = ({ product, index, clone }) => {
  const isMobile = useIsMobile();
  const tid = clone ? undefined : `top-pick-card-${product.id}`;
  return (
    <div className="relative flex-shrink-0 w-[195px] sm:w-[220px] md:w-[250px] group" data-testid={tid}>
      <a href={buildAffiliateUrl(product)} onClick={() => trackProductClick(product)} target={isMobile ? "_self" : "_blank"} rel={isMobile ? undefined : "noopener noreferrer"} className="block">
        <div className="aspect-[3/4] overflow-hidden relative bg-[#F5F5F5] rounded-lg">
          <img src={product.image_url} alt={product.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" loading="lazy" />
        </div>
        <div className="pt-2">
          <span className="text-[9px] tracking-[0.14em] uppercase text-[#8A7B67] block truncate">{product.brand_name}</span>
          <h3 className="font-medium text-[11px] leading-tight line-clamp-1 text-[#1A1712] mt-0.5">{product.title}</h3>
          <span className="text-[11px] text-[#1A1712] font-semibold">{'\u20B9'}{product.price?.toLocaleString('en-IN')}</span>
        </div>
      </a>
    </div>
  );
};

const FeaturedStorySection = () => {
  const [ed, setEd] = useState(null);
  const [products, setProducts] = useState([]);
  const scrollRef = useRef(null);
  const pausedUntil = useRef(0);
  const didRun = useRef(false);

  useEffect(() => {
    if (didRun.current) return; // guard against React.StrictMode's dev-only double-invoke
    didRun.current = true;
    (async () => {
      try {
        const r = await api.get("/editorials");
        const list = r.data || [];
        if (!list.length) return;
        // The featured STORY itself never changes — always the first live editorial.
        const chosen = list[0];
        const rr = await api.get(`/editorials/${chosen.slug}`);
        setEd(rr.data);
        // Only the PRODUCTS shown from that story shuffle on every page load/refresh.
        const shuffled = [...(rr.data.products || [])].sort(() => Math.random() - 0.5);
        setProducts(shuffled.slice(0, 8));
      } catch (e) { /* ignore */ }
    })();
  }, []);
  useEffect(() => {
    if (!products.length) return;
    let raf;
    const tick = () => {
      const el = scrollRef.current;
      if (el && Date.now() > pausedUntil.current) {
        el.scrollLeft += 0.6;
        if (el.scrollLeft >= el.scrollWidth / 2) el.scrollLeft = 0;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [products]);
  const nudge = (dir) => {
    const el = scrollRef.current; if (!el) return;
    pausedUntil.current = Date.now() + 4000;
    el.scrollBy({ left: dir * 320, behavior: "smooth" });
  };
  if (!ed || products.length === 0) return null;
  return (
    <section className="pt-0 sm:pt-1 md:pt-2 bg-white" data-testid="featured-story-section">
      <div className="w-full">
        <div className="bg-[#F3ECE1] overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] md:items-center">
            {/* LEFT PANEL */}
            <div className="px-5 sm:px-6 pt-5 pb-1 md:pl-12 md:pr-6 md:py-5">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#8A7B67] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Featured Story</p>
              <h2 className="text-2xl sm:text-3xl md:text-[2.4rem] text-[#1A1712] leading-[1] tracking-tight" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }} data-testid="featured-story-title">{ed.name}</h2>
              {(ed.description || ed.subtitle) && <p className="text-sm text-[#5B5346] mt-2.5 leading-snug max-w-[240px] line-clamp-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{ed.description || ed.subtitle}</p>}
              <Link to={`/editorial/${ed.slug}`} className="inline-flex items-center gap-2 mt-4 text-[10px] tracking-[0.22em] uppercase text-[#1A1712] border-b border-[#1A1712] pb-1 w-fit hover:gap-3 transition-all" data-testid="explore-story-btn">Explore Story <ArrowRight className="w-3.5 h-3.5" /></Link>
            </div>
            {/* RIGHT — auto-scrolling product row with manual left/right navigation */}
            <div className="relative group/story min-w-0 pr-5 sm:pr-6 md:pr-10 pl-5 sm:pl-6 md:pl-0 py-5"
              onMouseEnter={() => { pausedUntil.current = Infinity; }}
              onMouseLeave={() => { pausedUntil.current = 0; }}>
              <div ref={scrollRef} className="flex gap-3 sm:gap-4 overflow-x-auto scrollbar-hide">
                {[...products, ...products].map((p, i) => <StoryProductCard key={i} product={p} index={i} clone={i >= products.length} />)}
              </div>
              <button onClick={() => nudge(-1)} aria-label="Previous" data-testid="featured-story-prev" className="flex absolute left-1 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 shadow items-center justify-center hover:bg-white transition-colors z-10"><ChevronLeft className="w-4 h-4 text-[#1A1712]" /></button>
              <button onClick={() => nudge(1)} aria-label="Next" data-testid="featured-story-next" className="flex absolute right-1 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 shadow items-center justify-center hover:bg-white transition-colors z-10"><ChevronRight className="w-4 h-4 text-[#1A1712]" /></button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ==================== THE WORLD OF EDYTE (5-theme editorial collage) ====================
const WoeTile = ({ theme, className = "", style, labelPos = "bottom" }) => {
  if (!theme) return null;
  return (
    <Link to={`/theme/${theme.slug || theme.id}`} style={style} className={`group relative overflow-hidden block ${className}`} data-testid={`world-theme-${theme.id}`}>
      <img src={theme.image_url} alt={theme.name} className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-[900ms] group-hover:scale-105" loading="lazy" />
      <div className={`absolute inset-0 ${labelPos === "top" ? "bg-gradient-to-b from-[#0A0A0A]/55 via-transparent to-transparent" : "bg-gradient-to-t from-[#0A0A0A]/65 via-[#0A0A0A]/10 to-transparent"}`} />
      <div className={`absolute inset-0 p-4 sm:p-5 flex flex-col ${labelPos === "top" ? "justify-start" : "justify-end"}`}>
        <h3 className="text-white text-xl sm:text-2xl md:text-[1.6rem] leading-[1.05] tracking-tight" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}>{theme.name}</h3>
        <span className="block w-7 h-px bg-white/70 mt-2 group-hover:w-12 transition-all" />
      </div>
    </Link>
  );
};

const WoeIntro = () => (
  <div>
    <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#8A7B67] mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>The World of Edyte</p>
    <p className="text-2xl sm:text-3xl md:text-[1.9rem] text-[#1A1712] italic leading-[1.15]" style={{ fontFamily: "'Playfair Display', serif" }}>Curated worlds of<br/>fashion, waiting to<br/>be discovered.</p>
  </div>
);

const WoeExplore = ({ className = "" }) => (
  <Link to="/themes" className={`inline-flex items-center gap-2 text-[10px] sm:text-xs tracking-[0.22em] uppercase text-[#1A1712] border-b border-[#1A1712] pb-1.5 w-fit hover:gap-3 transition-all ${className}`} data-testid="explore-all-themes-btn">Explore All Themes <ArrowRight className="w-3.5 h-3.5" /></Link>
);

const WorldOfEdyteSection = () => {
  const [themes, setThemes] = useState([]);
  useEffect(() => {
    api.get("/themes").then(r => {
      const all = r.data || [];
      let list = all.filter(t => t.home_featured);
      if (list.length === 0) list = all.filter(t => t.type !== "price").slice(0, 5);
      list.sort((a, b) => (a.home_order ?? a.order ?? 99) - (b.home_order ?? b.order ?? 99));
      setThemes(list.slice(0, 5));
    }).catch(() => {});
  }, []);
  if (themes.length === 0) return null;
  const [t0, t1, t2, t3, t4] = themes; // 0 AfterDark, 1 Maharani, 2 Beach, 3 Minimal, 4 Romantic

  return (
    <section className="py-8 sm:py-10 md:py-16 bg-white" data-testid="world-of-edyte-section">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10">

        {/* DESKTOP — brick-offset editorial masonry (explicit grid, matches template) */}
        <div className="hidden md:grid" style={{ gridTemplateColumns: "repeat(12, 1fr)", gridTemplateRows: "repeat(10, 46px)", gap: "12px" }}>
          <div style={{ gridColumn: "1 / 4", gridRow: "2 / 5" }}><WoeIntro /></div>
          <WoeTile theme={t0} labelPos="bottom" className="h-full" style={{ gridColumn: "4 / 8", gridRow: "1 / 6" }} />
          <WoeTile theme={t1} labelPos="top" className="h-full" style={{ gridColumn: "8 / 10", gridRow: "1 / 9" }} />
          <WoeTile theme={t2} labelPos="top" className="h-full" style={{ gridColumn: "10 / 13", gridRow: "1 / 10" }} />
          <WoeTile theme={t3} labelPos="bottom" className="h-full" style={{ gridColumn: "1 / 5", gridRow: "6 / 11" }} />
          <WoeTile theme={t4} labelPos="bottom" className="h-full" style={{ gridColumn: "5 / 8", gridRow: "6 / 11" }} />
          <div style={{ gridColumn: "10 / 13", gridRow: "10 / 11" }} className="flex items-end justify-end"><WoeExplore /></div>
        </div>

        {/* MOBILE — optimized editorial cluster (unchanged) */}
        <div className="md:hidden">
          <WoeIntro />
          <div className="grid grid-cols-2 gap-3 mt-5">
            <WoeTile theme={t0} className="aspect-[3/4]" labelPos="bottom" />
            <WoeTile theme={t1} className="aspect-[3/4]" labelPos="top" />
            <WoeTile theme={t2} className="aspect-[3/4]" labelPos="top" />
            <WoeTile theme={t3} className="aspect-[3/4]" labelPos="bottom" />
            <WoeTile theme={t4} className="col-span-2 aspect-[16/9]" labelPos="bottom" />
          </div>
          <WoeExplore className="mt-5" />
        </div>

      </div>
    </section>
  );
};

// ==================== EDITORIAL PAGE ====================
const EditorialPage = () => {
  const { slug } = useParams();
  const [ed, setEd] = useState(null);
  const [products, setProducts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const isMobile = useIsMobile();
  const [swipeDone, setSwipeDone] = useState(false);
  useSEO({
    title: ed?.name ? `${ed.name} — Edyte Editorial` : "Editorial — Edyte",
    description: ed?.subtitle || ed?.description || "A featured Edyte editorial — a curated edit from India's independent fashion labels.",
  });
  useEffect(() => {
    setLoading(true);
    api.get(`/editorials/${slug}`).then(r => {
      setEd(r.data);
      const shuffled = [...(r.data.products || [])].sort(() => Math.random() - 0.5);
      setProducts(shuffled); setFiltered(shuffled);
    }).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen pt-16 sm:pt-24 px-4 sm:px-6 md:px-10 bg-white" data-testid="editorial-loading"><div className="max-w-[1400px] mx-auto"><Link to="/" className="text-xs text-[#71717A]">Back</Link><div className="skeleton h-10 w-48 mb-4 mt-4"></div></div></div>;
  if (notFound || !ed) return <div className="min-h-screen pt-24 flex items-center justify-center bg-white"><p className="text-[#71717A]">Editorial not found</p></div>;

  if (isMobile && !swipeDone && products.length > 0) {
    return <SwipeStack kind="edit" title={ed.name} subtitle={ed.subtitle || ed.description} coverImage={ed.cover_image} products={products} onExit={() => setSwipeDone(true)} themeContext={ed.name} />;
  }

  return (
    <div className="min-h-screen bg-white" data-testid="editorial-page">
      <section className="relative h-[32vh] sm:h-[38vh] md:h-[46vh] min-h-[240px]">
        {ed.cover_image && <img src={ed.cover_image} alt={ed.name} className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 hero-overlay"></div>
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-[1400px] mx-auto w-full px-4 sm:px-6 md:px-10 pb-6 sm:pb-10">
            <Link to="/" className="flex items-center gap-2 text-[10px] sm:text-xs tracking-[0.15em] uppercase text-white/80 hover:text-white transition-colors mb-3" data-testid="editorial-back"><ArrowLeft className="w-3.5 h-3.5" /> Back</Link>
            <p className="text-[10px] sm:text-xs tracking-[0.25em] uppercase text-[#FF4FB4] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Featured Editorial{ed.status === "archived" ? " · Archived" : ""}</p>
            <h1 className="font-heading text-2xl sm:text-3xl lg:text-5xl tracking-tight mb-2 text-white" data-testid="editorial-title">{ed.name}</h1>
            {ed.description && <p className="text-white/85 text-xs sm:text-sm md:text-base max-w-xl">{ed.description}</p>}
          </div>
        </div>
      </section>
      <section className="px-4 sm:px-6 md:px-10 py-6 sm:py-10">
        <div className="max-w-[1400px] mx-auto">
          <ProductFilters products={products} onFilter={setFiltered} />
          {filtered.length === 0 ? (
            <div className="text-center py-16"><p className="text-[#71717A] text-sm">No products in this editorial yet</p></div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5">
              {filtered.map((p, i) => <ProductCard key={p.id} product={p} index={i} themeContext={ed.name} />)}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
};

// ==================== HOME ====================
const Home = () => {
  useSEO({
    title: "Edyte — India's Independent Fashion, Curated",
    description: "The editorial gateway to India's emerging fashion labels. Discover distinct edits — Beach Club, Print Story, Desi by Design, Social Edit — curated with AI precision.",
  });
  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <HeroSection />
      <AiSearchSection />
      <FeaturedStorySection />
      <WorldOfEdyteSection />
      <EdytePickSection />
      <DiscoverLabelsSection />
      <AboutFAQSection />
      <Footer />
    </div>
  );
};

// ==================== THEMES GALLERY PAGE ====================
const ThemesPage = () => {
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  useSEO({
    title: "All Themes — Edyte",
    description: "Explore every Edyte edit — curated worlds of India's independent fashion, from Beach Club to Modern Maharani.",
  });
  useEffect(() => {
    api.get("/themes").then(r => setThemes((r.data || []).filter(t => t.type !== "price"))).catch(() => {}).finally(() => setLoading(false));
  }, []);
  return (
    <div className="min-h-screen bg-white" data-testid="themes-page">
      <section className="pt-24 sm:pt-28 pb-6 sm:pb-8 bg-[#FFF8F0]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10">
          <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#8A7B67] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>The World of Edyte</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-[#1A1712] tracking-tight" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}>All Themes</h1>
        </div>
      </section>
      <section className="py-8 sm:py-12">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">{[...Array(6)].map((_, i) => <div key={i} className="skeleton aspect-[4/5] rounded-2xl" />)}</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-5">
              {themes.map((t, i) => (
                <Link key={t.id} to={`/theme/${t.slug || t.id}`} className="group relative overflow-hidden rounded-2xl aspect-[4/5] fade-in" style={{ animationDelay: `${Math.min(i * 0.05, 0.4)}s`, opacity: 0 }} data-testid={`themes-page-card-${t.id}`}>
                  <img src={t.image_url} alt={t.name} className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-[900ms] group-hover:scale-105" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/75 via-[#0A0A0A]/15 to-transparent" />
                  <div className="absolute inset-0 p-5 sm:p-7 flex flex-col justify-end text-white">
                    {t.subtitle && <p className="text-[9px] sm:text-[10px] tracking-[0.2em] uppercase text-white/80 mb-1.5" style={{ fontFamily: "'Outfit', sans-serif" }}>{t.subtitle}</p>}
                    <h2 className="text-2xl sm:text-3xl md:text-4xl leading-tight tracking-tight" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}>{t.name}</h2>
                    <span className="mt-2 inline-flex items-center gap-2 text-[10px] sm:text-xs tracking-[0.15em] uppercase">Explore <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" /></span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
};

// ==================== LABELS (View All) PAGE ====================
const LabelsPage = () => {
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  useSEO({
    title: "All Labels — Edyte",
    description: "Browse every independent Indian fashion label on Edyte. Distinct stories, timeless style — curated for the modern dresser.",
  });

  useEffect(() => {
    api.get("/labels/all").then(r => setLabels(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="labels-page">
      {/* SMALL HERO BANNER */}
      <section className="pt-20 sm:pt-24 md:pt-28 pb-6 sm:pb-10 bg-white border-b border-[#E5E5E5]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10">
          <p className="text-[10px] sm:text-xs tracking-[0.28em] uppercase text-[#71717A] mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Discover Labels <span className="text-[#A1A1AA] ml-1">✦</span></p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl text-[#0A0A0A] tracking-tight leading-tight" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }} data-testid="labels-page-title">
            Independent labels.<br className="sm:hidden" /> Distinct stories.
          </h1>
          <p className="text-sm sm:text-base text-[#52525B] mt-3 sm:mt-4 max-w-[640px] leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }}>
            A growing edit of India's most compelling independent fashion labels — each known for a distinct point of view.
          </p>
        </div>
      </section>
      {/* GRID */}
      <section className="py-8 sm:py-12 md:py-16">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {[...Array(6)].map((_, i) => <div key={i} className="skeleton aspect-[3/4]" style={{ borderTopLeftRadius: '50% 24%', borderTopRightRadius: '50% 24%' }}></div>)}
            </div>
          ) : labels.length === 0 ? (
            <p className="text-center text-[#71717A] py-10" data-testid="labels-empty">No labels available yet.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 md:gap-7">
              {labels.map(l => <DiscoverLabelCard key={l.id} label={l} />)}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
};

// ==================== PRODUCT FILTERS ====================
const PRICE_BUCKETS = [
  { label: "Under ₹2,000", min: 0, max: 2000 },
  { label: "₹2,000 – ₹5,000", min: 2000, max: 5000 },
  { label: "₹5,000 – ₹10,000", min: 5000, max: 10000 },
  { label: "₹10,000 – ₹20,000", min: 10000, max: 20000 },
  { label: "Above ₹20,000", min: 20000, max: Infinity },
];

const ProductFilters = ({ products, onFilter, showBrand = true }) => {
  const [selectedBrands, setSelectedBrands] = useState(new Set());
  const [selectedBuckets, setSelectedBuckets] = useState(new Set());
  const [selectedCrafts, setSelectedCrafts] = useState(new Set());
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();

  const allBrands = Array.from(new Set(products.map(p => p.brand_name).filter(Boolean))).sort();
  const allCrafts = Array.from(new Set(products.map(p => p.craft).filter(Boolean))).sort();

  useEffect(() => {
    const filtered = products.filter(p => {
      if (selectedBrands.size > 0 && !selectedBrands.has(p.brand_name)) return false;
      if (selectedBuckets.size > 0) {
        const price = p.price || 0;
        const inAny = Array.from(selectedBuckets).some(idx => {
          const b = PRICE_BUCKETS[idx];
          return price >= b.min && price < b.max;
        });
        if (!inAny) return false;
      }
      if (selectedCrafts.size > 0 && !selectedCrafts.has(p.craft)) return false;
      return true;
    });
    onFilter(filtered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrands, selectedBuckets, selectedCrafts, products]);

  // Body scroll lock while the mobile sheet is open
  useEffect(() => {
    if (!isMobile || !open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open, isMobile]);

  const toggleBrand = (b) => {
    setSelectedBrands(prev => {
      const next = new Set(prev);
      if (next.has(b)) next.delete(b); else next.add(b);
      return next;
    });
  };
  const toggleBucket = (i) => {
    setSelectedBuckets(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };
  const toggleCraft = (c) => {
    setSelectedCrafts(prev => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c); else next.add(c);
      return next;
    });
  };
  const clearAll = () => { setSelectedBrands(new Set()); setSelectedBuckets(new Set()); setSelectedCrafts(new Set()); };
  const activeCount = selectedBrands.size + selectedBuckets.size + selectedCrafts.size;

  // Shared filter chips body (used by both desktop inline and mobile sheet)
  const FilterChips = (
    <div className="space-y-5">
      {showBrand && allBrands.length > 1 && (
        <div>
          <p className="text-[11px] tracking-[0.15em] uppercase text-[#71717A] mb-3">Brand</p>
          <div className="flex flex-wrap gap-2">
            {allBrands.map(b => (
              <button
                key={b}
                type="button"
                onClick={() => toggleBrand(b)}
                className={`px-4 py-2 text-xs tracking-wide border min-h-[40px] active:scale-95 transition-transform ${selectedBrands.has(b) ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]' : 'bg-white text-[#0A0A0A] border-[#E5E5E5] hover:border-[#0A0A0A]'}`}
                data-testid={`filter-brand-${b}`}
              >
                {b}
              </button>
            ))}
          </div>
        </div>
      )}
      <div>
        <p className="text-[11px] tracking-[0.15em] uppercase text-[#71717A] mb-3">Price</p>
        <div className="flex flex-wrap gap-2">
          {PRICE_BUCKETS.map((b, i) => (
            <button
              key={b.label}
              type="button"
              onClick={() => toggleBucket(i)}
              className={`px-4 py-2 text-xs tracking-wide border min-h-[40px] active:scale-95 transition-transform ${selectedBuckets.has(i) ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]' : 'bg-white text-[#0A0A0A] border-[#E5E5E5] hover:border-[#0A0A0A]'}`}
              data-testid={`filter-price-${i}`}
            >
              {b.label}
            </button>
          ))}
        </div>
      </div>
      {allCrafts.length > 1 && (
        <div>
          <p className="text-[11px] tracking-[0.15em] uppercase text-[#71717A] mb-3">Craft</p>
          <div className="flex flex-wrap gap-2">
            {allCrafts.map(c => (
              <button
                key={c}
                type="button"
                onClick={() => toggleCraft(c)}
                className={`px-4 py-2 text-xs tracking-wide border min-h-[40px] active:scale-95 transition-transform capitalize ${selectedCrafts.has(c) ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]' : 'bg-white text-[#0A0A0A] border-[#E5E5E5] hover:border-[#0A0A0A]'}`}
                data-testid={`filter-craft-${c}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Sticky filter bar — toggle button + (desktop) inline panel */}
      <div className="mb-6 sm:mb-8 border-b border-[#E5E5E5] sticky top-16 sm:top-20 md:top-24 z-30 bg-white -mx-4 sm:-mx-6 md:-mx-10 px-4 sm:px-6 md:px-10" data-testid="product-filters">
        <div className="flex items-center justify-between gap-3 py-3 sm:py-4">
          <button
            type="button"
            onClick={() => setOpen(o => !o)}
            className="flex items-center gap-2 text-[11px] sm:text-xs tracking-[0.18em] uppercase text-[#0A0A0A] hover:text-[#FF4FB4] transition-colors flex-1 text-left"
            data-testid="filters-toggle-btn"
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            Filters
            {activeCount > 0 && <span className="bg-[#0A0A0A] text-white px-1.5 py-0.5 text-[9px] tracking-normal">{activeCount}</span>}
            <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${open && !isMobile ? 'rotate-180' : ''}`} />
          </button>
          {/* Clear all — always visible when at least one filter is active (mobile + desktop) */}
          {activeCount > 0 && (
            <button
              type="button"
              onClick={clearAll}
              className="text-[11px] tracking-[0.18em] uppercase text-[#FF4FB4] hover:text-[#0A0A0A] transition-colors px-2 py-2"
              data-testid="filters-clear-btn"
            >
              Clear all
            </button>
          )}
        </div>
        {/* Desktop inline panel — hidden on mobile (mobile uses the bottom sheet below) */}
        {open && !isMobile && (
          <div className="pb-6">{FilterChips}</div>
        )}
      </div>

      {/* Mobile bottom-sheet — rendered as a sibling so it overlays everything */}
      {open && isMobile && (
        <div className="fixed inset-0 z-[60]" data-testid="filters-mobile-sheet" role="dialog" aria-modal="true">
          {/* Backdrop — tap to close */}
          <button
            type="button"
            aria-label="Close filters"
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-black/45 w-full h-full cursor-pointer"
            data-testid="filters-mobile-backdrop"
          />
          {/* Sheet */}
          <div className="absolute left-0 right-0 bottom-0 bg-white max-h-[88vh] flex flex-col rounded-t-2xl shadow-2xl animate-slide-up">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E5E5]">
              <h3 className="text-sm tracking-[0.18em] uppercase text-[#0A0A0A] flex items-center gap-2">
                <SlidersHorizontal className="w-4 h-4" /> Filters
                {activeCount > 0 && <span className="bg-[#0A0A0A] text-white px-1.5 py-0.5 text-[9px] tracking-normal">{activeCount}</span>}
              </h3>
              <div className="flex items-center gap-2">
                {activeCount > 0 && (
                  <button
                    type="button"
                    onClick={clearAll}
                    className="text-[11px] tracking-[0.18em] uppercase text-[#FF4FB4] active:scale-95 transition-transform px-2 py-1.5"
                    data-testid="filters-clear-btn-mobile"
                  >
                    Clear all
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="w-9 h-9 flex items-center justify-center text-[#0A0A0A] active:scale-95 transition-transform"
                  data-testid="filters-mobile-close"
                  aria-label="Close filters"
                >
                  <XIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
            {/* Body */}
            <div className="overflow-y-auto overscroll-contain px-5 py-5 flex-1">
              {FilterChips}
            </div>
            {/* Footer */}
            <div className="px-5 py-4 border-t border-[#E5E5E5] bg-white">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="w-full py-3.5 text-xs tracking-[0.18em] uppercase bg-[#0A0A0A] text-white active:scale-[0.98] transition-transform"
                data-testid="filters-mobile-apply"
              >
                Show results
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ==================== ALL PRODUCTS ====================
const AllProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  useSEO({
    title: "Edyte Pick — Curated Indie Fashion",
    description: "Shop the Edyte Pick: a hand-selected edit of independent Indian fashion. Filter by label or price and find pieces that feel like you.",
  });

  useEffect(() => {
    setLoading(true);
    api.get(`/products/all?page=1&limit=500`).then(r => { setProducts(r.data.products); setFiltered(r.data.products); }).catch(() => {}).finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white pt-16 sm:pt-20 md:pt-24" data-testid="all-products-page">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 py-4 sm:py-6 md:py-8">
        <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl tracking-tight text-[#0A0A0A] mb-1 sm:mb-2">Edyte Pick</h1>
        <p className="text-[#71717A] text-sm mb-6 sm:mb-8">Our curated edit of indie fashion</p>
        {loading ? <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">{[...Array(20)].map((_, i) => <div key={i} className="skeleton aspect-[2/3] rounded-2xl"></div>)}</div> : (
          <>
            <ProductFilters products={products} onFilter={setFiltered} />
            {filtered.length === 0 ? (
              <div className="text-center py-16"><p className="text-[#71717A] text-sm">No products match these filters</p></div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">{filtered.map((p, i) => <ProductCard key={p.id} product={p} index={i} showCaption={false} />)}</div>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

// ==================== SWIPE STACK (mobile Tinder-style) ====================
const SWIPE_THRESHOLD = 90;
const TAP_THRESHOLD = 8;

const SwipeAuthModal = ({ onClose, onSuccess }) => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("register");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name);
      onSuccess();
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center" data-testid="swipe-auth-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full bg-white rounded-t-3xl p-6 pb-8 shadow-2xl animate-slide-up">
        <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full hover:bg-[#F4F4F5]" aria-label="Close" data-testid="swipe-auth-close"><XIcon className="w-5 h-5 text-[#0A0A0A]" /></button>
        <p className="text-[10px] tracking-[0.22em] uppercase text-[#FF4FB4] mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Join Edyte</p>
        <h2 className="font-heading text-2xl tracking-tight text-[#0A0A0A] mb-1">Save this piece</h2>
        <p className="text-sm text-[#52525B] mb-5">{mode === "login" ? "Sign in to save pieces you love." : "Create a free account to save pieces — you'll pick up right where you left off."}</p>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-3 mb-4 rounded-lg" data-testid="swipe-auth-error">{error}</div>}
        <GoogleSignInButton label={mode === "login" ? "Continue with Google" : "Sign up with Google"} />
        <div className="flex items-center gap-3 my-4">
          <div className="h-px flex-1 bg-[#E5E5E5]" /><span className="text-xs text-[#71717A]">or</span><div className="h-px flex-1 bg-[#E5E5E5]" />
        </div>
        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && <input type="text" placeholder="Name" value={name} onChange={e => setName(e.target.value)} className="w-full border border-[#E5E5E5] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#0A0A0A]" data-testid="swipe-auth-name" />}
          <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} className="w-full border border-[#E5E5E5] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#0A0A0A]" data-testid="swipe-auth-email" required />
          <input type="password" placeholder={mode === "login" ? "Password" : "Password (min 6 chars)"} value={password} onChange={e => setPassword(e.target.value)} className="w-full border border-[#E5E5E5] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#0A0A0A]" data-testid="swipe-auth-password" required />
          <button type="submit" disabled={loading} className="w-full bg-[#0A0A0A] text-white py-3 rounded-lg text-sm tracking-wider uppercase hover:bg-[#FF4FB4] transition-colors disabled:opacity-50" data-testid="swipe-auth-submit">{loading ? "Please wait..." : (mode === "login" ? "Sign In & Save" : "Create Account & Save")}</button>
        </form>
        <p className="text-center text-xs text-[#71717A] mt-4">
          {mode === "login" ? "New to Edyte? " : "Already have an account? "}
          <button type="button" onClick={() => { setError(""); setMode(mode === "login" ? "register" : "login"); }} className="text-[#0A0A0A] underline" data-testid="swipe-auth-toggle">{mode === "login" ? "Create account" : "Sign in"}</button>
        </p>
      </div>
    </div>
  );
};

const SwipeStack = ({ kind, title, subtitle, coverImage, products, onExit, themeContext }) => {
  const { user, savedIds, toggleSave } = useAuth();
  const deckProducts = products.slice(0, 10);
  const deck = [{ type: "demo" }, ...deckProducts.map(p => ({ type: "product", product: p }))];
  const deckLen = deck.length;
  const [index, setIndex] = useState(0);
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [fly, setFly] = useState(null); // 'left' | 'right' | null
  const [authOpen, setAuthOpen] = useState(false);
  const pendingRef = useRef(null);
  const startX = useRef(0);

  useEffect(() => { if (index >= deckLen) onExit(); }, [index, deckLen, onExit]);

  const advance = useCallback(() => { setFly(null); setDragX(0); setIndex(i => i + 1); }, []);

  const handleDecision = (dir) => {
    if (fly) return;
    const current = deck[index];
    if (!current) return;
    if (current.type === "demo") { setFly(dir); return; }
    const product = current.product;
    if (dir === "right") {
      if (!user) { pendingRef.current = product; setAuthOpen(true); setDragX(0); return; }
      if (!savedIds.has(product.id)) toggleSave(product.id);
      else { try { toast("Already saved", { duration: 1200 }); } catch {} }
      setFly("right");
    } else {
      setFly("left");
    }
  };

  const onPointerDown = (e) => { if (fly) return; setDragging(true); startX.current = e.clientX; try { e.currentTarget.setPointerCapture(e.pointerId); } catch {} };
  const onPointerMove = (e) => { if (!dragging) return; setDragX(e.clientX - startX.current); };
  const onPointerUp = () => {
    if (!dragging) return;
    setDragging(false);
    const absX = Math.abs(dragX);
    if (absX > SWIPE_THRESHOLD) { handleDecision(dragX > 0 ? "right" : "left"); return; }
    if (absX < TAP_THRESHOLD) {
      // Treat a near-stationary release as a tap → open the brand/product page in the SAME tab (mobile)
      const current = deck[index];
      if (current && current.type === "product" && current.product?.product_link) {
        trackProductClick(current.product, themeContext);
        window.location.href = buildAffiliateUrl(current.product, themeContext);
        return;
      }
    }
    setDragX(0);
  };

  const onAuthSuccess = async () => {
    const product = pendingRef.current;
    setAuthOpen(false);
    if (product) {
      try {
        await api.post("/user/save", { product_id: product.id });
        try { toast.success("Saved", { duration: 1500 }); } catch {}
      } catch { try { toast.error("Couldn't save — please try again"); } catch {} }
      pendingRef.current = null;
      setFly("right");
    }
  };

  const topTransform = () => {
    if (fly === "right") return { transform: "translateX(125vw) rotate(16deg)", opacity: 0 };
    if (fly === "left") return { transform: "translateX(-125vw) rotate(-16deg)", opacity: 0 };
    return { transform: `translateX(${dragX}px) rotate(${dragX * 0.05}deg)`, opacity: 1 };
  };
  const saveOpacity = Math.max(0, Math.min(1, dragX / 100));
  const skipOpacity = Math.max(0, Math.min(1, -dragX / 100));

  const renderCardInner = (card, isTop) => {
    if (card.type === "demo") {
      return (
        <>
          <img src={shopifyImg(coverImage, 700)} alt={title} className="absolute inset-0 w-full h-full object-cover" draggable="false" loading={isTop ? "eager" : "lazy"} decoding="async" fetchPriority={isTop ? "high" : "low"} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/30" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white px-7">
            <p className="text-[10px] tracking-[0.28em] uppercase text-white/80 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{kind === "label" ? "Label" : "Edit"}</p>
            <h2 className="font-heading text-3xl leading-tight mb-2">{title}</h2>
            {subtitle && <p className="text-sm text-white/85 max-w-[260px] mb-8 line-clamp-3">{subtitle}</p>}
            <div className="swipe-hint-wiggle flex items-center gap-7">
              <div className="flex flex-col items-center gap-1.5 text-white/85"><span className="w-12 h-12 rounded-full border-2 border-white/50 flex items-center justify-center"><ArrowLeft className="w-5 h-5" /></span><span className="text-[10px] tracking-[0.18em] uppercase">Skip</span></div>
              <div className="flex flex-col items-center gap-1.5"><span className="w-12 h-12 rounded-full border-2 border-[#FF4FB4] bg-[#FF4FB4]/25 flex items-center justify-center"><Heart className="w-5 h-5 text-white fill-white" /></span><span className="text-[10px] tracking-[0.18em] uppercase">Save</span></div>
            </div>
            <p className="text-xs text-white/70 mt-7">Tap a card to shop · swipe right to save · left to skip</p>
          </div>
        </>
      );
    }
    const p = card.product;
    return (
      <>
        <img src={shopifyImg(p.image_url, 700)} alt={p.title} className="absolute inset-0 w-full h-full object-cover object-top" draggable="false" loading={isTop ? "eager" : "lazy"} decoding="async" fetchPriority={isTop ? "high" : "low"} />
        <span className="absolute top-4 left-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-sm text-[#0A0A0A] text-[10px] tracking-[0.14em] uppercase shadow-sm pointer-events-none" style={{ fontFamily: "'Outfit', sans-serif" }}><ExternalLink className="w-3 h-3" /> Tap to shop</span>
        <div className="absolute inset-x-0 bottom-0 p-5 pt-16 bg-gradient-to-t from-black/85 via-black/35 to-transparent text-white">
          <span className="text-[10px] tracking-[0.18em] uppercase text-white/80 block mb-1">{p.brand_name}</span>
          <h3 className="font-heading text-xl leading-tight mb-1 line-clamp-2">{p.title}</h3>
          <span className="text-base font-semibold">{'\u20B9'}{p.price?.toLocaleString('en-IN')}</span>
        </div>
      </>
    );
  };

  const totalProducts = deckProducts.length;
  const currentNum = Math.min(index, totalProducts); // 0 while on the demo/intro card, 1..N on products

  const layers = [];
  for (let off = 3; off >= 0; off--) {
    const i = index + off;
    if (i >= deckLen) continue;
    layers.push({ card: deck[i], off, i });
  }

  return (
    <div className="sm:hidden fixed inset-0 z-40 bg-[#FFF8F0] flex flex-col pt-16" data-testid="swipe-stack">
      <div className="flex items-center justify-between px-4 pt-0.5 pb-1 gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[9px] tracking-[0.24em] uppercase text-[#FF4FB4]" style={{ fontFamily: "'Outfit', sans-serif" }}>{kind === "label" ? "Label" : "Edit"}</p>
          <h1 className="font-heading text-base tracking-tight text-[#0A0A0A] truncate">{title}</h1>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="px-2.5 py-1 rounded-full bg-white border border-[#E5E5E5] text-[10px] tracking-[0.12em] uppercase text-[#0A0A0A] tabular-nums shadow-sm" data-testid="swipe-counter">
            {currentNum === 0 ? `${totalProducts} pieces` : `${currentNum} of ${totalProducts}`}
          </span>
          <button onClick={onExit} className="swipe-ctrl-btn w-9 h-9 rounded-full bg-white border border-[#E5E5E5] flex items-center justify-center shadow-sm" aria-label="See all products" data-testid="swipe-exit-btn"><XIcon className="w-4 h-4 text-[#0A0A0A]" /></button>
        </div>
      </div>
      {/* Progress bar across the deck */}
      <div className="px-4 pb-1.5">
        <div className="h-1 rounded-full bg-black/10 overflow-hidden">
          <div className="h-full bg-[#FF4FB4] rounded-full transition-all duration-300 ease-out" style={{ width: `${totalProducts ? (currentNum / totalProducts) * 100 : 0}%` }} data-testid="swipe-progress" />
        </div>
      </div>

      <div className="relative flex-1 mx-2 mb-1" data-testid="swipe-card-area">
        {layers.map(({ card, off, i }) => {
          const isTop = off === 0;
          const style = isTop
            ? { ...topTransform(), zIndex: 30 }
            : { transform: `translateY(${off * 10}px) scale(${1 - off * 0.035})`, opacity: off >= 3 ? 0.45 : off === 2 ? 0.8 : 1, zIndex: 30 - off };
          return (
            <div
              key={i}
              className={`swipe-card ${isTop ? "swipe-card-top" : ""} ${(!isTop || !dragging) ? "swipe-card-anim" : ""}`}
              style={style}
              onPointerDown={isTop ? onPointerDown : undefined}
              onPointerMove={isTop ? onPointerMove : undefined}
              onPointerUp={isTop ? onPointerUp : undefined}
              onPointerCancel={isTop ? onPointerUp : undefined}
              onTransitionEnd={isTop ? (e) => { if (fly && e.propertyName === "transform") advance(); } : undefined}
              data-testid={isTop ? "swipe-top-card" : undefined}
            >
              {renderCardInner(card, isTop)}
              {isTop && card.type !== "demo" && (
                <>
                  <span className="swipe-badge swipe-badge-save" style={{ opacity: saveOpacity }}>Save</span>
                  <span className="swipe-badge swipe-badge-skip" style={{ opacity: skipOpacity }}>Skip</span>
                </>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-end justify-center gap-10 pb-4 pt-0">
        <button onClick={() => handleDecision("left")} className="swipe-ctrl-btn flex flex-col items-center gap-1" data-testid="swipe-skip-btn">
          <span className="w-11 h-11 rounded-full bg-white border-2 border-[#E5E5E5] flex items-center justify-center shadow-md text-[#71717A]"><XIcon className="w-[18px] h-[18px]" /></span>
          <span className="text-[9px] tracking-[0.16em] uppercase text-[#71717A] flex items-center gap-0.5"><ArrowLeft className="w-2.5 h-2.5" /> Skip</span>
        </button>
        <button onClick={onExit} className="swipe-ctrl-btn flex flex-col items-center gap-1 pb-0.5" data-testid="swipe-to-grid-btn" aria-label="See all products">
          <span className="w-9 h-9 rounded-full bg-white border border-[#E5E5E5] flex items-center justify-center shadow-sm text-[#0A0A0A]"><LayoutGrid className="w-4 h-4" /></span>
          <span className="text-[9px] tracking-[0.16em] uppercase text-[#71717A]">All</span>
        </button>
        <button onClick={() => handleDecision("right")} className="swipe-ctrl-btn flex flex-col items-center gap-1" data-testid="swipe-save-btn">
          <span className="w-12 h-12 rounded-full bg-[#FF4FB4] flex items-center justify-center shadow-lg text-white"><Heart className="w-5 h-5 fill-white" /></span>
          <span className="text-[9px] tracking-[0.16em] uppercase text-[#0A0A0A] flex items-center gap-0.5">Save <ArrowRight className="w-2.5 h-2.5" /></span>
        </button>
      </div>

      {authOpen && <SwipeAuthModal onClose={() => { setAuthOpen(false); pendingRef.current = null; setDragX(0); }} onSuccess={onAuthSuccess} />}
    </div>
  );
};


// ==================== BRAND PAGE ====================
const BrandProductsPage = () => {
  const { brandId } = useParams();
  const [brand, setBrand] = useState(null);
  const [products, setProducts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const isMobile = useIsMobile();
  const [swipeDone, setSwipeDone] = useState(false);
  useSEO({
    title: brand?.brand_name ? `${brand.brand_name} — Shop Independent Indian Fashion | Edyte` : "Edyte Label — Independent Indian Fashion Brand",
    description: ensureSeoDescription(
      brand?.description || (brand?.brand_name ? `${brand.brand_name}${brand.tagline ? " — " + brand.tagline : ""}` : null),
      "Shop the full curated collection on Edyte — independent Indian fashion, handpicked for style, with direct links to buy from the label's own website."
    ),
  });

  useEffect(() => {
    setLoading(true);
    api.get(`/brands/${brandId}/products?page=1&limit=500`).then(r => {
      setBrand(r.data.brand);
      setProducts(r.data.products);
      setFiltered(r.data.products);
      setTotal(r.data.total);
      api.post("/analytics/view", { event_type: "brand_view", target_id: brandId, target_name: r.data.brand?.brand_name }).catch(() => {});
      // Meta Pixel: landing on an individual label detail page
      try {
        if (typeof window.fbq === "function") {
          window.fbq("track", "ViewContent", { content_type: "label", label_name: r.data.brand?.brand_name || undefined });
        }
      } catch {}
    }).catch(() => {}).finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [brandId]);

  if (isMobile && !swipeDone && !loading && products.length > 0) {
    return <SwipeStack kind="label" title={brand?.brand_name} subtitle={brand?.tagline} coverImage={brand?.hero_image || products[0]?.image_url} products={products} onExit={() => setSwipeDone(true)} />;
  }

  return (
    <div className="min-h-screen bg-white pt-16 sm:pt-20 md:pt-24" data-testid="brand-products-page">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 py-4 sm:py-6 md:py-8">
        <Link to="/" className="flex items-center gap-2 text-[10px] sm:text-xs tracking-[0.15em] uppercase text-[#71717A] hover:text-[#0A0A0A] transition-colors mb-4 sm:mb-6" data-testid="back-to-home-btn"><ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Back to Home</Link>
        <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl tracking-tight text-[#0A0A0A] mb-1 sm:mb-2">{brand?.brand_name}</h1>
        <p className="text-[#71717A] text-sm mb-3 sm:mb-4">{total > 0 ? `${total} curated products` : 'Loading...'}</p>
        {brand?.description && (
          <p className="text-[#3F3F46] text-sm sm:text-[15px] leading-relaxed max-w-3xl mb-6 sm:mb-8" data-testid="brand-description">{brand.description}</p>
        )}
        {loading ? <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">{[...Array(12)].map((_, i) => <div key={i} className="skeleton aspect-[2/3] rounded-2xl"></div>)}</div> : !products.length ? <div className="text-center py-16"><p className="text-[#71717A] text-sm">No products available</p></div> : (
          <>
            <ProductFilters products={products} onFilter={setFiltered} showBrand={false} />
            {filtered.length === 0 ? (
              <div className="text-center py-16"><p className="text-[#71717A] text-sm">No products match these filters</p></div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">{filtered.map((p, i) => <ProductCard key={p.id} product={p} index={i} showCaption={false} />)}</div>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

// ==================== THEME PAGE ====================
const ThemePage = () => {
  const { themeId } = useParams();
  const [theme, setTheme] = useState(null);
  const [products, setProducts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();
  const [swipeDone, setSwipeDone] = useState(false);
  const location = useLocation();
  const perspId = new URLSearchParams(location.search).get("p");
  const [persp, setPersp] = useState(null);
  const displayTitle = persp?.display_title || theme?.name;
  const displayDesc = persp?.seo_description || persp?.description || theme?.description;
  useSEO({
    title: displayTitle ? `${displayTitle} — Curated Edit | Edyte` : "Edyte Curated Theme — Independent Indian Fashion",
    description: ensureSeoDescription(
      displayDesc || (displayTitle ? `${displayTitle} — a curated Edyte edit` : null),
      "Explore handpicked pieces from India's emerging independent fashion labels on Edyte, with direct links to shop each brand."
    ),
  });

  useEffect(() => { if (perspId) api.get(`/perspectives/${perspId}`).then(r => setPersp(r.data)).catch(() => {}); }, [perspId]);

  useEffect(() => {
    Promise.all([api.get(`/themes/${themeId}`), api.get(`/themes/${themeId}/products`)]).then(([t, p]) => {
      setTheme(t.data);
      // Mix-and-match: shuffle once so it's not grouped by brand
      const shuffled = [...p.data].sort(() => Math.random() - 0.5);
      setProducts(shuffled);
      setFiltered(shuffled);
      api.post("/analytics/view", { event_type: "theme_view", target_id: themeId, target_name: t.data.name }).catch(() => {});
    }).catch(() => {}).finally(() => setLoading(false));
  }, [themeId]);

  if (loading) return <div className="min-h-screen pt-16 sm:pt-20 md:pt-24 px-4 sm:px-6 md:px-10 bg-white" data-testid="theme-page-loading"><div className="max-w-[1400px] mx-auto"><Link to="/" className="text-xs text-[#71717A]">Back</Link><div className="skeleton h-10 w-48 mb-4 mt-4"></div></div></div>;
  if (!theme) return <div className="min-h-screen pt-24 flex items-center justify-center bg-white"><p className="text-[#71717A]">Theme not found</p></div>;

  if (isMobile && !swipeDone && products.length > 0) {
    return <SwipeStack kind="theme" title={displayTitle} subtitle={persp?.description || theme.subtitle || theme.description} coverImage={theme.image_url} products={products} onExit={() => setSwipeDone(true)} themeContext={theme.name} />;
  }

  return (
    <div className="min-h-screen bg-white" data-testid="theme-page">
      <section className="relative h-[30vh] sm:h-[35vh] md:h-[45vh] min-h-[240px] sm:min-h-[300px] md:min-h-[350px]">
        <img src={theme.image_url} alt={theme.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay"></div>
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-[1400px] mx-auto w-full px-4 sm:px-6 md:px-10 pb-6 sm:pb-8 md:pb-10">
            <Link to="/" className="flex items-center gap-2 text-[10px] sm:text-xs tracking-[0.15em] uppercase text-[#71717A] hover:text-[#0A0A0A] transition-colors mb-3" data-testid="back-button"><ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Back</Link>
            {(persp ? "Featured Perspective" : theme.subtitle) && <p className="text-[10px] sm:text-xs tracking-[0.2em] uppercase text-[#FF4FB4] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="theme-subtitle">{persp ? "Featured Perspective" : theme.subtitle}</p>}
            <h1 className="font-heading text-xl sm:text-2xl lg:text-4xl tracking-tighter mb-2 text-[#0A0A0A]" data-testid="theme-title">{displayTitle}</h1>
            <p className="text-[#52525B] text-xs sm:text-sm md:text-base max-w-xl">{displayDesc}</p>
          </div>
        </div>
      </section>
      <section className="px-4 sm:px-6 md:px-10 py-6 sm:py-8 md:py-12">
        <div className="max-w-[1400px] mx-auto">
          <ProductFilters products={products} onFilter={setFiltered} />
          {filtered.length === 0 ? (
            <div className="text-center py-16"><p className="text-[#71717A] text-sm">No products match these filters</p></div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5">
              {filtered.map((p, i) => <ProductCard key={p.id} product={p} index={i} themeContext={theme.name} />)}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
};

// ==================== SAVED ITEMS PAGE ====================
const SavedItemsPage = () => {
  const { user, checking } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (checking) return; // wait for auth check to resolve before redirecting
    if (!user) { navigate("/login"); return; }
    api.get("/user/saves").then(r => { setProducts(r.data.saves); setFiltered(r.data.saves); }).catch(() => {}).finally(() => setLoading(false));
  }, [user, checking, navigate]);

  return (
    <div className="min-h-screen bg-white pt-16 sm:pt-20 md:pt-24" data-testid="saved-items-page">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 py-4 sm:py-6 md:py-8">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 text-[10px] sm:text-xs tracking-[0.15em] uppercase text-[#71717A] hover:text-[#0A0A0A] transition-colors mb-4 sm:mb-6" data-testid="back-home-btn"><ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Back</button>
        <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl tracking-tight text-[#0A0A0A] mb-1 sm:mb-2">Saved Items</h1>
        <p className="text-[#71717A] text-sm mb-6 sm:mb-8">{products.length} saved piece{products.length !== 1 ? 's' : ''}</p>
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">{[...Array(8)].map((_, i) => <div key={i} className="skeleton aspect-[2/3] rounded-2xl"></div>)}</div>
        ) : !products.length ? (
          <div className="text-center py-16">
            <Heart className="w-10 h-10 text-[#E5E5E5] mx-auto mb-4" />
            <p className="text-[#71717A] text-sm mb-2">No saved items yet</p>
            <p className="text-[#A1A1AA] text-xs mb-6">Discover pieces you love and save them here</p>
            <button onClick={() => navigate("/products")} className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-6 py-3 text-xs tracking-wider uppercase hover:bg-[#FF4FB4] transition-colors" data-testid="browse-btn">Browse Collection</button>
          </div>
        ) : (
          <>
            <ProductFilters products={products} onFilter={setFiltered} />
            {filtered.length === 0 ? (
              <div className="text-center py-16"><p className="text-[#71717A] text-sm">No saved items match these filters</p></div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                {filtered.map((p, i) => <ProductCard key={p.id} product={p} index={i} showCaption={false} />)}
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

// ==================== ADMIN DASHBOARD ====================
const CHART_COLORS = ["#FF4FB4", "#0A0A0A", "#EAFA87", "#00C853", "#6366F1", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#14B8A6", "#F97316"];

// ==================== SHARE LINK GENERATOR (Admin) ====================
const SHARE_SOURCES = ["instagram", "google", "facebook", "pinterest", "youtube", "whatsapp", "twitter", "linkedin", "newsletter", "blog"];

const ShareLinkGenerator = () => {
  const [source, setSource] = useState("instagram");
  const [destType, setDestType] = useState("home");
  const [themeId, setThemeId] = useState("");
  const [labelId, setLabelId] = useState("");
  const [themes, setThemes] = useState([]);
  const [labels, setLabels] = useState([]);
  const [baseUrl, setBaseUrl] = useState("https://theedyte.com");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/themes").then(r => setThemes(r.data || [])).catch(() => {});
    api.get("/labels/all").then(r => setLabels(r.data || [])).catch(() => {});
  }, []);

  const buildUrl = () => {
    const base = (baseUrl || "").trim().replace(/\/+$/, "");
    let path = "";
    let campaign = "homepage";
    if (destType === "theme" && themeId) {
      const th = themes.find(x => x.id === themeId);
      path = `/theme/${th?.slug || themeId}`;
      campaign = slugify(th?.name || "theme");
    } else if (destType === "label" && labelId) {
      const lbl = labels.find(x => x.id === labelId);
      path = `/brand/${lbl?.slug || labelId}`;
      campaign = slugify(lbl?.brand_name || "label");
    }
    const params = new URLSearchParams({ utm_source: source, utm_medium: "social", utm_campaign: campaign });
    return `${base}${path}?${params.toString()}`;
  };

  const url = buildUrl();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true); setTimeout(() => setCopied(false), 1800);
      try { toast.success("Link copied"); } catch {}
    } catch { try { toast.error("Couldn't copy — select and copy manually"); } catch {} }
  };

  const inputCls = "w-full border border-[#E5E5E5] px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#0A0A0A]";

  return (
    <div className="bg-white border border-[#E5E5E5] p-4 sm:p-6" data-testid="share-link-generator">
      <h3 className="text-sm font-semibold text-[#0A0A0A] mb-1">Share Link Generator</h3>
      <p className="text-xs text-[#71717A] mb-4">Create a UTM-tagged Edyte link to post on your socials. When a visitor arrives via this link, their source is attributed to every outbound click they make.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <label className="block text-[10px] tracking-[0.15em] uppercase text-[#71717A] mb-1.5">Source</label>
          <select value={source} onChange={e => setSource(e.target.value)} className={inputCls} data-testid="share-link-source">
            {SHARE_SOURCES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] tracking-[0.15em] uppercase text-[#71717A] mb-1.5">Destination</label>
          <select value={destType} onChange={e => setDestType(e.target.value)} className={inputCls} data-testid="share-link-desttype">
            <option value="home">Homepage</option>
            <option value="theme">Theme / Edit</option>
            <option value="label">Label</option>
          </select>
        </div>
        {destType === "theme" && (
          <div>
            <label className="block text-[10px] tracking-[0.15em] uppercase text-[#71717A] mb-1.5">Theme</label>
            <select value={themeId} onChange={e => setThemeId(e.target.value)} className={inputCls} data-testid="share-link-theme">
              <option value="">Select theme…</option>
              {themes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        )}
        {destType === "label" && (
          <div>
            <label className="block text-[10px] tracking-[0.15em] uppercase text-[#71717A] mb-1.5">Label</label>
            <select value={labelId} onChange={e => setLabelId(e.target.value)} className={inputCls} data-testid="share-link-label">
              <option value="">Select label…</option>
              {labels.map(l => <option key={l.id} value={l.id}>{l.brand_name}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className="block text-[10px] tracking-[0.15em] uppercase text-[#71717A] mb-1.5">Base URL</label>
          <input type="text" value={baseUrl} onChange={e => setBaseUrl(e.target.value)} className={inputCls} data-testid="share-link-base" />
        </div>
      </div>
      <div className="mt-4">
        <label className="block text-[10px] tracking-[0.15em] uppercase text-[#71717A] mb-1.5">Generated Link</label>
        <div className="flex flex-col sm:flex-row gap-2">
          <input type="text" readOnly value={url} onFocus={e => e.target.select()} className="flex-1 border border-[#E5E5E5] px-3 py-2 text-sm bg-[#FAFAFA] text-[#0A0A0A] focus:outline-none focus:border-[#0A0A0A]" data-testid="share-link-output" />
          <button onClick={copy} className="px-5 py-2 text-xs tracking-[0.12em] uppercase bg-[#0A0A0A] text-white hover:bg-[#FF4FB4] transition-colors whitespace-nowrap" data-testid="share-link-copy-btn">{copied ? "Copied ✓" : "Copy Link"}</button>
        </div>
      </div>
    </div>
  );
};


// ==================== ADMIN: FEATURED EDITORIALS ====================
const editorialInput = "w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0A0A0A]";
const editorialLabel = "text-[11px] font-semibold text-[#0A0A0A] uppercase tracking-wider";

const EditorialModal = ({ editorial, onSave, onClose }) => {
  const [form, setForm] = useState({
    name: editorial?.name || "", subtitle: editorial?.subtitle || "", description: editorial?.description || "",
    keywords: (editorial?.keywords || []).join(", "), exclude_keywords: (editorial?.exclude_keywords || []).join(", "),
    min_matches: editorial?.min_matches || 1, cover_image: editorial?.cover_image || "",
    price_min: editorial?.price_min || "", price_max: editorial?.price_max || "",
    categories: editorial?.categories || [],
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/admin/categories").then(r => setCats(r.data?.categories || [])).catch(() => {}); }, []);
  const toggleCat = (name) => setForm(f => { const s = new Set(f.categories); s.has(name) ? s.delete(name) : s.add(name); return { ...f, categories: [...s] }; });
  const submit = async () => { setSaving(true); await onSave(form); setSaving(false); };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] px-4" data-testid="editorial-modal">
      <div className="bg-white p-6 w-full max-w-lg max-h-[88vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-[#0A0A0A]">{editorial ? "Edit Editorial" : "New Editorial"}</h3>
          <button onClick={onClose} data-testid="editorial-modal-close"><XIcon className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className={editorialLabel}>Editorial name</label>
            <input value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Co-Ord Sets" className={editorialInput} data-testid="editorial-name-input" />
          </div>
          <div>
            <label className={editorialLabel}>Eyebrow / subtitle</label>
            <input value={form.subtitle} onChange={e => set("subtitle", e.target.value)} placeholder="Matching sets, effortlessly styled" className={editorialInput} data-testid="editorial-subtitle-input" />
          </div>
          <div>
            <label className={editorialLabel}>Description</label>
            <textarea value={form.description} onChange={e => set("description", e.target.value)} rows={2} className={editorialInput} data-testid="editorial-description-input" />
          </div>
          <div>
            <label className={editorialLabel}>Match keywords</label>
            <textarea value={form.keywords} onChange={e => set("keywords", e.target.value)} rows={2} placeholder="co-ord, coord, co ord set, matching set" className={editorialInput} data-testid="editorial-keywords-input" />
            <p className="text-[10px] text-[#A1A1AA] mt-1">Comma-separated. Products whose title/description contain these words are pulled in — from any theme.</p>
          </div>
          <div>
            <label className={editorialLabel}>Product types (optional, more precise)</label>
            <div className="flex flex-wrap gap-1.5 mt-1 max-h-28 overflow-y-auto" data-testid="editorial-categories">
              {cats.length === 0 && <span className="text-[10px] text-[#A1A1AA]">No categories yet — run "Extract product types" from the Editorials panel.</span>}
              {cats.map(c => {
                const on = form.categories.includes(c.name);
                return <button type="button" key={c.name} onClick={() => toggleCat(c.name)} className={`text-[11px] px-2 py-1 border rounded-full transition-colors ${on ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5] text-[#71717A] hover:border-[#0A0A0A]"}`} data-testid={`cat-chip-${c.name.replace(/[^a-z0-9]+/gi, "-")}`}>{c.name} <span className="opacity-60">{c.count}</span></button>;
              })}
            </div>
            <p className="text-[10px] text-[#A1A1AA] mt-1">Matches by extracted product type (e.g. "co-ord set", "shirt") — more precise than keywords. Combine with keywords/price to narrow further.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={editorialLabel}>Exclude keywords</label>
              <input value={form.exclude_keywords} onChange={e => set("exclude_keywords", e.target.value)} placeholder="kids, saree" className={editorialInput} data-testid="editorial-exclude-input" />
            </div>
            <div>
              <label className={editorialLabel}>Min matches</label>
              <input type="number" min={1} value={form.min_matches} onChange={e => set("min_matches", e.target.value)} className={editorialInput} data-testid="editorial-minmatches-input" />
            </div>
          </div>
          <div>
            <label className={editorialLabel}>Price range ₹ (optional)</label>
            <div className="grid grid-cols-2 gap-3">
              <input type="number" min={0} value={form.price_min} onChange={e => set("price_min", e.target.value)} placeholder="Min (e.g. 0)" className={editorialInput} data-testid="editorial-pricemin-input" />
              <input type="number" min={0} value={form.price_max} onChange={e => set("price_max", e.target.value)} placeholder="Max (e.g. 5000)" className={editorialInput} data-testid="editorial-pricemax-input" />
            </div>
            <p className="text-[10px] text-[#A1A1AA] mt-1">Leave blank for no limit. Only products within this price band are pulled in.</p>
          </div>
          <div>
            <label className={editorialLabel}>Cover image URL (optional)</label>
            <input value={form.cover_image} onChange={e => set("cover_image", e.target.value)} placeholder="Leave blank to auto-pick from a matched product" className={editorialInput} data-testid="editorial-cover-input" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-xs border border-[#E5E5E5]" data-testid="editorial-cancel-btn">Cancel</button>
          <button onClick={submit} disabled={saving || !form.name.trim()} className="px-4 py-2 text-xs bg-[#0A0A0A] text-white hover:bg-[#FF4FB4] transition-colors disabled:opacity-50" data-testid="editorial-save-btn">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
};

const EditorialPreviewModal = ({ preview, onClose, onSetCover, onSaveSelection }) => {
  const MAX = 50;
  const [selected, setSelected] = useState(() => new Set(preview.selected || []));
  const [saving, setSaving] = useState(false);
  const toggle = (id) => setSelected(prev => {
    const n = new Set(prev);
    if (n.has(id)) { n.delete(id); return n; }
    if (n.size >= MAX) { toast.error(`You can hand-pick up to ${MAX} products`); return prev; }
    n.add(id); return n;
  });
  const save = async () => { setSaving(true); await onSaveSelection([...selected]); setSaving(false); };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] px-4" data-testid="editorial-preview-modal">
      <div className="bg-white w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-[#E5E5E5]">
          <div>
            <h3 className="font-medium text-[#0A0A0A]">{preview.ed?.name}</h3>
            <p className="text-xs text-[#71717A] mt-0.5">{preview.products.length} matching products · <span className="text-[#FF4FB4] font-medium" data-testid="editorial-selected-count">{selected.size}/{MAX} hand-picked</span></p>
          </div>
          <button onClick={onClose} data-testid="editorial-preview-close"><XIcon className="w-4 h-4" /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1">
          {preview.products.length === 0 ? (
            <p className="text-sm text-[#71717A] py-8 text-center">No products matched. Adjust the keywords/price and try again.</p>
          ) : (
            <>
              <p className="text-xs text-[#71717A] mb-3">Click a product to hand-pick it into this editorial (max {MAX}). Hand-picked pieces are the ones shown on the page. Use <b>Cover</b> to set the tile image. Leave none selected to show all matches automatically.</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                {preview.products.map(p => {
                  const on = selected.has(p.id);
                  return (
                    <div key={p.id} onClick={() => toggle(p.id)} className={`group relative cursor-pointer rounded-md overflow-hidden ${on ? "ring-2 ring-[#FF4FB4]" : "ring-1 ring-[#E5E5E5]"}`} data-testid={`editorial-preview-item-${p.id}`}>
                      <div className="aspect-[2/3] bg-[#F5F5F5] overflow-hidden">
                        <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                      <span className={`absolute top-1 left-1 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold ${on ? "bg-[#FF4FB4] text-white" : "bg-white/85 text-[#71717A]"}`} data-testid={`select-toggle-${p.id}`}>{on ? "✓" : "+"}</span>
                      <div className="mt-1 px-0.5">
                        <p className="text-[10px] text-[#71717A] line-clamp-1">{p.brand_name}</p>
                        <p className="text-[10px] text-[#0A0A0A] line-clamp-1">{p.title}</p>
                        <p className="text-[10px] font-medium text-[#0A0A0A]">₹{p.price}</p>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); onSetCover(p.id); }} className="absolute top-1 right-1 text-[9px] px-1.5 py-0.5 bg-white/90 hover:bg-[#0A0A0A] hover:text-white transition-colors opacity-0 group-hover:opacity-100" data-testid={`set-cover-${p.id}`}>Cover</button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 p-5 border-t border-[#E5E5E5]">
          <button onClick={() => setSelected(new Set())} className="text-xs text-[#71717A] hover:text-[#0A0A0A]" data-testid="editorial-clear-selection-btn">Clear selection</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 text-xs bg-[#0A0A0A] text-white hover:bg-[#FF4FB4] transition-colors disabled:opacity-50" data-testid="editorial-save-selection-btn">{saving ? "Saving…" : `Save selection (${selected.size})`}</button>
        </div>
      </div>
    </div>
  );
};

const EditorialsAdmin = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = () => { setLoading(true); api.get("/admin/editorials").then(r => setItems(r.data?.items || [])).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try {
      if (modal?.editorial) { await api.put(`/admin/editorials/${modal.editorial.id}`, form); toast.success("Editorial updated"); }
      else { const r = await api.post("/admin/editorials", form); toast.success(`Created — ${r.data.product_count} products matched`); }
      setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const action = async (id, path, label) => {
    setBusy(id + path);
    try {
      const r = await api.post(`/admin/editorials/${id}/${path}`);
      toast.success(label);
      if (path === "refresh") { const ed = items.find(i => i.id === id); setPreview({ ed, products: r.data.products || [], count: r.data.product_count }); }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(null); }
  };

  const setCover = async (pid) => {
    if (!preview?.ed) return;
    try { await api.put(`/admin/editorials/${preview.ed.id}`, { cover_product_id: pid, cover_image: "" }); toast.success("Cover updated"); setPreview(null); load(); }
    catch { toast.error("Failed to set cover"); }
  };

  const saveSelection = async (ids) => {
    if (!preview?.ed) return;
    try { await api.put(`/admin/editorials/${preview.ed.id}`, { selected_product_ids: ids }); toast.success(ids.length ? `${ids.length} products hand-picked` : "Selection cleared — showing all matches"); setPreview(null); load(); }
    catch { toast.error("Failed to save selection"); }
  };

  const del = async (id) => { try { await api.delete(`/admin/editorials/${id}`); toast.success("Deleted"); setConfirmDel(null); load(); } catch { toast.error("Delete failed"); } };

  const [catBusy, setCatBusy] = useState(false);
  const runCategorize = async () => {
    setCatBusy(true);
    try { const r = await api.post("/admin/categorize"); toast.success(`Extracted product types for ${r.data.categorized} products`); }
    catch { toast.error("Categorize failed"); }
    finally { setCatBusy(false); }
  };

  const liveCount = items.filter(i => i.status === "live").length;
  const badge = (s) => s === "live" ? "bg-green-50 text-green-700" : s === "archived" ? "bg-gray-100 text-gray-500" : "bg-yellow-50 text-yellow-700";

  return (
    <div data-testid="admin-editorials">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold text-[#0A0A0A]">Featured Editorials <span className="text-[#71717A] font-normal">({liveCount}/2 live)</span></h3>
          <p className="text-xs text-[#71717A] mt-0.5">Mini-themes shown as two big tiles on the home page. Refresh to preview matches, then publish (max 2). Unpublish or archive to rotate.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={runCategorize} disabled={catBusy} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-[#E5E5E5] hover:border-[#0A0A0A] transition-colors disabled:opacity-50" data-testid="categorize-btn"><RefreshCw className={`w-3.5 h-3.5 ${catBusy ? "animate-spin" : ""}`} /> Extract product types</button>
          <button onClick={() => setModal({})} className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#0A0A0A] text-white hover:bg-[#FF4FB4] transition-colors" data-testid="add-editorial-btn"><Plus className="w-3.5 h-3.5" /> New Editorial</button>
        </div>
      </div>
      {loading ? <p className="text-sm text-[#71717A]">Loading…</p> : items.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-[#E5E5E5]"><p className="text-sm text-[#71717A]">No editorials yet. Create one like "Co-Ord Sets" or "Statement Sleeves".</p></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(ed => (
            <div key={ed.id} className="bg-white border border-[#E5E5E5] overflow-hidden" data-testid={`admin-editorial-${ed.id}`}>
              <div className="aspect-[16/9] bg-[#1A1A1A] overflow-hidden relative">
                {ed.cover_image_resolved && <img src={ed.cover_image_resolved} alt={ed.name} className="w-full h-full object-cover" />}
                <span className={`absolute top-2 left-2 text-[10px] px-2 py-0.5 uppercase tracking-wide ${badge(ed.status)}`} data-testid={`editorial-status-${ed.id}`}>{ed.status}</span>
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between mb-1">
                  <h4 className="font-medium text-[#0A0A0A] text-sm">{ed.name}</h4>
                  <button onClick={() => setModal({ editorial: ed })} className="text-[#71717A] hover:text-[#0A0A0A]" data-testid={`edit-editorial-${ed.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                </div>
                <p className="text-xs text-[#71717A] mb-2">{ed.product_count} products · {(ed.keywords || []).length} keywords</p>
                <div className="flex flex-wrap gap-1.5">
                  <button onClick={() => action(ed.id, "refresh", "Matches refreshed")} disabled={busy === ed.id + "refresh"} className="text-[10px] px-2 py-1 border border-[#E5E5E5] hover:border-[#0A0A0A] flex items-center gap-1" data-testid={`refresh-editorial-${ed.id}`}><RefreshCw className={`w-3 h-3 ${busy === ed.id + "refresh" ? "animate-spin" : ""}`} /> Preview</button>
                  {ed.status === "live" ? (
                    <button onClick={() => action(ed.id, "unpublish", "Unpublished")} className="text-[10px] px-2 py-1 border border-[#E5E5E5] hover:border-[#0A0A0A]" data-testid={`unpublish-editorial-${ed.id}`}>Unpublish</button>
                  ) : (
                    <button onClick={() => action(ed.id, "publish", "Published to home")} disabled={liveCount >= 2} className="text-[10px] px-2 py-1 bg-[#0A0A0A] text-white hover:bg-[#FF4FB4] transition-colors disabled:opacity-40" data-testid={`publish-editorial-${ed.id}`}>Publish</button>
                  )}
                  {ed.status !== "archived" && <button onClick={() => action(ed.id, "archive", "Archived")} className="text-[10px] px-2 py-1 border border-[#E5E5E5] hover:border-[#0A0A0A] flex items-center gap-1" data-testid={`archive-editorial-${ed.id}`}><Archive className="w-3 h-3" /> Archive</button>}
                  <button onClick={() => setConfirmDel(ed)} className="text-[10px] px-2 py-1 border border-red-200 text-red-500 hover:bg-red-50" data-testid={`delete-editorial-${ed.id}`}><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {modal && <EditorialModal editorial={modal.editorial} onSave={save} onClose={() => setModal(null)} />}
      {preview && <EditorialPreviewModal preview={preview} onClose={() => setPreview(null)} onSetCover={setCover} onSaveSelection={saveSelection} />}
      {confirmDel && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] px-4" data-testid="editorial-delete-confirm">
          <div className="bg-white p-6 w-full max-w-sm">
            <h3 className="font-medium text-[#0A0A0A] mb-2">Delete "{confirmDel.name}"?</h3>
            <p className="text-sm text-[#71717A] mb-4">This can't be undone.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDel(null)} className="px-4 py-2 text-xs border border-[#E5E5E5]">Cancel</button>
              <button onClick={() => del(confirmDel.id)} className="px-4 py-2 text-xs bg-red-500 text-white hover:bg-red-600" data-testid="confirm-delete-editorial-btn">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== ADMIN: PERSPECTIVES ====================
const PerspectiveModal = ({ item, themes, onSave, onClose }) => {
  const [form, setForm] = useState({
    theme_id: item?.theme_id || (themes[0]?.id || ""),
    display_title: item?.display_title || "",
    description: item?.description || "",
    seo_description: item?.seo_description || "",
    cover_product_id: item?.cover_product_id || "",
    banner_image: item?.banner_image || "",
    visible: item?.visible ?? true,
    order: item?.order ?? 0,
  });
  const [prods, setProds] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  useEffect(() => {
    if (!form.theme_id) return;
    api.get(`/themes/${form.theme_id}/products`).then(r => setProds((r.data || []).slice(0, 24))).catch(() => setProds([]));
  }, [form.theme_id]);
  const submit = async () => { setSaving(true); await onSave(form); setSaving(false); };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] px-4" data-testid="perspective-modal">
      <div className="bg-white p-6 w-full max-w-lg max-h-[88vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-[#0A0A0A]">{item ? "Edit Perspective" : "New Perspective"}</h3>
          <button onClick={onClose} data-testid="perspective-modal-close"><XIcon className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className={editorialLabel}>Main theme</label>
            <select value={form.theme_id} onChange={e => set("theme_id", e.target.value)} className={editorialInput} data-testid="perspective-theme-select">
              {themes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label className={editorialLabel}>Display title (home)</label>
            <input value={form.display_title} onChange={e => set("display_title", e.target.value)} placeholder="e.g. Salt & Sun" className={editorialInput} data-testid="perspective-title-input" />
          </div>
          <div>
            <label className={editorialLabel}>Short description</label>
            <textarea value={form.description} onChange={e => set("description", e.target.value)} rows={2} placeholder="Slow days. Easy dressing. Pieces that travel with you." className={editorialInput} data-testid="perspective-desc-input" />
          </div>
          <div>
            <label className={editorialLabel}>SEO description (longer, shown on the perspective page)</label>
            <textarea value={form.seo_description} onChange={e => set("seo_description", e.target.value)} rows={3} placeholder="A richer paragraph shown on the perspective page and used for search engines." className={editorialInput} data-testid="perspective-seo-input" />
          </div>
          <div>
            <label className={editorialLabel}>Editorial banner image URL (shown on home — full-bleed with dissolve)</label>
            <input value={form.banner_image} onChange={e => set("banner_image", e.target.value)} placeholder="https://…  or  /persp-salt-sun.webp" className={editorialInput} data-testid="perspective-banner-input" />
            {form.banner_image && <div className="mt-2 h-24 w-full overflow-hidden rounded bg-[#F4F4F4]"><img src={form.banner_image} alt="" className="w-full h-full object-cover" /></div>}
          </div>
          <div>
            <label className={editorialLabel}>Cover — pick a product (fallback if no banner set)</label>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mt-1 max-h-40 overflow-y-auto">
              {prods.map(p => (
                <button type="button" key={p.id} onClick={() => set("cover_product_id", form.cover_product_id === p.id ? "" : p.id)} className={`aspect-[2/3] overflow-hidden rounded ${form.cover_product_id === p.id ? "ring-2 ring-[#FF4FB4]" : "ring-1 ring-[#E5E5E5]"}`} data-testid={`perspective-cover-${p.id}`}>
                  <img src={p.image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs text-[#0A0A0A]"><input type="checkbox" checked={form.visible} onChange={e => set("visible", e.target.checked)} data-testid="perspective-visible-toggle" /> Show on home page</label>
          <div>
            <label className={editorialLabel}>Order (lower shows first)</label>
            <input type="number" value={form.order} onChange={e => set("order", e.target.value)} className={editorialInput} data-testid="perspective-order-input" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-xs border border-[#E5E5E5]" data-testid="perspective-cancel-btn">Cancel</button>
          <button onClick={submit} disabled={saving || !form.display_title.trim() || !form.theme_id} className="px-4 py-2 text-xs bg-[#0A0A0A] text-white hover:bg-[#FF4FB4] transition-colors disabled:opacity-50" data-testid="perspective-save-btn">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
};

const REASON_LABELS = {
  uncategorized: "No category",
  low_confidence_department: "Unsure dept",
  low_confidence_category: "Unsure category",
  unassigned_theme: "No theme",
  department_category_conflict: "Dept/category clash",
};

const ConfBadge = ({ value }) => {
  if (value === undefined || value === null) return null;
  const color = value >= 0.7 ? "text-green-700 bg-green-50" : value >= 0.5 ? "text-amber-700 bg-amber-50" : "text-red-600 bg-red-50";
  return <span className={`text-[9px] px-1 py-0.5 rounded ${color}`}>{Math.round(value * 100)}%</span>;
};

const ReviewCard = ({ p, categories, crafts, departments, themes, onVerify }) => {
  const [cat, setCat] = useState(p.category && p.category !== "other" ? p.category : "");
  const [craft, setCraft] = useState(p.craft || "");
  const [dept, setDept] = useState(p.department || "fashion");
  const [theme, setTheme] = useState(p.assigned_theme && !["unassigned", "excluded"].includes(p.assigned_theme) ? p.assigned_theme : "");
  const [busy, setBusy] = useState(false);
  const am = p.attr_meta || {};
  const act = async (payload) => { setBusy(true); try { await onVerify(p.id, payload); } finally { setBusy(false); } };
  return (
    <div className="bg-white border border-[#E5E5E5] overflow-hidden flex flex-col" data-testid={`review-card-${p.id}`}>
      <div className="aspect-[3/4] bg-[#F4F4F5] overflow-hidden relative">
        {p.image_url && <img src={p.image_url} alt="" className="w-full h-full object-cover" />}
        <div className="absolute top-1.5 left-1.5 flex flex-wrap gap-1">
          {p.review_reasons.map(r => <span key={r} className="text-[9px] px-1.5 py-0.5 bg-[#0A0A0A]/80 text-white uppercase tracking-wide" data-testid={`review-reason-${r}`}>{REASON_LABELS[r] || r}</span>)}
        </div>
      </div>
      <div className="p-3 flex flex-col gap-2 flex-1">
        <div>
          <p className="text-xs font-medium text-[#0A0A0A] line-clamp-2" title={p.title}>{p.title}</p>
          <p className="text-[10px] text-[#71717A] mt-0.5">{p.brand_name} · ₹{p.price}</p>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          <label className="text-[9px] text-[#A1A1AA] flex items-center gap-1">Dept <ConfBadge value={am.department?.confidence} /></label>
          <label className="text-[9px] text-[#A1A1AA] flex items-center gap-1">Category <ConfBadge value={am.category?.confidence} /></label>
          <select value={dept} onChange={e => setDept(e.target.value)} className="border border-[#E5E5E5] px-1.5 py-1 text-[11px]" data-testid={`review-dept-${p.id}`}>
            {departments.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={cat} onChange={e => setCat(e.target.value)} className="border border-[#E5E5E5] px-1.5 py-1 text-[11px]" data-testid={`review-cat-${p.id}`}>
            <option value="">— category —</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={craft} onChange={e => setCraft(e.target.value)} className="border border-[#E5E5E5] px-1.5 py-1 text-[11px]" data-testid={`review-craft-${p.id}`}>
            <option value="">— craft —</option>
            {crafts.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={theme} onChange={e => setTheme(e.target.value)} className="border border-[#E5E5E5] px-1.5 py-1 text-[11px]" data-testid={`review-theme-${p.id}`}>
            <option value="">— theme —</option>
            {themes.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-auto pt-1">
          <button disabled={busy} onClick={() => act({ category: cat || null, craft: craft || null, department: dept, assigned_theme: theme || null })} className="flex-1 text-[10px] px-2 py-1.5 bg-[#0A0A0A] text-white hover:bg-[#FF4FB4] transition-colors disabled:opacity-50" data-testid={`review-save-${p.id}`}>Save & lock</button>
          <button disabled={busy} onClick={() => act({ approve_as_is: true })} className="text-[10px] px-2 py-1.5 border border-green-300 text-green-700 hover:bg-green-50 disabled:opacity-50" data-testid={`review-approve-${p.id}`}>Looks right</button>
          <button disabled={busy} onClick={() => act({ exclude: true })} className="text-[10px] px-2 py-1.5 border border-red-200 text-red-500 hover:bg-red-50 disabled:opacity-50" data-testid={`review-exclude-${p.id}`}>Exclude</button>
        </div>
      </div>
    </div>
  );
};

const ReviewAdmin = () => {
  const [summary, setSummary] = useState(null);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [brand, setBrand] = useState("");
  const [reason, setReason] = useState("");
  const [tax, setTax] = useState([]);
  const [themes, setThemes] = useState([]);
  const [vision, setVision] = useState({ running: false });

  const loadSummary = () => { api.get("/admin/review/summary").then(r => setSummary(r.data)).catch(() => {}); };
  const loadList = () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: "60" });
    if (brand) params.append("brand", brand);
    if (reason) params.append("reason", reason);
    api.get(`/admin/review?${params}`).then(r => { setProducts(r.data?.products || []); setTotal(r.data?.total || 0); }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { loadSummary(); api.get("/admin/taxonomy").then(r => setTax(r.data?.terms || [])).catch(() => {}); api.get("/themes").then(r => setThemes(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { loadList(); }, [brand, reason]);

  const categories = tax.filter(t => t.type === "category" && t.active).map(t => t.canonical);
  const crafts = tax.filter(t => t.type === "craft" && t.active).map(t => t.canonical);
  const departments = tax.filter(t => t.type === "department" && t.active).map(t => t.canonical);

  const verify = async (id, payload) => {
    try { await api.post(`/admin/review/${id}/verify`, payload); toast.success("Locked ✓"); setProducts(ps => ps.filter(p => p.id !== id)); setTotal(t => t - 1); loadSummary(); }
    catch { toast.error("Verify failed"); }
  };

  const runVision = async () => {
    try {
      setVision({ running: true, processed: 0, total: 0 });
      const params = new URLSearchParams({ limit: "20" });
      if (brand) params.append("brand", brand);
      await api.post(`/admin/vision-enrich?${params}`);
      toast.success("AI vision started on up to 20 vague products…");
      const poll = setInterval(async () => {
        try {
          const r = await api.get("/admin/vision-enrich/status");
          setVision(r.data);
          if (!r.data.running) { clearInterval(poll); toast.success(`AI vision done: ${r.data.updated} updated of ${r.data.total}`); loadSummary(); loadList(); }
        } catch { clearInterval(poll); setVision({ running: false }); }
      }, 3000);
    } catch (e) { setVision({ running: false }); toast.error(e.response?.data?.detail || "Vision start failed"); }
  };

  return (
    <div data-testid="admin-review">
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-[#0A0A0A]">Review Queue <span className="text-[#71717A] font-normal">({summary?.total ?? "…"} need review)</span></h3>
        <p className="text-xs text-[#71717A] mt-0.5">Fix low-confidence or conflicting products one by one. Every decision is locked forever — automated re-runs never overwrite it. Theme changes apply on the next Re-curate.</p>
      </div>

      {summary && (
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(summary.by_reason).map(([r, c]) => (
            <button key={r} onClick={() => setReason(reason === r ? "" : r)} className={`text-[11px] px-3 py-1.5 border transition-colors ${reason === r ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5] text-[#71717A] hover:border-[#0A0A0A]"}`} data-testid={`review-filter-${r}`}>
              {REASON_LABELS[r] || r} <span className="opacity-70">({c})</span>
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <select value={brand} onChange={e => setBrand(e.target.value)} className="border border-[#E5E5E5] px-3 py-1.5 text-xs" data-testid="review-brand-filter">
          <option value="">All labels ({summary?.total ?? 0})</option>
          {(summary?.by_brand || []).map(b => <option key={b.brand_name} value={b.brand_name}>{b.brand_name} ({b.count})</option>)}
        </select>
        {(brand || reason) && <button onClick={() => { setBrand(""); setReason(""); }} className="text-[11px] px-2 py-1.5 text-[#71717A] hover:text-[#0A0A0A] underline" data-testid="review-clear-filters">Clear filters</button>}
        <button onClick={runVision} disabled={vision.running} className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 border border-[#FF4FB4] text-[#FF4FB4] hover:bg-[#FF4FB4] hover:text-white transition-colors disabled:opacity-50" data-testid="review-vision-btn" title="Let AI read the photos of vague products and classify them">
          <Sparkles className="w-3.5 h-3.5" /> {vision.running ? `AI reading… ${vision.processed || 0}/${vision.total || 0}` : "AI-assist 20 vague"}
        </button>
        <span className="text-[11px] text-[#A1A1AA]">Showing {products.length} of {total}</span>
      </div>

      {loading ? <p className="text-sm text-[#71717A]">Loading…</p> : products.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-[#E5E5E5]"><p className="text-sm text-[#71717A]">Nothing to review here. 🎉</p></div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {products.map(p => <ReviewCard key={p.id} p={p} categories={categories} crafts={crafts} departments={departments} themes={themes} onVerify={verify} />)}
        </div>
      )}
    </div>
  );
};

const TAX_TYPES = [
  { id: "department", label: "Departments", hint: "Top-level class: fashion, jewellery, accessories, cosmetics…" },
  { id: "category", label: "Categories", hint: "Garment types (scoped to a department)" },
  { id: "craft", label: "Crafts", hint: "Technique: handloom, block print, hand embroidered…" },
];

const TaxonomyModal = ({ term, type, departments, onSave, onClose }) => {
  const [form, setForm] = useState({
    type: term?.type || type || "category",
    canonical: term?.canonical || "",
    label: term?.label || "",
    aliases: (term?.aliases || []).join(", "),
    parent: term?.parent || (( term?.type || type) === "category" ? "fashion" : ""),
    priority: term?.priority ?? 100,
    confidence: term?.confidence ?? 0.7,
    active: term?.active ?? true,
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = () => {
    if (!form.canonical.trim()) { toast.error("Canonical term is required"); return; }
    onSave({
      type: form.type,
      canonical: form.canonical.trim(),
      label: form.label.trim(),
      aliases: form.aliases.split(/[,\n]/).map(s => s.trim()).filter(Boolean),
      parent: form.type === "category" ? (form.parent || null) : null,
      priority: Number(form.priority) || 0,
      confidence: Math.max(0, Math.min(1, Number(form.confidence) || 0)),
      active: !!form.active,
    });
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] px-4" data-testid="taxonomy-modal">
      <div className="bg-white p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-[#0A0A0A]">{term ? "Edit term" : "New term"}</h3>
          <button onClick={onClose} className="text-[#71717A] hover:text-[#0A0A0A]" data-testid="taxonomy-modal-close"><XIcon className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-[#71717A] mb-1">Type</label>
            <select value={form.type} onChange={e => set("type", e.target.value)} disabled={!!term} className="w-full border border-[#E5E5E5] px-3 py-2 text-sm disabled:bg-[#FAFAFA]" data-testid="taxonomy-type-select">
              {TAX_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-[#71717A] mb-1">Canonical *</label>
              <input value={form.canonical} onChange={e => set("canonical", e.target.value)} placeholder="e.g. kalamkari" className="w-full border border-[#E5E5E5] px-3 py-2 text-sm" data-testid="taxonomy-canonical-input" />
            </div>
            <div>
              <label className="block text-xs text-[#71717A] mb-1">Display label</label>
              <input value={form.label} onChange={e => set("label", e.target.value)} placeholder="e.g. Kalamkari" className="w-full border border-[#E5E5E5] px-3 py-2 text-sm" data-testid="taxonomy-label-input" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-[#71717A] mb-1">Synonyms / keywords <span className="text-[#A1A1AA]">(comma or newline separated — these are what we match in titles)</span></label>
            <textarea value={form.aliases} onChange={e => set("aliases", e.target.value)} rows={3} placeholder="kalamkari, kalamkaari, hand-painted pen" className="w-full border border-[#E5E5E5] px-3 py-2 text-sm" data-testid="taxonomy-aliases-input" />
          </div>
          {form.type === "category" && (
            <div>
              <label className="block text-xs text-[#71717A] mb-1">Parent department</label>
              <select value={form.parent} onChange={e => set("parent", e.target.value)} className="w-full border border-[#E5E5E5] px-3 py-2 text-sm" data-testid="taxonomy-parent-select">
                {(departments.length ? departments : ["fashion"]).map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-[#71717A] mb-1">Priority <span className="text-[#A1A1AA]">(lower checked first)</span></label>
              <input type="number" value={form.priority} onChange={e => set("priority", e.target.value)} className="w-full border border-[#E5E5E5] px-3 py-2 text-sm" data-testid="taxonomy-priority-input" />
            </div>
            <div>
              <label className="block text-xs text-[#71717A] mb-1">Confidence <span className="text-[#A1A1AA]">(0–1)</span></label>
              <input type="number" step="0.05" min="0" max="1" value={form.confidence} onChange={e => set("confidence", e.target.value)} className="w-full border border-[#E5E5E5] px-3 py-2 text-sm" data-testid="taxonomy-confidence-input" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#0A0A0A]">
            <input type="checkbox" checked={form.active} onChange={e => set("active", e.target.checked)} data-testid="taxonomy-active-checkbox" /> Active
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-xs border border-[#E5E5E5]" data-testid="taxonomy-cancel-btn">Cancel</button>
          <button onClick={submit} className="px-4 py-2 text-xs bg-[#0A0A0A] text-white hover:bg-[#FF4FB4] transition-colors" data-testid="taxonomy-save-btn">Save</button>
        </div>
      </div>
    </div>
  );
};

const TaxonomyAdmin = () => {
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("department");
  const [modal, setModal] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  const load = () => { setLoading(true); api.get("/admin/taxonomy").then(r => setTerms(r.data?.terms || [])).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const departments = terms.filter(t => t.type === "department").map(t => t.canonical);
  const save = async (form) => {
    try {
      if (modal?.term) { await api.put(`/admin/taxonomy/${modal.term.id}`, form); toast.success("Term updated"); }
      else { await api.post("/admin/taxonomy", form); toast.success("Term added"); }
      setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => { try { await api.delete(`/admin/taxonomy/${id}`); toast.success("Deleted"); setConfirmDel(null); load(); } catch { toast.error("Delete failed"); } };
  const toggleActive = async (t) => { try { await api.put(`/admin/taxonomy/${t.id}`, { ...t, active: !t.active }); load(); } catch { toast.error("Failed"); } };
  const reresolve = async () => {
    setResolving(true);
    try {
      const r = await api.post("/admin/taxonomy/reresolve");
      toast.success(`Re-classified ${r.data?.category?.categorized || 0} products against the current taxonomy`);
    } catch { toast.error("Re-resolve failed"); }
    finally { setResolving(false); }
  };
  const backfillEthnic = async () => {
    setBackfilling(true);
    try {
      const r = await api.post("/admin/backfill-ethnic");
      toast.success(`Backfilled is-ethnic on ${r.data?.updated || 0} products (${r.data?.curated || 0} curated)`);
    } catch { toast.error("Backfill failed"); }
    finally { setBackfilling(false); }
  };

  const filtered = terms.filter(t => t.type === type).sort((a, b) => (a.priority || 0) - (b.priority || 0));
  const activeType = TAX_TYPES.find(t => t.id === type);

  return (
    <div data-testid="admin-taxonomy">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold text-[#0A0A0A]">Classification Taxonomy</h3>
          <p className="text-xs text-[#71717A] mt-0.5">The vocabulary that classifies every product — edit it here, no code needed. Add a synonym, then "Re-classify".</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={backfillEthnic} disabled={backfilling} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors disabled:opacity-50" data-testid="taxonomy-backfill-ethnic-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${backfilling ? "animate-spin" : ""}`} /> {backfilling ? "Backfilling…" : "Backfill is-ethnic"}
          </button>
          <button onClick={reresolve} disabled={resolving} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors disabled:opacity-50" data-testid="taxonomy-reresolve-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${resolving ? "animate-spin" : ""}`} /> {resolving ? "Re-classifying…" : "Re-classify products"}
          </button>
          <button onClick={() => setModal({})} className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#0A0A0A] text-white hover:bg-[#FF4FB4] transition-colors" data-testid="add-taxonomy-btn"><Plus className="w-3.5 h-3.5" /> New term</button>
        </div>
      </div>

      <div className="flex gap-1 mb-4 border-b border-[#E5E5E5]">
        {TAX_TYPES.map(t => (
          <button key={t.id} onClick={() => setType(t.id)} className={`px-3 py-2 text-xs border-b-2 transition-colors ${type === t.id ? "border-[#0A0A0A] text-[#0A0A0A] font-medium" : "border-transparent text-[#71717A] hover:text-[#0A0A0A]"}`} data-testid={`taxonomy-type-tab-${t.id}`}>
            {t.label} <span className="text-[#A1A1AA]">({terms.filter(x => x.type === t.id).length})</span>
          </button>
        ))}
      </div>
      <p className="text-xs text-[#A1A1AA] mb-3">{activeType?.hint}</p>

      {loading ? <p className="text-sm text-[#71717A]">Loading…</p> : (
        <div className="bg-white border border-[#E5E5E5] overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-[#FAFAFA] text-[10px] uppercase tracking-wide text-[#71717A] font-medium">
            <div className="col-span-2">Term</div>
            <div className="col-span-5">Synonyms</div>
            {type === "category" && <div className="col-span-1">Parent</div>}
            <div className="col-span-1">Prio</div>
            <div className="col-span-1">Conf</div>
            <div className={`${type === "category" ? "col-span-2" : "col-span-3"} text-right`}>Actions</div>
          </div>
          {filtered.map(t => (
            <div key={t.id} className={`grid grid-cols-12 gap-2 px-4 py-2.5 border-t border-[#F0F0F0] text-xs items-center ${!t.active ? "opacity-50" : ""}`} data-testid={`taxonomy-row-${t.id}`}>
              <div className="col-span-2 font-medium text-[#0A0A0A]">{t.label || t.canonical}</div>
              <div className="col-span-5 text-[#71717A] truncate" title={t.aliases.join(", ")}>{t.aliases.slice(0, 6).join(", ")}{t.aliases.length > 6 ? `  +${t.aliases.length - 6}` : ""}</div>
              {type === "category" && <div className="col-span-1 text-[#71717A]">{t.parent || "—"}</div>}
              <div className="col-span-1 text-[#71717A]">{t.priority}</div>
              <div className="col-span-1 text-[#71717A]">{t.confidence}</div>
              <div className={`${type === "category" ? "col-span-2" : "col-span-3"} flex items-center justify-end gap-1.5`}>
                <button onClick={() => toggleActive(t)} className="text-[10px] px-2 py-1 border border-[#E5E5E5] hover:border-[#0A0A0A]" data-testid={`taxonomy-toggle-${t.id}`}>{t.active ? "On" : "Off"}</button>
                <button onClick={() => setModal({ term: t })} className="text-[#71717A] hover:text-[#0A0A0A]" data-testid={`taxonomy-edit-${t.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => setConfirmDel(t)} className="text-red-400 hover:text-red-600" data-testid={`taxonomy-delete-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="px-4 py-10 text-center text-sm text-[#71717A]">No {activeType?.label.toLowerCase()} yet.</div>}
        </div>
      )}

      {modal && <TaxonomyModal term={modal.term} type={type} departments={departments} onSave={save} onClose={() => setModal(null)} />}
      {confirmDel && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] px-4" data-testid="taxonomy-delete-confirm">
          <div className="bg-white p-6 w-full max-w-sm">
            <h3 className="font-medium text-[#0A0A0A] mb-2">Delete "{confirmDel.label || confirmDel.canonical}"?</h3>
            <p className="text-xs text-[#71717A]">Products already classified keep their value until the next re-classify.</p>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setConfirmDel(null)} className="px-4 py-2 text-xs border border-[#E5E5E5]">Cancel</button>
              <button onClick={() => del(confirmDel.id)} className="px-4 py-2 text-xs bg-red-500 text-white hover:bg-red-600" data-testid="confirm-delete-taxonomy-btn">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const PerspectivesAdmin = () => {
  const [items, setItems] = useState([]);
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const load = () => { setLoading(true); api.get("/admin/perspectives").then(r => setItems(r.data?.items || [])).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); api.get("/themes").then(r => setThemes(r.data || [])).catch(() => {}); }, []);
  const visibleCount = items.filter(i => i.visible).length;
  const save = async (form) => {
    try {
      if (modal?.item) { await api.put(`/admin/perspectives/${modal.item.id}`, form); toast.success("Perspective updated"); }
      else { await api.post("/admin/perspectives", form); toast.success("Perspective created"); }
      setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const toggle = async (it) => { try { await api.put(`/admin/perspectives/${it.id}`, { visible: !it.visible }); load(); } catch { toast.error("Failed"); } };
  const del = async (id) => { try { await api.delete(`/admin/perspectives/${id}`); toast.success("Deleted"); setConfirmDel(null); load(); } catch { toast.error("Delete failed"); } };
  return (
    <div data-testid="admin-perspectives">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold text-[#0A0A0A]">This Week's Perspectives <span className="text-[#71717A] font-normal">({visibleCount} shown · max 2 on home)</span></h3>
          <p className="text-xs text-[#71717A] mt-0.5">Publish any main theme to the home page with a custom title, description & cover. First 2 marked "shown" (by order) appear.</p>
        </div>
        <button onClick={() => setModal({})} className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#0A0A0A] text-white hover:bg-[#FF4FB4] transition-colors" data-testid="add-perspective-btn"><Plus className="w-3.5 h-3.5" /> New Perspective</button>
      </div>
      {loading ? <p className="text-sm text-[#71717A]">Loading…</p> : items.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-[#E5E5E5]"><p className="text-sm text-[#71717A]">No perspectives yet. Publish a main theme to the home page.</p></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(p => (
            <div key={p.id} className="bg-white border border-[#E5E5E5] overflow-hidden" data-testid={`admin-perspective-${p.id}`}>
              <div className="aspect-[16/9] bg-[#1A1A1A] overflow-hidden relative">
                {p.cover_image_resolved && <img src={p.cover_image_resolved} alt="" className="w-full h-full object-cover" />}
                <span className={`absolute top-2 left-2 text-[10px] px-2 py-0.5 uppercase tracking-wide ${p.visible ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`} data-testid={`perspective-status-${p.id}`}>{p.visible ? "shown" : "hidden"}</span>
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between mb-1">
                  <h4 className="font-medium text-[#0A0A0A] text-sm">{p.display_title}</h4>
                  <button onClick={() => setModal({ item: p })} className="text-[#71717A] hover:text-[#0A0A0A]" data-testid={`edit-perspective-${p.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                </div>
                <p className="text-xs text-[#71717A] mb-2">Theme: {p.theme_name} · order {p.order}</p>
                <div className="flex flex-wrap gap-1.5">
                  <button onClick={() => toggle(p)} className="text-[10px] px-2 py-1 border border-[#E5E5E5] hover:border-[#0A0A0A]" data-testid={`toggle-perspective-${p.id}`}>{p.visible ? "Hide" : "Show"}</button>
                  <button onClick={() => setConfirmDel(p)} className="text-[10px] px-2 py-1 border border-red-200 text-red-500 hover:bg-red-50" data-testid={`delete-perspective-${p.id}`}><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {modal && <PerspectiveModal item={modal.item} themes={themes} onSave={save} onClose={() => setModal(null)} />}
      {confirmDel && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] px-4" data-testid="perspective-delete-confirm">
          <div className="bg-white p-6 w-full max-w-sm">
            <h3 className="font-medium text-[#0A0A0A] mb-2">Delete "{confirmDel.display_title}"?</h3>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setConfirmDel(null)} className="px-4 py-2 text-xs border border-[#E5E5E5]">Cancel</button>
              <button onClick={() => del(confirmDel.id)} className="px-4 py-2 text-xs bg-red-500 text-white hover:bg-red-600" data-testid="confirm-delete-perspective-btn">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [dashboard, setDashboard] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [viewAnalytics, setViewAnalytics] = useState(null);
  const [visitorAnalytics, setVisitorAnalytics] = useState(null);
  const [affiliateAnalytics, setAffiliateAnalytics] = useState(null);
  const [analyticsPeriod, setAnalyticsPeriod] = useState("7d");
  const [searchAnalytics, setSearchAnalytics] = useState(null);
  const [trending, setTrending] = useState(null);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [activeJobs, setActiveJobs] = useState([]);  // running brand pipelines
  const [recurateConfirm, setRecurateConfirm] = useState(false);
  const [adminToast, setAdminToast] = useState(null); // { msg, kind: 'info'|'error'|'success' }
  const [scheduler, setScheduler] = useState([]);
  const [runningTasks, setRunningTasks] = useState({});
  const schedulerPollRef = useRef(null);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editBrand, setEditBrand] = useState(null);
  const [editTheme, setEditTheme] = useState(null);
  const [newBrand, setNewBrand] = useState(false);
  const [newTheme, setNewTheme] = useState(false);
  const [themeDetails, setThemeDetails] = useState({});
  // Subscribers + Newsletter state
  const [subscribersData, setSubscribersData] = useState(null);
  const [newsletters, setNewsletters] = useState([]);
  const [newsletterDraft, setNewsletterDraft] = useState({ id: null, subject: "", body_html: "" });
  const [newsletterPreview, setNewsletterPreview] = useState(null);
  const [searchEnabled, setSearchEnabled] = useState(true);
  const [searchViewAllCount, setSearchViewAllCount] = useState(50);

  useEffect(() => {
    if (!user || user.role !== "admin") { navigate("/login"); return; }
    loadData();
  }, [user, navigate]);

  useEffect(() => () => { if (schedulerPollRef.current) clearTimeout(schedulerPollRef.current); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [d, a, s, u] = await Promise.all([
        api.get("/admin/dashboard"),
        api.get("/admin/analytics"),
        api.get("/admin/scheduler"),
        api.get("/admin/users")
      ]);
      setDashboard(d.data);
      setAnalytics(a.data);
      setScheduler(s.data);
      setAllUsers(u.data.users);
      api.get("/search/settings").then(r => {
        setSearchEnabled(!!r.data?.enabled);
        setSearchViewAllCount(r.data?.view_all_count ?? 50);
      }).catch(() => {});
      // Load trending + cached insights (non-blocking)
      api.get("/admin/analytics/trending").then(r => setTrending(r.data)).catch(() => {});
      api.get("/admin/analytics/insights").then(r => setInsights(r.data)).catch(() => {});
      // Theme details — fetch in parallel, don't block UI
      const themes = d.data?.theme_stats || [];
      Promise.all(themes.map(t => api.get(`/themes/${t.id}`).then(r => [t.id, r.data]).catch(() => null)))
        .then(pairs => {
          const details = {};
          pairs.filter(Boolean).forEach(([id, data]) => { details[id] = data; });
          setThemeDetails(details);
        });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // Refetch view + visitor analytics whenever the period filter changes
  useEffect(() => {
    if (!user || user.role !== "admin") return;
    api.get(`/admin/analytics/views?period=${analyticsPeriod}`).then(r => setViewAnalytics(r.data)).catch(() => {});
    api.get(`/admin/analytics/visitors?period=${analyticsPeriod}`).then(r => setVisitorAnalytics(r.data)).catch(() => {});
    api.get(`/admin/analytics/affiliate?period=${analyticsPeriod}`).then(r => setAffiliateAnalytics(r.data)).catch(() => {});
    api.get(`/admin/analytics/search?period=${analyticsPeriod}`).then(r => setSearchAnalytics(r.data)).catch(() => {});
  }, [analyticsPeriod, user]);

  // Lazy-load subscribers + newsletters when their tabs are opened
  useEffect(() => {
    if (!user || user.role !== "admin") return;
    if (tab === "subscribers") {
      api.get("/admin/subscribers").then(r => setSubscribersData(r.data)).catch(() => {});
    } else if (tab === "newsletter") {
      api.get("/admin/newsletters").then(r => setNewsletters(r.data?.items || [])).catch(() => {});
      if (!subscribersData) api.get("/admin/subscribers").then(r => setSubscribersData(r.data)).catch(() => {});
    }
  }, [tab, user]);

  const refreshSubscribers = () => api.get("/admin/subscribers").then(r => setSubscribersData(r.data)).catch(() => {});
  const refreshNewsletters = () => api.get("/admin/newsletters").then(r => setNewsletters(r.data?.items || [])).catch(() => {});

  const handleSaveNewsletter = async () => {
    const { id, subject, body_html } = newsletterDraft;
    if (!subject.trim() || !body_html.trim()) {
      showToast("Subject and body are required.", "error");
      return;
    }
    try {
      if (id) {
        await api.put(`/admin/newsletters/${id}`, { subject, body_html });
        showToast("Draft updated.", "success");
      } else {
        await api.post("/admin/newsletters", { subject, body_html });
        showToast("Draft saved.", "success");
      }
      setNewsletterDraft({ id: null, subject: "", body_html: "" });
      refreshNewsletters();
    } catch (e) {
      showToast(formatError(e?.response?.data?.detail) || "Save failed.", "error");
    }
  };

  const handleSendNewsletter = async (nl) => {
    if (!window.confirm(`Send "${nl.subject}" to all active subscribers?\n\nNote: email sending is currently MOCKED — it will be marked sent but no emails will actually go out until Resend is connected.`)) return;
    try {
      const r = await api.post(`/admin/newsletters/${nl.id}/send`);
      showToast(`Newsletter marked sent to ${r.data.recipient_count} subscribers (MOCKED).`, "success");
      refreshNewsletters();
    } catch (e) {
      showToast(formatError(e?.response?.data?.detail) || "Send failed.", "error");
    }
  };

  const handleDeleteNewsletter = async (id) => {
    if (!window.confirm("Delete this draft?")) return;
    try {
      await api.delete(`/admin/newsletters/${id}`);
      showToast("Draft deleted.", "success");
      if (newsletterDraft.id === id) setNewsletterDraft({ id: null, subject: "", body_html: "" });
      refreshNewsletters();
    } catch (e) {
      showToast(formatError(e?.response?.data?.detail) || "Delete failed.", "error");
    }
  };

  const handleExportSubscribers = async () => {
    try {
      const r = await api.get("/admin/subscribers/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "edyte-subscribers.csv"; document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (e) {
      showToast("Export failed.", "error");
    }
  };

  // Poll active processing jobs every 4s while admin is on the dashboard.
  // This is the single source of truth for the "is something tagging right now?" banner.
  useEffect(() => {
    if (!user || user.role !== "admin") return;
    let alive = true;
    const tick = () => {
      api.get("/admin/process/active").then(r => {
        if (!alive) return;
        const jobs = r.data?.active || [];
        setActiveJobs(prev => {
          // If a job we were tracking just finished, refresh the dashboard once.
          const prevIds = new Set(prev.map(j => j.brand_id));
          const currIds = new Set(jobs.map(j => j.brand_id));
          const finished = [...prevIds].filter(id => !currIds.has(id));
          if (finished.length > 0) refreshDashboard();
          return jobs;
        });
      }).catch(() => {});
    };
    tick();
    const id = setInterval(tick, 4000);
    return () => { alive = false; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const showToast = (msg, kind = "info", ms = 4000) => {
    setAdminToast({ msg, kind });
    setTimeout(() => setAdminToast(null), ms);
  };

  const cancelBrandJob = async (brandId, brandName) => {
    try {
      await api.post(`/admin/brands/${brandId}/cancel`);
      showToast(`Cancellation requested for "${brandName}". It will stop after the current product.`, "info");
      // Mark cancel locally so the UI shows "Stopping…" immediately.
      setActiveJobs(prev => prev.map(j => j.brand_id === brandId ? { ...j, cancel_requested: true } : j));
    } catch (e) {
      showToast(`Cancel failed: ${e?.response?.data?.detail || e.message}`, "error");
    }
  };

  const generateInsights = async () => {
    setInsightsLoading(true);
    try {
      const r = await api.post("/admin/analytics/insights/generate");
      setInsights(r.data);
    } catch (e) {
      setInsights({ summary: "Failed to generate insights.", bullets: [String(e?.response?.data?.detail || e.message || e)], generated_at: null });
    } finally {
      setInsightsLoading(false);
    }
  };

  // Light refresh — only refetches the dashboard (brands/themes table). Used after toggles, edits, deletes.
  const refreshDashboard = async () => {
    try {
      const d = await api.get("/admin/dashboard");
      setDashboard(d.data);
    } catch (e) { /* ignore */ }
  };

  const toggleFeatured = async (brandId, current) => {
    await api.put(`/admin/brands/${brandId}`, { featured: !current });
    refreshDashboard();
  };

  const [confirmDelete, setConfirmDelete] = useState(null); // brand object pending deletion
  const [adminError, setAdminError] = useState(null);

  const toggleVisibility = async (brandId, current) => {
    await api.put(`/admin/brands/${brandId}`, { visible: !current });
    refreshDashboard();
  };

  const toggleSearchEnabled = async () => {
    const next = !searchEnabled;
    setSearchEnabled(next);
    try { await api.put("/admin/search/settings", { enabled: next, view_all_count: searchViewAllCount }); } catch { setSearchEnabled(!next); }
  };

  const saveSearchCounts = async () => {
    try {
      await api.put("/admin/search/settings", { enabled: searchEnabled, view_all_count: searchViewAllCount });
      toast.success("Search display settings saved");
    } catch { toast.error("Failed to save"); }
  };

  const deleteBrand = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/admin/brands/${confirmDelete.id}`);
      setConfirmDelete(null);
      refreshDashboard();
    } catch (e) {
      setAdminError(`Delete failed: ${e?.response?.data?.detail || e.message}`);
      setConfirmDelete(null);
    }
  };

  const saveBrand = async (data) => {
    // Optimistically close modal so admin doesn't feel stuck
    setEditBrand(null);
    setNewBrand(false);
    try {
      if (data.id) {
        await api.put(`/admin/brands/${data.id}`, data);
      } else {
        const res = await api.post("/admin/brands", data);
        if (res.data?.id && data.scrape_now) {
          // Fire-and-forget. AI tagging is OFF unless admin explicitly checked it.
          const skip = data.tag_with_ai ? "false" : "true";
          api.post(`/admin/brands/${res.data.id}/process?skip_tagging=${skip}`).catch(() => {});
        }
      }
      refreshDashboard();
    } catch (e) {
      setAdminError(`Save failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const processBrand = async (brandId) => {
    const brand = dashboard?.brand_stats?.find(b => b.id === brandId);
    const brandLabel = brand?.name || "brand";
    // Guard against duplicate clicks: if the brand is already in the active list, just nudge the user.
    if (activeJobs.some(j => j.brand_id === brandId)) {
      showToast(`"${brandLabel}" is already being processed.`, "info");
      return;
    }
    try {
      await api.post(`/admin/brands/${brandId}/process`);
      showToast(`Processing started for "${brandLabel}".`, "success");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e.message;
      if (status === 409) {
        showToast(detail, "info");  // Already running — friendly notice, not an error
      } else {
        setAdminError(`Failed to start processing: ${detail}`);
      }
      return;
    }
    refreshDashboard();
  };

  const retagBrand = async (brandId) => {
    const brand = dashboard?.brand_stats?.find(b => b.id === brandId);
    const brandLabel = brand?.name || "brand";
    if (activeJobs.some(j => j.brand_id === brandId)) {
      showToast(`"${brandLabel}" is already being processed.`, "info");
      return;
    }
    if (!window.confirm(`Re-tag ALL products of "${brandLabel}"? This resets their tags and re-runs the AI tagger (uses AI credits). Use this after tagging-logic changes.`)) {
      return;
    }
    try {
      const res = await api.post(`/admin/brands/${brandId}/retag`);
      showToast(`Re-tagging "${brandLabel}" (${res.data?.reset_count ?? 0} products).`, "success");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e.message;
      if (status === 409) {
        showToast(detail, "info");
      } else {
        setAdminError(`Failed to start re-tag: ${detail}`);
      }
      return;
    }
    refreshDashboard();
  };

  const saveTheme = async (data) => {
    setEditTheme(null);
    setNewTheme(false);
    try {
      if (data.id) {
        await api.put(`/admin/themes/${data.id}`, data);
      } else {
        await api.post("/admin/themes", data);
      }
      refreshDashboard();
    } catch (e) {
      setAdminError(`Theme save failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const toggleThemeHome = async (t, detail) => {
    const next = !(detail?.home_featured);
    try {
      await api.put(`/admin/themes/${t.id}`, { home_featured: next });
      setThemeDetails(prev => ({ ...prev, [t.id]: { ...(prev[t.id] || {}), home_featured: next } }));
    } catch { setAdminError("Failed to update On Home"); }
  };

  const recurateThemes = async () => {
    setRecurateConfirm(false);
    try {
      await api.post("/admin/curate");
      showToast("Re-curation started in background. Theme counts will refresh in 15–30s.", "success", 6000);
      setTimeout(loadData, 15000);
      setTimeout(loadData, 45000);
    } catch (e) {
      showToast(`Re-curation failed: ${e?.response?.data?.detail || e.message}`, "error");
    }
  };

  const formatSchedulerResult = (task) => {
    const r = task?.last_result;
    if (!r || typeof r !== "object") return "";
    return Object.entries(r)
      .filter(([, v]) => typeof v === "number")
      .map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`)
      .join(" · ");
  };

  const triggerTask = async (taskId) => {
    const prev = scheduler.find(t => t.task_id === taskId);
    const prevRun = prev?.last_run || null;
    const taskName = prev?.name || "Task";
    try {
      await api.post(`/admin/scheduler/${taskId}/run`);
    } catch (e) {
      showToast(`Failed to start ${taskName}: ${e?.response?.data?.detail || e.message}`, "error");
      return;
    }
    setRunningTasks(prev => ({ ...prev, [taskId]: true }));
    showToast(`${taskName} started — running in the background…`, "info");

    let polls = 0;
    const maxPolls = 180; // ~12 min safety cap at 4s
    const tick = async () => {
      polls += 1;
      let data = null;
      try { const r = await api.get("/admin/scheduler"); data = r.data; setScheduler(r.data); } catch { /* keep polling */ }
      const task = data?.find(t => t.task_id === taskId);
      // Done = a NEW run (different started_at) that's no longer 'running'.
      const isNewRun = task && task.last_run && task.last_run !== prevRun;
      if (isNewRun && task.last_status && task.last_status !== "running") {
        setRunningTasks(p => { const n = { ...p }; delete n[taskId]; return n; });
        const summary = formatSchedulerResult(task);
        showToast(
          `${task.name} ${task.last_status === "completed" ? "✓ complete" : "failed"}${summary ? " — " + summary : ""}`,
          task.last_status === "completed" ? "success" : "error"
        );
        return;
      }
      if (polls >= maxPolls) {
        setRunningTasks(p => { const n = { ...p }; delete n[taskId]; return n; });
        return;
      }
      schedulerPollRef.current = setTimeout(tick, 4000);
    };
    schedulerPollRef.current = setTimeout(tick, 3000);
  };

  if (loading) return <div className="min-h-screen pt-24 bg-[#FAFAFA] flex items-center justify-center"><p className="text-[#71717A]">Loading dashboard...</p></div>;

  const stats = dashboard?.stats || {};

  const tabs = [
    { id: "overview", label: "Overview", icon: <BarChart3 className="w-4 h-4" /> },
    { id: "brands", label: "Labels", icon: <Tag className="w-4 h-4" /> },
    { id: "themes", label: "Themes", icon: <Settings className="w-4 h-4" /> },
    { id: "taxonomy", label: "Taxonomy", icon: <LayoutGrid className="w-4 h-4" /> },
    { id: "review", label: "Review", icon: <Eye className="w-4 h-4" /> },
    { id: "editorials", label: "Editorials", icon: <Newspaper className="w-4 h-4" /> },
    { id: "perspectives", label: "Perspectives", icon: <Compass className="w-4 h-4" /> },
    { id: "scheduler", label: "Scheduler", icon: <Clock className="w-4 h-4" /> },
    { id: "users", label: "Users", icon: <Users className="w-4 h-4" /> },
    { id: "analytics", label: "Analytics", icon: <BarChart3 className="w-4 h-4" /> },
    { id: "subscribers", label: "Subscribers", icon: <Mail className="w-4 h-4" /> },
    { id: "newsletter", label: "Newsletter", icon: <Send className="w-4 h-4" /> },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA] pt-16 sm:pt-20" data-testid="admin-dashboard">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 py-6">
        <h1 className="font-heading text-2xl sm:text-3xl tracking-tight text-[#0A0A0A] mb-6">Admin Dashboard</h1>

        {/* Active processing jobs banner — visible across all tabs so admin always knows what's running */}
        {activeJobs.length > 0 && (
          <div className="mb-5 border border-[#FF4FB4] bg-[#FFF5FB] p-4 space-y-3" data-testid="active-jobs-banner">
            <div className="flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-[#FF4FB4] font-semibold">
              <span className="inline-block w-2 h-2 rounded-full bg-[#FF4FB4] animate-pulse" />
              {activeJobs.length} {activeJobs.length === 1 ? 'job' : 'jobs'} running
            </div>
            <ul className="space-y-2">
              {activeJobs.map(j => {
                const stageLabel = {
                  scraping: "Scraping",
                  scraping_fallback: "Scraping (Playwright)",
                  tagging: "Tagging with AI",
                  curating: "Curating themes",
                  skipped_tagging: "Curating",
                }[j.status] || j.status;
                const tagged = j.tagged_count || 0;
                const total = j.total_to_tag || 0;
                const pct = total > 0 ? Math.round((tagged / total) * 100) : 0;
                return (
                  <li key={j.brand_id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-white border border-[#E5E5E5] px-3 py-2.5">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-sm text-[#0A0A0A]">
                        <span className="font-medium truncate">{j.brand_name}</span>
                        <span className="text-[10px] tracking-[0.15em] uppercase text-[#71717A]">{stageLabel}</span>
                        {j.cancel_requested && <span className="text-[10px] tracking-[0.15em] uppercase text-orange-600">Stopping…</span>}
                      </div>
                      {j.status === "tagging" && total > 0 && (
                        <div className="mt-1.5">
                          <div className="flex items-center justify-between text-[10px] text-[#71717A] mb-1">
                            <span>{tagged} / {total} products</span>
                            <span>{pct}%</span>
                          </div>
                          <div className="h-1 bg-[#F5F5F5] overflow-hidden">
                            <div className="h-full bg-[#FF4FB4] transition-all duration-500" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => cancelBrandJob(j.brand_id, j.brand_name)}
                      disabled={j.cancel_requested}
                      className="px-3 py-1.5 text-[10px] tracking-[0.18em] uppercase border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                      data-testid={`cancel-job-${j.brand_id}`}
                    >
                      {j.cancel_requested ? "Stopping…" : "Stop"}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Toast — short-lived feedback for non-blocking actions */}
        {adminToast && (
          <div
            className={`mb-4 px-4 py-2.5 text-sm border ${
              adminToast.kind === "error" ? "bg-red-50 border-red-200 text-red-800" :
              adminToast.kind === "success" ? "bg-green-50 border-green-200 text-green-800" :
              "bg-blue-50 border-blue-200 text-blue-800"
            }`}
            data-testid="admin-toast"
          >
            {adminToast.msg}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 overflow-x-auto scrollbar-hide mb-6 border-b border-[#E5E5E5] pb-px">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-1.5 px-3 sm:px-4 py-2.5 text-xs sm:text-sm whitespace-nowrap transition-colors border-b-2 ${tab === t.id ? "border-[#0A0A0A] text-[#0A0A0A] font-medium" : "border-transparent text-[#71717A] hover:text-[#0A0A0A]"}`} data-testid={`admin-tab-${t.id}`}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* Overview */}
        {tab === "overview" && (
          <div className="space-y-6">
            <div className="bg-white border border-[#E5E5E5] p-4 sm:p-5" data-testid="ai-search-toggle-card">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-[#0A0A0A]">AI Fashion Discovery Search</h3>
                  <p className="text-xs text-[#71717A] mt-0.5">Shows the "What are you looking for?" search panel on the Home page, between the banner and Featured Story. Clicking Search opens a dedicated results page.</p>
                </div>
                <button onClick={toggleSearchEnabled} className="text-[#71717A] hover:text-[#0A0A0A] flex-shrink-0" data-testid="ai-search-toggle-switch" title={searchEnabled ? "Enabled — click to disable" : "Disabled — click to enable"}>
                  {searchEnabled ? <ToggleRight className="w-8 h-8 text-[#00C853]" /> : <ToggleLeft className="w-8 h-8 text-red-400" />}
                </button>
              </div>
              <div className="flex items-center gap-4 mt-3 pt-3 border-t border-[#F0F0F0]">
                <label className="flex items-center gap-2 text-xs text-[#71717A]">
                  Max results shown on search page
                  <input type="number" min="2" value={searchViewAllCount} onChange={(e) => setSearchViewAllCount(e.target.value)} className="w-16 border border-[#E5E5E5] px-2 py-1 text-xs text-[#0A0A0A]" data-testid="ai-search-view-all-count-input" />
                </label>
                <button onClick={saveSearchCounts} className="px-3 py-1.5 text-xs bg-[#0A0A0A] text-white hover:bg-[#FF4FB4] transition-colors" data-testid="ai-search-save-counts-btn">Save</button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
              {[
                { label: "Users", value: stats.total_users },
                { label: "Brands", value: stats.total_brands },
                { label: "Products", value: stats.total_products },
                { label: "Tagged", value: stats.tagged_products },
                { label: "Curated", value: stats.curated_products },
              ].map((s, i) => (
                <div key={i} className="bg-white border border-[#E5E5E5] p-4 sm:p-5" data-testid={`stat-${s.label.toLowerCase()}`}>
                  <p className="text-[10px] sm:text-xs text-[#71717A] uppercase tracking-wider mb-1">{s.label}</p>
                  <p className="text-xl sm:text-2xl font-semibold text-[#0A0A0A]">{s.value?.toLocaleString()}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white border border-[#E5E5E5] p-4 sm:p-6">
                <h3 className="text-sm font-semibold text-[#0A0A0A] mb-4">Products by Brand</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={analytics?.products_by_brand || []}>
                    <XAxis dataKey="brand" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={60} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#FF4FB4" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="bg-white border border-[#E5E5E5] p-4 sm:p-6">
                <h3 className="text-sm font-semibold text-[#0A0A0A] mb-4">Products by Theme</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={analytics?.products_by_theme || []}>
                    <XAxis dataKey="theme" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#0A0A0A" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* Brands/Labels */}
        {tab === "brands" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-[#0A0A0A]">Fashion Labels ({dashboard?.brand_stats?.length})</h3>
              <button onClick={() => setNewBrand(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#0A0A0A] text-white hover:bg-[#FF4FB4] transition-colors" data-testid="add-brand-btn"><Plus className="w-3.5 h-3.5" /> Add Label</button>
            </div>
            <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-[#E5E5E5] bg-[#FAFAFA]">
                  <th className="text-left px-4 py-3 text-xs text-[#71717A] uppercase tracking-wider">Brand</th>
                  <th className="text-left px-4 py-3 text-xs text-[#71717A] uppercase tracking-wider">Products</th>
                  <th className="text-left px-4 py-3 text-xs text-[#71717A] uppercase tracking-wider">Tagged</th>
                  <th className="text-left px-4 py-3 text-xs text-[#71717A] uppercase tracking-wider">Status</th>
                  <th className="text-center px-4 py-3 text-xs text-[#71717A] uppercase tracking-wider">Visible</th>
                  <th className="text-center px-4 py-3 text-xs text-[#71717A] uppercase tracking-wider" title="Featured labels appear on the home page (top 4 by product count)">Featured</th>
                  <th className="text-right px-4 py-3 text-xs text-[#71717A] uppercase tracking-wider">Actions</th>
                </tr></thead>
                <tbody>
                  {dashboard?.brand_stats?.map(b => (
                    <tr key={b.id} className="border-b border-[#E5E5E5] last:border-0" data-testid={`admin-brand-${b.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium">{b.name}</div>
                        <div className="text-[10px] text-[#A1A1AA] truncate max-w-[200px]">{b.website_url}</div>
                      </td>
                      <td className="px-4 py-3 text-[#71717A]">{b.total_products}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs ${b.tagged_products > 0 ? 'text-green-700' : 'text-[#71717A]'}`}>{b.tagged_products}</span>
                        {b.total_products > 0 && b.tagged_products < b.total_products && (
                          <span className="text-[10px] text-orange-500 ml-1">({b.total_products - b.tagged_products} pending)</span>
                        )}
                      </td>
                      <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 ${b.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{b.status}</span>
                        {b.process_status && (() => {
                          const ps = b.process_status;
                          const r = b.process_result || {};
                          const tip = ps === 'failed'
                            ? (r.note || r.error || 'Failed — check logs')
                            : ps === 'completed'
                              ? `Scraped ${r.scraped || 0} · Tagged ${r.tagged || 0} · Curated ${r.curated || 0}`
                              : `In progress: ${ps}`;
                          const cls = ps === 'failed' ? 'bg-red-50 text-red-700'
                                     : ps === 'completed' ? (r && r.scraped === 0 && r.tagged === 0 ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700')
                                     : 'bg-yellow-50 text-yellow-700';
                          const label = ps === 'failed' ? 'Failed' : ps === 'completed' ? 'Done' : ps;
                          return (
                            <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 ${cls}`} title={tip} data-testid={`process-status-${b.id}`}>{label}</span>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={() => toggleVisibility(b.id, b.visible !== false)} className="text-[#71717A] hover:text-[#0A0A0A]" data-testid={`toggle-visible-${b.id}`} title={b.visible !== false ? "Visible — click to hide" : "Hidden — click to show"}>
                          {b.visible !== false ? <ToggleRight className="w-6 h-6 text-[#00C853]" /> : <ToggleLeft className="w-6 h-6 text-red-400" />}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={() => toggleFeatured(b.id, b.featured)} className="text-[#71717A] hover:text-[#0A0A0A]" data-testid={`toggle-featured-${b.id}`}>
                          {b.featured ? <ToggleRight className="w-6 h-6 text-[#00C853]" /> : <ToggleLeft className="w-6 h-6" />}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {(() => {
                            const job = activeJobs.find(j => j.brand_id === b.id);
                            if (job) {
                              const tagged = job.tagged_count || 0;
                              const total = job.total_to_tag || 0;
                              const stage = job.status === 'tagging' && total > 0 ? `Tagging ${tagged}/${total}` : (job.status === 'scraping' || job.status === 'scraping_fallback') ? 'Scraping' : job.status === 'curating' ? 'Curating' : 'Processing';
                              return (
                                <span className="text-[10px] px-2.5 py-1 bg-[#FFF5FB] border border-[#FF4FB4] text-[#FF4FB4] whitespace-nowrap inline-flex items-center gap-1" data-testid={`process-active-${b.id}`}>
                                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#FF4FB4] animate-pulse" />
                                  {job.cancel_requested ? "Stopping…" : stage}
                                </span>
                              );
                            }
                            return (
                              <button onClick={() => processBrand(b.id)} className="text-[10px] px-2.5 py-1 bg-[#0A0A0A] text-white hover:bg-[#FF4FB4] transition-colors whitespace-nowrap" data-testid={`process-brand-${b.id}`} title="Scrape → Tag → Curate">
                                <Play className="w-3 h-3 inline mr-1" />Process
                              </button>
                            );
                          })()}
                          {!activeJobs.find(j => j.brand_id === b.id) && (
                            <button onClick={() => retagBrand(b.id)} className="text-[10px] px-2.5 py-1 border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors whitespace-nowrap" data-testid={`retag-brand-${b.id}`} title="Reset tags & re-run AI tagger for this brand (uses AI credits)">
                              <RefreshCw className="w-3 h-3 inline mr-1" />Re-tag
                            </button>
                          )}
                          <button onClick={() => setEditBrand(b)} className="text-[#71717A] hover:text-[#0A0A0A]" data-testid={`edit-brand-${b.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                          <button onClick={() => setConfirmDelete(b)} className="text-[#71717A] hover:text-red-600" data-testid={`delete-brand-${b.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(editBrand || newBrand) && <BrandModal brand={editBrand} themes={dashboard?.theme_stats || []} onSave={saveBrand} onClose={() => { setEditBrand(null); setNewBrand(false); }} />}
            {confirmDelete && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4" data-testid="confirm-delete-modal">
                <div className="bg-white p-6 w-full max-w-sm">
                  <h3 className="font-medium text-[#0A0A0A] mb-2">Delete this label?</h3>
                  <p className="text-sm text-[#71717A] mb-5">This will permanently remove <span className="font-semibold text-[#0A0A0A]">{confirmDelete.name}</span> and all of its {confirmDelete.total_products || 0} products.</p>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 text-xs border border-[#E5E5E5] hover:border-[#0A0A0A]" data-testid="confirm-delete-cancel">Cancel</button>
                    <button onClick={deleteBrand} className="px-4 py-2 text-xs bg-red-600 text-white hover:bg-red-700 transition-colors" data-testid="confirm-delete-confirm">Delete</button>
                  </div>
                </div>
              </div>
            )}
            {adminError && (
              <div className="fixed bottom-6 right-6 bg-red-600 text-white text-sm px-4 py-3 shadow-lg flex items-center gap-3 z-50" data-testid="admin-error-toast">
                <span>{adminError}</span>
                <button onClick={() => setAdminError(null)} className="opacity-80 hover:opacity-100"><XIcon className="w-4 h-4" /></button>
              </div>
            )}
          </div>
        )}

        {/* Themes */}
        {tab === "themes" && (
          <div>
            <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
              <h3 className="text-sm font-semibold text-[#0A0A0A]">Themes ({dashboard?.theme_stats?.length})</h3>
              <div className="flex items-center gap-2">
                <button onClick={() => setRecurateConfirm(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors" data-testid="recurate-themes-btn" title="Re-runs theme assignment for ALL tagged products. Free — no scrape, no AI cost.">
                  <Play className="w-3.5 h-3.5" /> Re-curate Themes
                </button>
                <button onClick={() => setNewTheme(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#0A0A0A] text-white hover:bg-[#FF4FB4] transition-colors" data-testid="add-theme-btn"><Plus className="w-3.5 h-3.5" /> Add Theme</button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {dashboard?.theme_stats?.map(t => {
                const detail = themeDetails[t.id];
                const rulesCount = detail?.rules?.length || 0;
                const excludeCount = detail?.exclude?.length || 0;
                return (
                  <div key={t.id} className="bg-white border border-[#E5E5E5] p-4 sm:p-5" data-testid={`admin-theme-${t.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-[#0A0A0A] mb-1">{t.name}</h4>
                        <p className="text-xs text-[#71717A]">{t.curated_count} curated products</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => toggleThemeHome(t, detail)} className={`text-[10px] px-2 py-1 border transition-colors ${detail?.home_featured ? "bg-[#FF4FB4] text-white border-[#FF4FB4]" : "border-[#E5E5E5] text-[#71717A] hover:border-[#0A0A0A]"}`} data-testid={`theme-home-toggle-${t.id}`} title="Show this theme in the 'World of Edyte' collage on the home page">
                          {detail?.home_featured ? "★ On Home" : "☆ On Home"}
                        </button>
                        <button onClick={() => setEditTheme({ ...t, ...(detail || {}) })} className="text-[#71717A] hover:text-[#0A0A0A]" data-testid={`edit-theme-${t.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <span className="text-[10px] px-2 py-0.5 bg-blue-50 text-blue-700">{rulesCount} rules</span>
                      <span className="text-[10px] px-2 py-0.5 bg-orange-50 text-orange-700">{excludeCount} exclusions</span>
                      <span className="text-[10px] px-2 py-0.5 bg-green-50 text-green-700">min score: {detail?.min_score || 0}</span>
                    </div>
                    {detail?.rules?.length > 0 && (
                      <div className="mt-3 text-[10px] text-[#71717A] space-y-1">
                        {detail.rules.slice(0, 3).map((r, i) => (
                          <div key={i} className="flex items-center gap-1">
                            <span className="font-medium uppercase">{r.type}</span>: {r.values ? r.values.join(", ") : `${r.key}=${String(r.value)}`} <span className="text-[#A1A1AA]">(w:{r.weight})</span>
                          </div>
                        ))}
                        {detail.rules.length > 3 && <div className="text-[#A1A1AA]">+{detail.rules.length - 3} more...</div>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {(editTheme || newTheme) && <ThemeModal theme={editTheme} onSave={saveTheme} onClose={() => { setEditTheme(null); setNewTheme(false); }} />}
          </div>
        )}

        {tab === "editorials" && <EditorialsAdmin />}

        {tab === "taxonomy" && <TaxonomyAdmin />}

        {tab === "review" && <ReviewAdmin />}

        {tab === "perspectives" && <PerspectivesAdmin />}

        {/* Scheduler */}
        {tab === "scheduler" && (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-[#0A0A0A]">Scraping Scheduler</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {scheduler.map(task => {
                const isRunning = !!runningTasks[task.task_id] || task.last_status === "running";
                const resultSummary = formatSchedulerResult(task);
                return (
                <div key={task.task_id} className="bg-white border border-[#E5E5E5] p-4 sm:p-5" data-testid={`scheduler-${task.task_id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-medium text-sm text-[#0A0A0A]">{task.name}</h4>
                      <span className={`text-[10px] px-2 py-0.5 mt-1 inline-block ${task.frequency === 'daily' ? 'bg-blue-50 text-blue-700' : task.frequency === 'every_3_days' ? 'bg-purple-50 text-purple-700' : 'bg-orange-50 text-orange-700'}`}>{task.frequency.replace('_', ' ')}</span>
                    </div>
                    {isRunning ? (
                      <span className="flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] sm:text-xs bg-[#FFF5FB] border border-[#FF4FB4] text-[#FF4FB4]" data-testid={`running-${task.task_id}`}>
                        <RefreshCw className="w-3 h-3 animate-spin" /> Running…
                      </span>
                    ) : (
                      <button onClick={() => triggerTask(task.task_id)} className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] sm:text-xs bg-[#0A0A0A] text-white hover:bg-[#FF4FB4] transition-colors" data-testid={`run-${task.task_id}`}><Play className="w-3 h-3" /> Run</button>
                    )}
                  </div>
                  <p className="text-xs text-[#71717A] mb-2">{task.description}</p>
                  <div className="text-[10px] text-[#71717A] space-y-0.5">
                    <p>Last: {task.last_run ? new Date(task.last_run).toLocaleString() : 'Never'}</p>
                    <p>Status: <span className={isRunning ? 'text-[#FF4FB4]' : task.last_status === 'completed' ? 'text-green-600' : task.last_status === 'failed' ? 'text-red-600' : 'text-[#71717A]'} data-testid={`status-${task.task_id}`}>{isRunning ? 'running' : task.last_status}</span></p>
                    {!isRunning && resultSummary && <p data-testid={`result-${task.task_id}`}>Result: {resultSummary}</p>}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Users */}
        {tab === "users" && (
          <div>
            <h3 className="text-sm font-semibold text-[#0A0A0A] mb-4">Registered Users ({allUsers.length})</h3>
            <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-[#E5E5E5] bg-[#FAFAFA]">
                  <th className="text-left px-4 py-3 text-xs text-[#71717A] uppercase tracking-wider">Name</th>
                  <th className="text-left px-4 py-3 text-xs text-[#71717A] uppercase tracking-wider">Email</th>
                  <th className="text-left px-4 py-3 text-xs text-[#71717A] uppercase tracking-wider">Role</th>
                  <th className="text-left px-4 py-3 text-xs text-[#71717A] uppercase tracking-wider">Joined</th>
                </tr></thead>
                <tbody>
                  {allUsers.map(u => (
                    <tr key={u.id} className="border-b border-[#E5E5E5] last:border-0">
                      <td className="px-4 py-3 font-medium">{u.name || '-'}</td>
                      <td className="px-4 py-3 text-[#71717A]">{u.email}</td>
                      <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 ${u.role === 'admin' ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'}`}>{u.role}</span></td>
                      <td className="px-4 py-3 text-[#71717A] text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Analytics — combined product analytics + user views */}
        {tab === "analytics" && (
          <div className="space-y-6">
            {/* Period filter + Generate insights */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="analytics-controls">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] tracking-[0.18em] uppercase text-[#71717A] mr-1">Period</span>
                {[
                  { v: "today", label: "Today" },
                  { v: "7d", label: "Last 7 days" },
                  { v: "30d", label: "Last 30 days" },
                  { v: "all", label: "All time" },
                ].map(opt => (
                  <button
                    key={opt.v}
                    onClick={() => setAnalyticsPeriod(opt.v)}
                    className={`px-3 py-1.5 text-[11px] tracking-wide border transition-all ${analyticsPeriod === opt.v ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]' : 'bg-white text-[#0A0A0A] border-[#E5E5E5] hover:border-[#0A0A0A]'}`}
                    data-testid={`analytics-period-${opt.v}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={async () => {
                    const phrase = window.prompt('This will permanently delete ALL analytics events and AI insights. Type RESET to confirm:');
                    if (phrase !== 'RESET') return;
                    try {
                      const { data } = await api.post('/admin/analytics/reset', { confirm: 'RESET' });
                      alert(`Analytics cleared. Removed ${data.events_deleted} events and ${data.insights_deleted} insight snapshots.`);
                      window.location.reload();
                    } catch (e) {
                      alert('Reset failed: ' + (e?.response?.data?.detail || e.message));
                    }
                  }}
                  className="inline-flex items-center justify-center gap-2 px-3 py-1.5 text-[11px] tracking-[0.15em] uppercase border border-[#E5E5E5] text-[#71717A] hover:bg-[#FEE2E2] hover:text-[#B91C1C] hover:border-[#FCA5A5] transition-colors"
                  data-testid="reset-analytics-btn"
                  title="Permanently delete all analytics events and AI insights"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Reset
                </button>
                <button
                  onClick={generateInsights}
                  disabled={insightsLoading}
                  className="inline-flex items-center justify-center gap-2 px-3 py-1.5 text-[11px] tracking-[0.15em] uppercase bg-[#FF4FB4] text-white hover:bg-[#0A0A0A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="generate-insights-btn"
                >
                  {insightsLoading ? "Generating…" : (insights?.generated_at ? "Refresh Insights" : "Generate Insights")}
                </button>
              </div>
            </div>

            {/* Tiny Intelligence Insights */}
            <div className="bg-gradient-to-br from-[#FFF5FB] to-white border border-[#FF4FB4]/30 p-4 sm:p-5" data-testid="insights-panel">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-[#FF4FB4] font-semibold">Tiny Intelligence Insights</p>
                  <h3 className="text-sm font-semibold text-[#0A0A0A] mt-0.5">AI-generated observations</h3>
                </div>
                {insights?.generated_at && (
                  <span className="text-[10px] text-[#71717A] whitespace-nowrap">Updated {new Date(insights.generated_at).toLocaleString()}</span>
                )}
              </div>
              {insights?.summary || insights?.bullets?.length ? (
                <>
                  {insights.summary && <p className="text-sm text-[#0A0A0A] mb-2" data-testid="insights-summary">{insights.summary}</p>}
                  {insights.bullets?.length > 0 && (
                    <ul className="space-y-1.5 mt-2" data-testid="insights-bullets">
                      {insights.bullets.map((b, i) => (
                        <li key={i} className="text-sm text-[#3F3F46] flex gap-2"><span className="text-[#FF4FB4] mt-0.5">✦</span><span>{b}</span></li>
                      ))}
                    </ul>
                  )}
                </>
              ) : (
                <p className="text-xs text-[#71717A]">No insights yet. Click "Generate Insights" to get an AI summary of your latest activity.</p>
              )}
            </div>

            {/* AI Fashion Discovery Search Analytics */}
            <div className="bg-white border border-[#E5E5E5] p-4 sm:p-5" data-testid="search-analytics-section">
              <h3 className="text-sm font-semibold text-[#0A0A0A] mb-3">AI Fashion Discovery — Search Analytics</h3>
              {searchAnalytics ? (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    <div className="border border-[#F0F0F0] p-3" data-testid="search-stat-total">
                      <p className="text-[10px] tracking-[0.15em] uppercase text-[#71717A]">Total Searches</p>
                      <p className="text-xl font-semibold text-[#0A0A0A] mt-1">{searchAnalytics.total_searches}</p>
                    </div>
                    <div className="border border-[#F0F0F0] p-3" data-testid="search-stat-zero-rate">
                      <p className="text-[10px] tracking-[0.15em] uppercase text-[#71717A]">Zero-Result Rate</p>
                      <p className="text-xl font-semibold text-[#0A0A0A] mt-1">{searchAnalytics.zero_result_rate}%</p>
                    </div>
                    <div className="border border-[#F0F0F0] p-3" data-testid="search-stat-rule">
                      <p className="text-[10px] tracking-[0.15em] uppercase text-[#71717A]">Resolved by Rules</p>
                      <p className="text-xl font-semibold text-[#0A0A0A] mt-1">{searchAnalytics.source_split?.rule || 0}</p>
                    </div>
                    <div className="border border-[#F0F0F0] p-3" data-testid="search-stat-ai">
                      <p className="text-[10px] tracking-[0.15em] uppercase text-[#71717A]">Resolved by AI</p>
                      <p className="text-xl font-semibold text-[#FF4FB4] mt-1">{searchAnalytics.source_split?.ai || 0}</p>
                    </div>
                  </div>
                  {searchAnalytics.top_queries?.length > 0 ? (
                    <table className="w-full text-sm" data-testid="search-top-queries-table">
                      <thead>
                        <tr className="text-left text-[10px] tracking-[0.15em] uppercase text-[#71717A] border-b border-[#F0F0F0]">
                          <th className="pb-2">Search Query</th>
                          <th className="pb-2 text-right">Times Searched</th>
                          <th className="pb-2 text-right">Avg Results</th>
                        </tr>
                      </thead>
                      <tbody>
                        {searchAnalytics.top_queries.map((row, i) => (
                          <tr key={i} className="border-b border-[#F5F5F5] last:border-0">
                            <td className="py-2 text-[#0A0A0A]">{row.query}</td>
                            <td className="py-2 text-right tabular-nums">{row.count}</td>
                            <td className={`py-2 text-right tabular-nums ${row.avg_results === 0 ? 'text-[#EF4444]' : 'text-[#71717A]'}`}>{row.avg_results}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-xs text-[#71717A]">No searches yet in this period.</p>
                  )}
                </>
              ) : (
                <p className="text-xs text-[#71717A]">Loading…</p>
              )}
            </div>

            {/* Trending This Week */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="trending-section">
              <div className="bg-white border border-[#E5E5E5] p-4 sm:p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-[#0A0A0A]">Trending Labels This Week</h3>
                  <span className="text-[10px] tracking-[0.15em] uppercase text-[#71717A]">vs prior 7d</span>
                </div>
                {trending?.labels?.length > 0 ? (
                  <ol className="space-y-2" data-testid="trending-labels">
                    {trending.labels.map((row, i) => (
                      <li key={row.name} className="flex items-center justify-between text-sm border-b border-[#F5F5F5] last:border-0 pb-1.5">
                        <span className="flex items-center gap-2 truncate"><span className="text-[#71717A] tabular-nums w-5">{i + 1}.</span><span className="text-[#0A0A0A] truncate">{row.name}</span></span>
                        <span className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-[#0A0A0A] font-semibold tabular-nums">{row.count}</span>
                          <span className={`text-[10px] tracking-wide uppercase tabular-nums ${row.trend === 'up' ? 'text-[#00C853]' : row.trend === 'down' ? 'text-[#EF4444]' : row.trend === 'new' ? 'text-[#FF4FB4]' : 'text-[#71717A]'}`}>
                            {row.trend === 'new' ? 'NEW' : row.trend === 'flat' ? '—' : `${row.trend_pct > 0 ? '+' : ''}${row.trend_pct}%`}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ol>
                ) : <p className="text-xs text-[#71717A] py-6 text-center">No label activity this week yet</p>}
              </div>
              <div className="bg-white border border-[#E5E5E5] p-4 sm:p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-[#0A0A0A]">Trending Themes This Week</h3>
                  <span className="text-[10px] tracking-[0.15em] uppercase text-[#71717A]">vs prior 7d</span>
                </div>
                {trending?.themes?.length > 0 ? (
                  <ol className="space-y-2" data-testid="trending-themes">
                    {trending.themes.map((row, i) => (
                      <li key={row.name} className="flex items-center justify-between text-sm border-b border-[#F5F5F5] last:border-0 pb-1.5">
                        <span className="flex items-center gap-2 truncate"><span className="text-[#71717A] tabular-nums w-5">{i + 1}.</span><span className="text-[#0A0A0A] truncate">{row.name}</span></span>
                        <span className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-[#0A0A0A] font-semibold tabular-nums">{row.count}</span>
                          <span className={`text-[10px] tracking-wide uppercase tabular-nums ${row.trend === 'up' ? 'text-[#00C853]' : row.trend === 'down' ? 'text-[#EF4444]' : row.trend === 'new' ? 'text-[#FF4FB4]' : 'text-[#71717A]'}`}>
                            {row.trend === 'new' ? 'NEW' : row.trend === 'flat' ? '—' : `${row.trend_pct > 0 ? '+' : ''}${row.trend_pct}%`}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ol>
                ) : <p className="text-xs text-[#71717A] py-6 text-center">No theme activity this week yet</p>}
              </div>
            </div>

            {/* Repeat Customer / Visitor Analytics */}
            <div data-testid="repeat-customer-section">
              <h3 className="text-sm font-semibold text-[#0A0A0A] mb-3">Audience &amp; Repeat Customers</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
                <div className="bg-white border border-[#E5E5E5] p-3 sm:p-4">
                  <p className="text-[10px] tracking-[0.15em] uppercase text-[#71717A]">Total Visitors</p>
                  <p className="text-xl sm:text-2xl font-semibold text-[#0A0A0A] mt-1" data-testid="stat-total-visitors">{visitorAnalytics?.total_visitors ?? 0}</p>
                </div>
                <div className="bg-white border border-[#E5E5E5] p-3 sm:p-4">
                  <p className="text-[10px] tracking-[0.15em] uppercase text-[#71717A]">Repeat Visitors</p>
                  <p className="text-xl sm:text-2xl font-semibold text-[#FF4FB4] mt-1" data-testid="stat-repeat-visitors">{visitorAnalytics?.repeat_visitors ?? 0}</p>
                  <p className="text-[10px] text-[#71717A] mt-0.5">{visitorAnalytics?.repeat_percentage ?? 0}% of total</p>
                </div>
                <div className="bg-white border border-[#E5E5E5] p-3 sm:p-4">
                  <p className="text-[10px] tracking-[0.15em] uppercase text-[#71717A]">One-time Visitors</p>
                  <p className="text-xl sm:text-2xl font-semibold text-[#0A0A0A] mt-1">{visitorAnalytics?.one_timers ?? 0}</p>
                </div>
                <div className="bg-white border border-[#E5E5E5] p-3 sm:p-4">
                  <p className="text-[10px] tracking-[0.15em] uppercase text-[#71717A]">Active 24h</p>
                  <p className="text-xl sm:text-2xl font-semibold text-[#00C853] mt-1">{visitorAnalytics?.active_24h ?? 0}</p>
                </div>
                <div className="bg-white border border-[#E5E5E5] p-3 sm:p-4">
                  <p className="text-[10px] tracking-[0.15em] uppercase text-[#71717A]">Active 7d</p>
                  <p className="text-xl sm:text-2xl font-semibold text-[#0A0A0A] mt-1">{visitorAnalytics?.active_7d ?? 0}</p>
                </div>
                <div className="bg-white border border-[#E5E5E5] p-3 sm:p-4">
                  <p className="text-[10px] tracking-[0.15em] uppercase text-[#71717A]">Loyal (5+)</p>
                  <p className="text-xl sm:text-2xl font-semibold text-[#6366F1] mt-1">{visitorAnalytics?.high_loyalty ?? 0}</p>
                </div>
              </div>
              {visitorAnalytics?.top_repeats?.length > 0 && (
                <div className="bg-white border border-[#E5E5E5] p-4 sm:p-5">
                  <p className="text-xs font-semibold text-[#0A0A0A] mb-3 uppercase tracking-wider">Top Returning Visitors</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="repeat-visitors-table">
                      <thead>
                        <tr className="text-[#71717A] border-b border-[#E5E5E5]">
                          <th className="text-left py-2 font-medium">Visitor</th>
                          <th className="text-right py-2 font-medium">Visits</th>
                          <th className="text-right py-2 font-medium hidden sm:table-cell">First Seen</th>
                          <th className="text-right py-2 font-medium">Last Seen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visitorAnalytics.top_repeats.map((v, i) => (
                          <tr key={i} className="border-b border-[#F5F5F5]">
                            <td className="py-2 text-[#0A0A0A]">{v.label}</td>
                            <td className="py-2 text-right font-semibold text-[#FF4FB4]">{v.visit_count}</td>
                            <td className="py-2 text-right text-[#71717A] hidden sm:table-cell">{v.first_visit ? new Date(v.first_visit).toLocaleDateString() : '—'}</td>
                            <td className="py-2 text-right text-[#71717A]">{v.last_visit ? new Date(v.last_visit).toLocaleDateString() : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* User View Analytics */}
            <div className="bg-white border border-[#E5E5E5] p-4 sm:p-5">
              <p className="text-xs text-[#71717A]">Total tracked events: <span className="font-semibold text-[#0A0A0A]">{viewAnalytics?.total_events || 0}</span></p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="bg-white border border-[#E5E5E5] p-4 sm:p-6">
                <h3 className="text-sm font-semibold text-[#0A0A0A] mb-4">Most Discovered Labels</h3>
                {viewAnalytics?.brand_views?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={viewAnalytics.brand_views} layout="vertical">
                      <XAxis type="number" tick={{ fontSize: 10 }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={90} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#FF4FB4" radius={[0, 2, 2, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p className="text-xs text-[#71717A] py-8 text-center">No brand views tracked yet</p>}
              </div>
              <div className="bg-white border border-[#E5E5E5] p-4 sm:p-6">
                <h3 className="text-sm font-semibold text-[#0A0A0A] mb-4">Theme Driving Discovery</h3>
                {viewAnalytics?.theme_views?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={viewAnalytics.theme_views} layout="vertical">
                      <XAxis type="number" tick={{ fontSize: 10 }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={90} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#0A0A0A" radius={[0, 2, 2, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p className="text-xs text-[#71717A] py-8 text-center">No theme views tracked yet</p>}
              </div>
              <div className="bg-white border border-[#E5E5E5] p-4 sm:p-6">
                <h3 className="text-sm font-semibold text-[#0A0A0A] mb-4">Most Clicked Products</h3>
                {viewAnalytics?.product_clicks?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={viewAnalytics.product_clicks} layout="vertical">
                      <XAxis type="number" tick={{ fontSize: 10 }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={90} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#6366F1" radius={[0, 2, 2, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p className="text-xs text-[#71717A] py-8 text-center">No product clicks tracked yet</p>}
              </div>
            </div>
            {/* Affiliate / Outbound Click Performance */}
            <div className="bg-white border border-[#E5E5E5] p-4 sm:p-5" data-testid="affiliate-analytics-section">
              <div className="flex items-baseline justify-between flex-wrap gap-2">
                <h3 className="text-sm font-semibold text-[#0A0A0A]">Affiliate Performance <span className="text-[#71717A] font-normal">— outbound clicks (UTM-tagged)</span></h3>
                <p className="text-xs text-[#71717A]">Total outbound clicks: <span className="font-semibold text-[#FF4FB4]" data-testid="affiliate-total-clicks">{affiliateAnalytics?.total_clicks || 0}</span></p>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="bg-white border border-[#E5E5E5] p-4 sm:p-6">
                <h3 className="text-sm font-semibold text-[#0A0A0A] mb-4">Top Labels by Clicks</h3>
                {affiliateAnalytics?.top_labels?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={affiliateAnalytics.top_labels} layout="vertical">
                      <XAxis type="number" tick={{ fontSize: 10 }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={90} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#FF4FB4" radius={[0, 2, 2, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p className="text-xs text-[#71717A] py-8 text-center">No outbound clicks yet</p>}
              </div>
              <div className="bg-white border border-[#E5E5E5] p-4 sm:p-6">
                <h3 className="text-sm font-semibold text-[#0A0A0A] mb-4">Top Themes by Clicks</h3>
                {affiliateAnalytics?.top_themes?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={affiliateAnalytics.top_themes} layout="vertical">
                      <XAxis type="number" tick={{ fontSize: 10 }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={90} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#0A0A0A" radius={[0, 2, 2, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p className="text-xs text-[#71717A] py-8 text-center">No outbound clicks yet</p>}
              </div>
              <div className="bg-white border border-[#E5E5E5] p-4 sm:p-6">
                <h3 className="text-sm font-semibold text-[#0A0A0A] mb-4">Traffic Source</h3>
                {affiliateAnalytics?.sources?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie data={affiliateAnalytics.sources} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name }) => name}>
                        {affiliateAnalytics.sources.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <p className="text-xs text-[#71717A] py-8 text-center">No outbound clicks yet</p>}
              </div>
            </div>
            {affiliateAnalytics?.breakdown?.length > 0 && (
              <div className="bg-white border border-[#E5E5E5] p-4 sm:p-6" data-testid="affiliate-breakdown-table">
                <h3 className="text-sm font-semibold text-[#0A0A0A] mb-4">Label × Theme × Source</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-[#E5E5E5] text-[#71717A] text-left">
                        <th className="py-2 font-medium">Label</th>
                        <th className="py-2 font-medium">Theme</th>
                        <th className="py-2 font-medium">Source</th>
                        <th className="py-2 font-medium text-right">Clicks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {affiliateAnalytics.breakdown.slice(0, 30).map((r, i) => (
                        <tr key={i} className="border-b border-[#F5F5F5]">
                          <td className="py-2 text-[#0A0A0A]">{r.label}</td>
                          <td className="py-2 text-[#52525B] capitalize">{(r.theme || '—').replace(/-/g, ' ')}</td>
                          <td className="py-2 text-[#52525B] capitalize">{r.source}</td>
                          <td className="py-2 text-right font-semibold text-[#FF4FB4]">{r.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <ShareLinkGenerator />
            {/* Product Analytics */}
            {analytics && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-white border border-[#E5E5E5] p-4 sm:p-6">
                  <h3 className="text-sm font-semibold text-[#0A0A0A] mb-4">Vibe Distribution</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie data={analytics.vibe_distribution?.slice(0, 8)} dataKey="count" nameKey="vibe" cx="50%" cy="50%" outerRadius={80} label={({ vibe }) => vibe}>
                        {analytics.vibe_distribution?.slice(0, 8).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="bg-white border border-[#E5E5E5] p-4 sm:p-6">
                  <h3 className="text-sm font-semibold text-[#0A0A0A] mb-4">Price Distribution</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={analytics.price_distribution}>
                      <XAxis dataKey="range" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#0A0A0A" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="bg-white border border-[#E5E5E5] p-4 sm:p-6">
                  <h3 className="text-sm font-semibold text-[#0A0A0A] mb-4">Quality Distribution</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie data={analytics.quality_distribution} dataKey="count" nameKey="quality" cx="50%" cy="50%" outerRadius={80} label>
                        {analytics.quality_distribution?.map((_, i) => <Cell key={i} fill={["#00C853", "#F59E0B", "#EF4444"][i]} />)}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="bg-white border border-[#E5E5E5] p-4 sm:p-6">
                  <h3 className="text-sm font-semibold text-[#0A0A0A] mb-4">Products by Brand (Tagged)</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={analytics.products_by_brand} layout="vertical">
                      <XAxis type="number" tick={{ fontSize: 10 }} />
                      <YAxis dataKey="brand" type="category" tick={{ fontSize: 10 }} width={100} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#FF4FB4" radius={[0, 2, 2, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Subscribers */}
        {tab === "subscribers" && (
          <div className="space-y-5" data-testid="admin-subscribers">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              <div className="bg-white border border-[#E5E5E5] p-4">
                <p className="text-[10px] text-[#71717A] uppercase tracking-wider mb-1">Total</p>
                <p className="text-2xl font-semibold text-[#0A0A0A]" data-testid="subs-total">{subscribersData?.total || 0}</p>
              </div>
              <div className="bg-white border border-[#E5E5E5] p-4">
                <p className="text-[10px] text-[#71717A] uppercase tracking-wider mb-1">Active</p>
                <p className="text-2xl font-semibold text-[#15803D]" data-testid="subs-active">{subscribersData?.active || 0}</p>
              </div>
              <div className="bg-white border border-[#E5E5E5] p-4">
                <p className="text-[10px] text-[#71717A] uppercase tracking-wider mb-1">From Footer</p>
                <p className="text-2xl font-semibold text-[#0A0A0A]">{subscribersData?.by_source?.footer || 0}</p>
              </div>
              <div className="bg-white border border-[#E5E5E5] p-4">
                <p className="text-[10px] text-[#71717A] uppercase tracking-wider mb-1">From Popup</p>
                <p className="text-2xl font-semibold text-[#0A0A0A]">{subscribersData?.by_source?.popup || 0}</p>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-[#0A0A0A]">All subscribers</h3>
              <button
                onClick={handleExportSubscribers}
                disabled={!subscribersData?.items?.length}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-[10px] tracking-[0.15em] uppercase bg-[#0A0A0A] text-white hover:bg-[#FF4FB4] transition-colors disabled:opacity-40"
                data-testid="subs-export-btn"
              >
                <Download className="w-3.5 h-3.5" /> Export CSV
              </button>
            </div>
            <div className="bg-white border border-[#E5E5E5] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-wider text-[#71717A]">
                    <tr>
                      <th className="text-left px-4 py-3">Email</th>
                      <th className="text-left px-4 py-3">Source</th>
                      <th className="text-left px-4 py-3">Status</th>
                      <th className="text-left px-4 py-3">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(subscribersData?.items || []).map((s) => (
                      <tr key={s.id} className="border-t border-[#F0F0F0]" data-testid={`subscriber-row-${s.email}`}>
                        <td className="px-4 py-3 text-[#0A0A0A]">{s.email}</td>
                        <td className="px-4 py-3 text-[#52525B]">{s.source}</td>
                        <td className="px-4 py-3">
                          <span className={`text-[10px] uppercase tracking-wider ${s.status === "active" ? "text-[#15803D]" : "text-[#71717A]"}`}>{s.status}</span>
                        </td>
                        <td className="px-4 py-3 text-[#52525B] text-xs">{s.created_at ? new Date(s.created_at).toLocaleDateString() : ""}</td>
                      </tr>
                    ))}
                    {(!subscribersData?.items || subscribersData.items.length === 0) && (
                      <tr><td colSpan={4} className="px-4 py-10 text-center text-sm text-[#71717A]" data-testid="subs-empty">No subscribers yet. Share the site to start growing your list.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Newsletter */}
        {tab === "newsletter" && (
          <div className="space-y-5" data-testid="admin-newsletter">
            <div className="bg-[#FEF3C7] border border-[#FCD34D] px-4 py-3 text-xs text-[#92400E] flex items-start gap-2" data-testid="newsletter-mocked-banner">
              <span className="font-semibold uppercase tracking-wider text-[10px]">Heads up</span>
              <span>Email sending is currently <strong>MOCKED</strong>. Drafts and "sent" status are saved, but no real emails go out until Resend is connected with an API key.</span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Composer */}
              <div className="bg-white border border-[#E5E5E5] p-5">
                <h3 className="text-sm font-semibold text-[#0A0A0A] mb-4">{newsletterDraft.id ? "Edit draft" : "Compose newsletter"}</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-[#71717A] mb-1.5 block">Subject</label>
                    <input
                      type="text"
                      value={newsletterDraft.subject}
                      onChange={(e) => setNewsletterDraft(d => ({ ...d, subject: e.target.value }))}
                      placeholder="The Edyte Weekly — Issue 01"
                      className="w-full px-3 py-2.5 text-sm border border-[#E5E5E5] focus:outline-none focus:border-[#0A0A0A]"
                      data-testid="newsletter-subject"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-[#71717A] mb-1.5 block">Body (HTML allowed)</label>
                    <textarea
                      value={newsletterDraft.body_html}
                      onChange={(e) => setNewsletterDraft(d => ({ ...d, body_html: e.target.value }))}
                      placeholder="<h2>This week's edit</h2><p>...</p>"
                      rows={12}
                      className="w-full px-3 py-2.5 text-sm border border-[#E5E5E5] focus:outline-none focus:border-[#0A0A0A] font-mono"
                      data-testid="newsletter-body"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2 pt-1">
                    <button
                      onClick={handleSaveNewsletter}
                      className="px-4 py-2 text-[10px] tracking-[0.15em] uppercase bg-[#0A0A0A] text-white hover:bg-[#FF4FB4] transition-colors"
                      data-testid="newsletter-save-btn"
                    >
                      {newsletterDraft.id ? "Update Draft" : "Save Draft"}
                    </button>
                    <button
                      onClick={() => setNewsletterPreview({ subject: newsletterDraft.subject, body_html: newsletterDraft.body_html })}
                      disabled={!newsletterDraft.subject || !newsletterDraft.body_html}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-[10px] tracking-[0.15em] uppercase border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors disabled:opacity-40"
                      data-testid="newsletter-preview-btn"
                    >
                      <Eye className="w-3.5 h-3.5" /> Preview
                    </button>
                    {newsletterDraft.id && (
                      <button
                        onClick={() => setNewsletterDraft({ id: null, subject: "", body_html: "" })}
                        className="px-4 py-2 text-[10px] tracking-[0.15em] uppercase text-[#71717A] hover:text-[#0A0A0A] transition-colors"
                        data-testid="newsletter-clear-btn"
                      >
                        New Draft
                      </button>
                    )}
                  </div>
                  <p className="text-[11px] text-[#71717A] pt-1">
                    Recipients: <strong>{subscribersData?.active || 0}</strong> active subscriber{(subscribersData?.active || 0) === 1 ? "" : "s"}
                  </p>
                </div>
              </div>

              {/* History */}
              <div className="bg-white border border-[#E5E5E5] p-5">
                <h3 className="text-sm font-semibold text-[#0A0A0A] mb-4">All newsletters</h3>
                {newsletters.length === 0 ? (
                  <p className="text-xs text-[#71717A] py-6 text-center" data-testid="newsletters-empty">No newsletters yet. Compose your first one.</p>
                ) : (
                  <div className="space-y-2">
                    {newsletters.map(nl => (
                      <div key={nl.id} className="border border-[#F0F0F0] p-3" data-testid={`newsletter-row-${nl.id}`}>
                        <div className="flex items-start justify-between gap-3 mb-1">
                          <p className="text-sm font-medium text-[#0A0A0A] flex-1 line-clamp-1">{nl.subject}</p>
                          <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 ${nl.status === "sent" ? "bg-[#DCFCE7] text-[#15803D]" : "bg-[#F4F4F5] text-[#71717A]"}`}>
                            {nl.status}{nl.status === "sent" ? ` · ${nl.sent_count}` : ""}
                          </span>
                        </div>
                        <p className="text-[10px] text-[#71717A]">
                          {nl.status === "sent"
                            ? `Sent ${nl.sent_at ? new Date(nl.sent_at).toLocaleString() : ""}${nl.mocked ? " · MOCKED" : ""}`
                            : `Saved ${nl.created_at ? new Date(nl.created_at).toLocaleString() : ""}`}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-2.5">
                          <button
                            onClick={() => setNewsletterPreview({ subject: nl.subject, body_html: nl.body_html })}
                            className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase text-[#71717A] hover:text-[#0A0A0A] transition-colors"
                            data-testid={`newsletter-preview-${nl.id}`}
                          >
                            <Eye className="w-3 h-3" /> Preview
                          </button>
                          {nl.status === "draft" && (
                            <>
                              <button
                                onClick={() => setNewsletterDraft({ id: nl.id, subject: nl.subject, body_html: nl.body_html })}
                                className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase text-[#71717A] hover:text-[#0A0A0A] transition-colors"
                                data-testid={`newsletter-edit-${nl.id}`}
                              >
                                <Pencil className="w-3 h-3" /> Edit
                              </button>
                              <button
                                onClick={() => handleSendNewsletter(nl)}
                                className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase text-[#FF4FB4] hover:text-[#0A0A0A] transition-colors"
                                data-testid={`newsletter-send-${nl.id}`}
                              >
                                <Send className="w-3 h-3" /> Send
                              </button>
                              <button
                                onClick={() => handleDeleteNewsletter(nl.id)}
                                className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase text-[#DC2626] hover:text-[#0A0A0A] transition-colors"
                                data-testid={`newsletter-delete-${nl.id}`}
                              >
                                <Trash2 className="w-3 h-3" /> Delete
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Newsletter preview modal */}
        {newsletterPreview && (
          <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4" onClick={() => setNewsletterPreview(null)} data-testid="newsletter-preview-modal">
            <div className="bg-white max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white">
                <p className="text-[10px] uppercase tracking-wider text-[#71717A]">Preview</p>
                <button onClick={() => setNewsletterPreview(null)} className="text-[#71717A] hover:text-[#0A0A0A]" data-testid="newsletter-preview-close"><XIcon className="w-4 h-4" /></button>
              </div>
              <div className="p-6">
                <p className="text-[10px] uppercase tracking-wider text-[#71717A] mb-1">Subject</p>
                <h2 className="text-xl text-[#0A0A0A] mb-5" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}>{newsletterPreview.subject}</h2>
                <div className="prose prose-sm max-w-none text-[#0A0A0A]" dangerouslySetInnerHTML={{ __html: newsletterPreview.body_html }} />
              </div>
            </div>
          </div>
        )}



        {/* Re-curate themes confirmation modal */}
        {recurateConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4" data-testid="recurate-confirm-modal">
            <div className="bg-white p-6 w-full max-w-sm">
              <h3 className="font-medium text-[#0A0A0A] mb-2">Re-curate all themes?</h3>
              <p className="text-sm text-[#71717A] mb-5">This will re-run theme assignment for ALL tagged products. <span className="text-[#0A0A0A] font-medium">It's free</span> — no scraping and no AI calls. Counts will refresh in 15–30s.</p>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setRecurateConfirm(false)} className="px-4 py-2 text-xs border border-[#E5E5E5] hover:border-[#0A0A0A]" data-testid="recurate-confirm-cancel">Cancel</button>
                <button onClick={recurateThemes} className="px-4 py-2 text-xs bg-[#0A0A0A] text-white hover:bg-[#FF4FB4] transition-colors" data-testid="recurate-confirm-confirm">Re-curate</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== MODALS ====================
const BrandModal = ({ brand, onSave, onClose, themes = [] }) => {
  const [name, setName] = useState(brand?.name || brand?.brand_name || "");
  const [url, setUrl] = useState(brand?.website_url || brand?.url || "");
  const [tagline, setTagline] = useState(brand?.tagline || "");
  const [description, setDescription] = useState(brand?.description || "");
  const [heroImage, setHeroImage] = useState(brand?.hero_image || "");
  const [overrideTheme, setOverrideTheme] = useState(brand?.override_theme || "");
  // Default OFF — auto-tagging burns LLM credits. Admin must opt in.
  const [scrapeNow, setScrapeNow] = useState(false);
  const [tagWithAI, setTagWithAI] = useState(false);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4" data-testid="brand-modal">
      <div className="bg-white p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-[#0A0A0A]">{brand ? "Edit Label" : "Add New Label"}</h3>
          <button onClick={onClose}><XIcon className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <input type="text" placeholder="Brand Name" value={name} onChange={e => setName(e.target.value)} className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0A0A0A]" data-testid="brand-name-input" />
          </div>
          <div>
            <input type="text" placeholder="https://www.brandname.com" value={url} onChange={e => setUrl(e.target.value)} className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0A0A0A]" data-testid="brand-url-input" />
            <p className="text-[10px] text-[#A1A1AA] mt-1">Shopify store URL (e.g., https://www.brandname.com)</p>
          </div>
          <div>
            <input type="text" placeholder="Tagline (5-6 words)" value={tagline} onChange={e => setTagline(e.target.value)} maxLength={70} className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0A0A0A]" data-testid="brand-tagline-input" />
            <p className="text-[10px] text-[#A1A1AA] mt-1">Shown on Discover Labels card. Keep it short.</p>
          </div>
          <div>
            <label className="block text-[11px] font-medium text-[#0A0A0A] mb-1">Description (SEO)</label>
            <textarea placeholder="A richer paragraph about this label — its story, craft, signature style. Shown on the label page and used as the page's meta description for Google." value={description} onChange={e => setDescription(e.target.value)} rows={4} maxLength={600} className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0A0A0A] resize-y" data-testid="brand-description-input" />
            <p className="text-[10px] text-[#A1A1AA] mt-1">{description.length}/600 · Displayed on the brand page and used for SEO.</p>
          </div>
          <div>
            <input type="text" placeholder="Hero Image URL (optional)" value={heroImage} onChange={e => setHeroImage(e.target.value)} className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0A0A0A]" data-testid="brand-hero-input" />
            <p className="text-[10px] text-[#A1A1AA] mt-1">If empty, the highest-aesthetic product image is used.</p>
          </div>
          <div>
            <label className="block text-[11px] font-medium text-[#0A0A0A] mb-1">Primary Edit (override)</label>
            <select value={overrideTheme} onChange={e => setOverrideTheme(e.target.value)} className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0A0A0A] bg-white" data-testid="brand-override-theme-select">
              <option value="">None — use AI/keyword curation</option>
              {themes.map(t => (
                <option key={t.id || t.name} value={t.name}>{t.name}</option>
              ))}
            </select>
            <p className="text-[10px] text-[#A1A1AA] mt-1">Pins ALL of this label's products into one edit (even untagged ones). No AI/credits. Click "Re-curate Themes" after saving.</p>
          </div>
          {!brand && (
            <div className="space-y-2 pt-2 border-t border-[#F0F0F0] mt-2">
              <label className="flex items-start gap-2 cursor-pointer" data-testid="scrape-now-toggle">
                <input type="checkbox" checked={scrapeNow} onChange={e => setScrapeNow(e.target.checked)} className="w-4 h-4 mt-0.5 accent-[#0A0A0A]" />
                <span className="text-xs text-[#52525B] leading-snug"><span className="font-medium text-[#0A0A0A]">Scrape products now</span> <span className="text-[#71717A]">(free — Shopify API)</span></span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer" data-testid="tag-now-toggle">
                <input type="checkbox" checked={tagWithAI} disabled={!scrapeNow} onChange={e => setTagWithAI(e.target.checked)} className="w-4 h-4 mt-0.5 accent-[#FF4FB4]" />
                <span className={`text-xs leading-snug ${scrapeNow ? 'text-[#52525B]' : 'text-[#A1A1AA]'}`}><span className="font-medium text-[#0A0A0A]">Also AI-tag products</span> <span className="text-[#FF4FB4]">(uses LLM credits — ~₹0.50 per product)</span></span>
              </label>
              {scrapeNow && !tagWithAI && (
                <p className="text-[10px] text-[#71717A] pl-6">Products will be saved untagged. You can tag later with "Process" button.</p>
              )}
            </div>
          )}
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={() => onSave({ id: brand?.id, brand_name: name, website_url: url, tagline: tagline || null, description: description || null, hero_image: heroImage || null, override_theme: overrideTheme || "", scrape_now: scrapeNow, tag_with_ai: tagWithAI })} className="px-4 py-2 text-xs bg-[#0A0A0A] text-white hover:bg-[#FF4FB4] transition-colors" data-testid="brand-save-btn">{brand ? "Save" : (scrapeNow ? "Add & Scrape" : "Add Label")}</button>
          <button onClick={onClose} className="px-4 py-2 text-xs border border-[#E5E5E5] hover:border-[#0A0A0A]">Cancel</button>
        </div>
      </div>
    </div>
  );
};

const ThemeModal = ({ theme, onSave, onClose }) => {
  const [name, setName] = useState(theme?.name || "");
  const [subtitle, setSubtitle] = useState(theme?.subtitle || "");
  const [desc, setDesc] = useState(theme?.description || "");
  const [order, setOrder] = useState(theme?.order ?? "");
  const [rules, setRules] = useState(theme?.rules || []);
  const [exclude, setExclude] = useState(theme?.exclude || []);
  const [minScore, setMinScore] = useState(theme?.min_score || 0);
  const [isPrice, setIsPrice] = useState(theme?.type === "price");
  const [priceMax, setPriceMax] = useState(theme?.price_max ?? 15000);
  const [collectionCfg, setCollectionCfg] = useState({
    filter: theme?.filter || null,
    manual_include: theme?.manual_include || [],
    manual_exclude: theme?.manual_exclude || [],
    pinned_order: theme?.pinned_order || [],
  });
  const [imageUrl, setImageUrl] = useState(theme?.image_url || "");
  const [imgPickerOpen, setImgPickerOpen] = useState(false);
  const [imgOptions, setImgOptions] = useState([]);
  const [imgLoading, setImgLoading] = useState(false);

  const openImagePicker = async () => {
    setImgPickerOpen(true);
    if (imgOptions.length || !theme?.id) return;
    setImgLoading(true);
    try {
      const r = await api.get(`/themes/${theme.id}/products`);
      setImgOptions((r.data || []).filter(p => p.image_url).slice(0, 48));
    } catch { /* ignore */ }
    finally { setImgLoading(false); }
  };

  const addRule = () => setRules([...rules, { type: "vibe", values: [], weight: 1 }]);
  const removeRule = (i) => setRules(rules.filter((_, idx) => idx !== i));
  const updateRule = (i, field, val) => {
    const updated = [...rules];
    updated[i] = { ...updated[i], [field]: val };
    setRules(updated);
  };

  const addExclude = () => setExclude([...exclude, { type: "vibe", values: [] }]);
  const removeExclude = (i) => setExclude(exclude.filter((_, idx) => idx !== i));
  const updateExclude = (i, field, val) => {
    const updated = [...exclude];
    updated[i] = { ...updated[i], [field]: val };
    setExclude(updated);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4" data-testid="theme-modal">
      <div className="bg-white p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-[#0A0A0A]">{theme ? "Edit Theme" : "Add Theme"}</h3>
          <button onClick={onClose}><XIcon className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <input type="text" placeholder="Theme Name" value={name} onChange={e => setName(e.target.value)} className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0A0A0A]" data-testid="theme-name-input" />
          <div className="flex gap-2">
            <input type="text" placeholder="Subtitle (e.g. Resort & Travel)" value={subtitle} onChange={e => setSubtitle(e.target.value)} className="flex-1 border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0A0A0A]" data-testid="theme-subtitle-input" />
            <input type="number" placeholder="Order" value={order} onChange={e => setOrder(e.target.value === "" ? "" : parseInt(e.target.value))} className="w-20 border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0A0A0A]" data-testid="theme-order-input" />
          </div>
          <textarea placeholder="Description" value={desc} onChange={e => setDesc(e.target.value)} rows={2} className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0A0A0A]" data-testid="theme-desc-input" />

          {/* Cover image — pick from existing products */}
          <div className="border border-[#E5E5E5] p-2.5">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 flex-shrink-0 bg-[#F4F4F4] rounded overflow-hidden">
                {imageUrl ? <img src={imageUrl} alt="cover" className="w-full h-full object-cover" data-testid="theme-cover-preview" /> : <div className="w-full h-full flex items-center justify-center text-[9px] text-[#A1A1AA]">No image</div>}
              </div>
              <div className="flex-1">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[#0A0A0A] mb-1">Cover Image</p>
                <div className="flex gap-2">
                  <button type="button" onClick={openImagePicker} disabled={!theme?.id} className="text-[10px] px-2.5 py-1.5 bg-[#0A0A0A] text-white hover:bg-[#FF4FB4] transition-colors disabled:opacity-40" data-testid="pick-theme-image-btn">Pick from products</button>
                  {imageUrl && <button type="button" onClick={() => setImageUrl("")} className="text-[10px] px-2.5 py-1.5 border border-[#E5E5E5] text-[#71717A] hover:border-[#0A0A0A]">Clear</button>}
                </div>
                {!theme?.id && <p className="text-[9px] text-[#A1A1AA] mt-1">Save the theme first to pick from its products.</p>}
              </div>
            </div>
            <input type="text" placeholder="…or paste an image URL" value={imageUrl} onChange={e => setImageUrl(e.target.value)} className="w-full border border-[#E5E5E5] px-2 py-1.5 text-[11px] mt-2 focus:outline-none focus:border-[#0A0A0A]" data-testid="theme-image-url-input" />
            {imgPickerOpen && (
              <div className="mt-2 border-t border-[#E5E5E5] pt-2" data-testid="theme-image-picker">
                {imgLoading ? <p className="text-[10px] text-[#71717A] py-3 text-center">Loading products…</p> : (
                  imgOptions.length === 0 ? <p className="text-[10px] text-[#71717A] py-3 text-center">No products found for this theme yet. Re-curate first.</p> : (
                    <div className="grid grid-cols-6 gap-1.5 max-h-52 overflow-y-auto">
                      {imgOptions.map(p => (
                        <button key={p.id || p.product_id} type="button" onClick={() => { setImageUrl(p.image_url); setImgPickerOpen(false); }} className={`aspect-square rounded overflow-hidden border-2 ${imageUrl === p.image_url ? "border-[#FF4FB4]" : "border-transparent hover:border-[#0A0A0A]"}`} data-testid={`theme-image-option-${p.id || p.product_id}`} title={p.title}>
                          <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                        </button>
                      ))}
                    </div>
                  )
                )}
                <button type="button" onClick={() => setImgPickerOpen(false)} className="text-[10px] text-[#71717A] mt-1.5 underline">Close picker</button>
              </div>
            )}
          </div>

          {/* Price collection toggle (e.g. Budget Badass) */}
          <div className="flex items-center gap-3 p-2 bg-[#FAFAFA] border border-[#E5E5E5]">
            <label className="flex items-center gap-2 text-xs font-semibold text-[#0A0A0A] cursor-pointer">
              <input type="checkbox" checked={isPrice} onChange={e => setIsPrice(e.target.checked)} data-testid="theme-price-toggle" />
              Price-based collection
            </label>
            {isPrice && (
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-xs text-[#71717A]">Max ₹</span>
                <input type="number" value={priceMax} onChange={e => setPriceMax(parseInt(e.target.value) || 0)} className="w-28 border border-[#E5E5E5] px-2 py-1 text-sm focus:outline-none focus:border-[#0A0A0A]" data-testid="theme-price-max-input" />
              </div>
            )}
          </div>
          {isPrice && <p className="text-[11px] text-[#71717A] -mt-1">Price collections include every product at/under the max price — matching rules below are ignored.</p>}

          {/* Phase 3 (Option B): structured attribute-query builder with live preview */}
          <CollectionBuilder api={api} name={name} initial={collectionCfg} onChange={setCollectionCfg} />

          {/* Matching Rules (legacy — still power label chips & analytics via assigned_theme) */}
          <details className="border border-[#E5E5E5] rounded">
            <summary className="text-[11px] text-[#71717A] px-2 py-1.5 cursor-pointer select-none">Legacy scoring rules (advanced)</summary>
            <div className="p-2">
          {/* Matching Rules */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider">Matching Rules</label>
              <button onClick={addRule} className="text-[10px] px-2 py-1 bg-[#0A0A0A] text-white hover:bg-[#FF4FB4] transition-colors" data-testid="add-rule-btn">+ Rule</button>
            </div>
            {rules.map((rule, i) => (
              <div key={i} className="flex items-start gap-2 mb-2 p-2 bg-[#FAFAFA] border border-[#E5E5E5]">
                <select value={rule.type} onChange={e => updateRule(i, "type", e.target.value)} className="border border-[#E5E5E5] px-2 py-1 text-xs flex-shrink-0">
                  <option value="vibe">vibe</option>
                  <option value="occasion">occasion</option>
                  <option value="travel">travel</option>
                  <option value="season">season</option>
                  <option value="attribute">attribute</option>
                  <option value="keyword">keyword</option>
                  <option value="material">material</option>
                </select>
                {rule.type === "attribute" ? (
                  <div className="flex-1 flex gap-1">
                    <input placeholder="key" value={rule.key || ""} onChange={e => updateRule(i, "key", e.target.value)} className="border border-[#E5E5E5] px-2 py-1 text-xs flex-1" />
                    <input placeholder="value" value={rule.value !== undefined ? String(rule.value) : (rule.values || []).join(",")} onChange={e => {
                      const v = e.target.value;
                      if (v === "true" || v === "false") updateRule(i, "value", v === "true");
                      else updateRule(i, "values", v.split(",").map(s => s.trim()).filter(Boolean));
                    }} className="border border-[#E5E5E5] px-2 py-1 text-xs flex-1" />
                  </div>
                ) : (
                  <input placeholder="values (comma-sep)" value={(rule.values || []).join(", ")} onChange={e => updateRule(i, "values", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} className="border border-[#E5E5E5] px-2 py-1 text-xs flex-1" />
                )}
                <input type="number" placeholder="w" value={rule.weight || 1} onChange={e => updateRule(i, "weight", parseInt(e.target.value) || 1)} className="border border-[#E5E5E5] px-2 py-1 text-xs w-12" />
                <button onClick={() => removeRule(i)} className="text-red-400 hover:text-red-600 flex-shrink-0"><XIcon className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>

          {/* Exclude Rules */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider">Exclusions</label>
              <button onClick={addExclude} className="text-[10px] px-2 py-1 bg-[#0A0A0A] text-white hover:bg-[#FF4FB4] transition-colors" data-testid="add-exclude-btn">+ Exclusion</button>
            </div>
            {exclude.map((ex, i) => (
              <div key={i} className="flex items-start gap-2 mb-2 p-2 bg-red-50/50 border border-red-100">
                <select value={ex.type} onChange={e => updateExclude(i, "type", e.target.value)} className="border border-[#E5E5E5] px-2 py-1 text-xs flex-shrink-0">
                  <option value="vibe">vibe</option>
                  <option value="occasion">occasion</option>
                  <option value="travel">travel</option>
                  <option value="attribute">attribute</option>
                </select>
                {ex.type === "attribute" ? (
                  <div className="flex-1 flex gap-1">
                    <input placeholder="key" value={ex.key || ""} onChange={e => updateExclude(i, "key", e.target.value)} className="border border-[#E5E5E5] px-2 py-1 text-xs flex-1" />
                    <input placeholder="value" value={ex.value !== undefined ? String(ex.value) : ""} onChange={e => {
                      const v = e.target.value;
                      if (v === "true" || v === "false") updateExclude(i, "value", v === "true");
                      else updateExclude(i, "value", v);
                    }} className="border border-[#E5E5E5] px-2 py-1 text-xs flex-1" />
                  </div>
                ) : (
                  <input placeholder="values (comma-sep)" value={(ex.values || []).join(", ")} onChange={e => updateExclude(i, "values", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} className="border border-[#E5E5E5] px-2 py-1 text-xs flex-1" />
                )}
                <button onClick={() => removeExclude(i)} className="text-red-400 hover:text-red-600 flex-shrink-0"><XIcon className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>

          {/* Min Score */}
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider">Min Score</label>
            <input type="number" value={minScore} onChange={e => setMinScore(parseInt(e.target.value) || 0)} className="border border-[#E5E5E5] px-3 py-2 text-sm w-20 focus:outline-none focus:border-[#0A0A0A]" data-testid="theme-min-score-input" />
          </div>
            </div>
          </details>
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={() => onSave({ id: theme?.id, name, subtitle, description: desc, image_url: imageUrl, order: order === "" ? null : order, rules, exclude, min_score: minScore, type: isPrice ? "price" : null, price_max: isPrice ? priceMax : null, filter: collectionCfg.filter, manual_include: collectionCfg.manual_include, manual_exclude: collectionCfg.manual_exclude, pinned_order: collectionCfg.pinned_order })} className="px-4 py-2 text-xs bg-[#0A0A0A] text-white hover:bg-[#FF4FB4] transition-colors" data-testid="theme-save-btn">Save</button>
          <button onClick={onClose} className="px-4 py-2 text-xs border border-[#E5E5E5] hover:border-[#0A0A0A]">Cancel</button>
        </div>
      </div>
    </div>
  );
};

// ==================== PROTECTED ROUTE ====================
const ProtectedAdminRoute = ({ children }) => {
  const { user, checking } = useAuth();
  if (checking) return <div className="min-h-screen pt-24 flex items-center justify-center"><p className="text-[#71717A]">Loading...</p></div>;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;
  return children;
};

// ==================== APP ROUTER (handles OAuth callback) ====================
const AppRouter = () => {
  const location = useLocation();

  // Track visitor (anonymous, persisted via localStorage) — runs once per app load
  useEffect(() => {
    try {
      getSource(); // capture first-touch acquisition source once
      let vid = localStorage.getItem("edyte_visitor_id");
      if (!vid) {
        vid = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `v_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        localStorage.setItem("edyte_visitor_id", vid);
      }
      api.post("/analytics/visit", { visitor_id: vid }).catch(() => {});
    } catch {}
  }, []);

  // Google Analytics 4 (gtag.js) — fire a page_view on initial load and on every client-side route change
  useEffect(() => {
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
    // Meta Pixel — fire PageView on initial load AND every client-side route change
    if (typeof window.fbq === "function") {
      window.fbq("track", "PageView");
    }
  }, [location.pathname, location.search]);

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/labels" element={<LabelsPage />} />
      <Route path="/products" element={<AllProductsPage />} />
      <Route path="/brand/:brandId" element={<BrandProductsPage />} />
      <Route path="/theme/:themeId" element={<ThemePage />} />
      <Route path="/themes" element={<ThemesPage />} />
      <Route path="/editorial/:slug" element={<EditorialPage />} />
      <Route path="/saved" element={<SavedItemsPage />} />
      <Route path="/search" element={<SearchResultsPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/admin" element={<ProtectedAdminRoute><AdminDashboard /></ProtectedAdminRoute>} />
    </Routes>
  );
};

// ==================== APP ====================
function App() {
  return (
    <div className="App relative bg-white">
      <BrowserRouter>
        <AuthProvider>
          <Navbar />
          <AppRouter />
          <SignupPopup />
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
